import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PageHeader } from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { ordersApi, freightApi, dealerCommissionSettingsApi, fabricSettingsApi } from '@/services/api';
import { CarrierSelection } from '@/components/order/CarrierSelection';
import { InfoTooltip } from '@/components/InfoTooltip';
import { parseFraction, toFractionStr } from '@/utils/fractions';
import { DimensionInput } from '@/components/DimensionInput';
import clsx from 'clsx';

// ============================================================================
// MAX DIMENSIONS - Current limits (RAD not yet launched)
// When RAD launches, extend to 1188" x 288" (99ft x 24ft)
// ============================================================================

const MAX_SCREEN_WIDTH = 288; // inches (24ft) - current max until RAD launches
const MAX_SCREEN_HEIGHT = 192; // inches (16ft) - current max until RAD launches

// ============================================================================
// LEAD TIME CALCULATION
// ============================================================================

const DEFAULT_LEAD_TIME_DAYS = 21; // Default fallback (solar)

// Lead times by fabric category (fetched from API)
interface LeadTimes {
  solar: number;
  premiumSolar: number;
  cvw: number;
  insect: number;
}

// Map pricing category to lead time key
function getLeadTimeForFabric(pricingCategory: string, leadTimes: LeadTimes): number {
  switch (pricingCategory) {
    case 'solar94':
      return leadTimes.solar;
    case 'solar99':
      return leadTimes.premiumSolar;
    case 'cvw':
      return leadTimes.cvw;
    case 'insect':
      return leadTimes.insect;
    default:
      return leadTimes.solar; // Default to solar
  }
}

// Add business days to a date (skips weekends)
function addBusinessDays(date: Date, days: number): Date {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (result.getDay() !== 0 && result.getDay() !== 6) {
      added++;
    }
  }
  return result;
}

// Format date as YYYY-MM-DD for input[type="date"]
function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0];
}

// Format date for display (e.g., "Jan 28, 2026")
function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ============================================================================
// PRICING MATRICES (from @keder/shared)
// ============================================================================

const PRICING_MATRICES = {
  cvw: {
    productType: 'cvw',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3988.6, 4111.96, 4239.13, 4370.24, 4505.4, 4644.74, 4788.4, 4936.49, 5089.17, 5246.56, 5408.83, 5576.11, 5748.57, 5926.36, 6109.65, 6298.61, 6493.41, 6694.24, 6901.27, 7114.72, 7334.76],
      96: [4107.4, 4234.43, 4365.39, 4500.41, 4639.59, 4783.09, 4931.05, 5083.62, 5240.94, 5403.15, 5570.39, 5742.81, 5920.56, 6103.77, 6291.62, 6486.21, 6686.81, 6893.62, 7106.83, 7326.63, 7553.22],
      108: [4230.6, 4361.44, 4496.33, 4635.4, 4778.76, 4926.55, 5078.92, 5235.99, 5397.99, 5565.06, 5737.35, 5914.99, 6098.14, 6286.93, 6480.34, 6680.76, 6887.38, 7100.39, 7319.99, 7546.39, 7779.78],
      120: [4358.2, 4492.99, 4631.95, 4775.2, 4922.89, 5075.15, 5232.13, 5394.01, 5560.99, 5733.23, 5910.87, 6094.06, 6282.96, 6477.71, 6677.35, 6882.99, 7095.11, 7314.55, 7540.77, 7773.99, 8014.43],
      132: [4489.1, 4627.94, 4771.07, 4918.63, 5070.75, 5227.58, 5389.27, 5555.98, 5727.85, 5905.05, 6087.73, 6276.03, 6470.12, 6670.01, 6876.3, 7088.97, 7308.22, 7534.25, 7767.26, 8007.49, 8255.14],
      144: [4623.3, 4766.29, 4913.7, 5065.67, 5222.34, 5383.86, 5550.37, 5722.02, 5898.96, 6081.35, 6269.33, 6463.05, 6662.66, 6868.29, 7081.87, 7300.89, 7526.7, 7759.48, 7999.46, 8246.87, 8501.93],
      156: [4761.9, 4909.18, 5061.01, 5217.53, 5378.9, 5545.26, 5716.75, 5893.54, 6075.77, 6263.59, 6457.13, 6656.55, 6862.01, 7073.64, 7294.17, 7519.76, 7752.33, 7992.1, 8239.28, 8494.1, 8756.8],
      168: [4904.9, 5056.6, 5212.99, 5374.21, 5540.43, 5711.78, 5888.41, 6070.5, 6258.16, 6451.55, 6650.8, 6856.07, 7067.51, 7285.25, 7513.22, 7745.58, 7985.14, 8232.1, 8486.7, 8749.18, 9019.77],
      180: [5052.3, 5208.56, 5369.65, 5535.72, 5706.93, 5883.43, 6065.38, 6252.92, 6446.19, 6645.35, 6850.54, 7061.9, 7279.58, 7503.7, 7739, 7978.35, 8225.1, 8479.49, 8741.74, 9012.1, 9290.83],
      192: [5204.1, 5365.05, 5530.98, 5702.04, 5878.39, 6060.2, 6247.61, 6440.78, 6639.85, 6845.06, 7056.52, 7274.39, 7498.81, 7729.93, 7971.52, 8218.07, 8472.23, 8734.26, 9004.39, 9282.88, 9569.98]
    }
  },
  solar99: {
    productType: 'solar99',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3438.44, 3544.78, 3654.41, 3767.43, 3884.00, 4003.07, 4127.91, 4255.58, 4387.19, 4522.89, 4662.77, 4806.97, 4955.64, 5108.91, 5266.92, 5430.81, 5599.75, 5773.87, 5953.35, 6138.36, 6329.05],
      96: [3546.01, 3666.71, 3790.80, 3918.40, 4049.59, 4184.50, 4323.25, 4465.96, 4612.72, 4763.70, 4919.00, 5078.75, 5243.12, 5412.23, 5586.23, 5765.26, 5949.49, 6139.09, 6334.21, 6535.01, 6741.70],
      108: [3617.67, 3729.56, 3844.90, 3963.82, 4086.41, 4212.80, 4343.09, 4477.40, 4615.85, 4758.58, 4905.71, 5057.36, 5213.67, 5374.80, 5540.47, 5712.85, 5890.53, 6074.69, 6263.47, 6458.07, 6658.64],
      120: [3725.73, 3840.90, 3959.55, 4082.40, 4208.40, 4338.60, 4473.00, 4611.60, 4754.40, 4901.40, 5052.60, 5209.05, 5369.70, 5536.65, 5707.80, 5884.20, 6065.85, 6253.80, 6447.00, 6646.50, 6852.30],
      132: [3796.49, 3913.91, 4034.95, 4159.74, 4288.40, 4421.03, 4557.77, 4698.77, 4844.14, 4994.04, 5148.59, 5307.94, 5472.23, 5641.58, 5815.37, 5995.23, 6180.64, 6371.80, 6568.87, 6772.02, 6981.47],
      144: [3959.34, 4081.79, 4208.03, 4338.18, 4472.35, 4610.68, 4753.30, 4900.34, 5051.95, 5208.26, 5369.41, 5535.52, 5706.75, 5883.23, 6064.83, 6252.40, 6446.77, 6646.12, 6851.65, 7063.52, 7280.95],
      156: [4345.11, 4479.50, 4617.04, 4758.86, 4905.11, 5055.90, 5211.38, 5371.68, 5536.95, 5707.34, 5883.00, 6064.06, 6250.70, 6443.09, 6641.74, 6846.60, 7058.80, 7277.59, 7502.13, 7733.65, 7970.35],
      168: [4457.15, 4595.00, 4736.11, 4880.61, 5028.65, 5180.37, 5335.89, 5495.38, 5658.99, 5826.89, 5999.21, 6176.14, 6357.86, 6544.54, 6736.36, 6933.51, 7136.19, 7344.61, 7559.00, 7779.49, 8006.38],
      180: [4529.07, 4669.20, 4815.67, 4964.61, 5118.15, 5276.45, 5439.64, 5607.87, 5781.31, 5960.12, 6144.45, 6334.48, 6529.39, 6730.37, 6936.63, 7148.25, 7365.54, 7588.68, 7817.88, 8053.34, 8295.32],
      192: [4644.26, 4787.90, 4935.97, 5088.62, 5246.01, 5408.25, 5575.52, 5747.96, 5925.74, 6109.01, 6297.94, 6492.73, 6693.53, 6900.55, 7113.97, 7333.98, 7559.81, 7792.65, 8031.73, 8277.25, 8530.46]
    }
  },
  solar94: {
    productType: 'solar94',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3274.70, 3375.98, 3480.39, 3588.03, 3699.00, 3813.40, 3931.34, 4052.93, 4178.28, 4307.51, 4440.73, 4578.07, 4719.66, 4865.63, 5016.11, 5171.25, 5331.19, 5496.07, 5666.05, 5841.29, 6021.95],
      96: [3378.10, 3482.58, 3590.29, 3701.33, 3815.80, 3933.81, 4055.48, 4180.91, 4310.21, 4443.52, 4580.95, 4722.62, 4868.69, 5019.26, 5174.50, 5334.53, 5499.52, 5669.61, 5844.96, 6025.73, 6212.09],
      108: [3445.40, 3551.96, 3661.81, 3775.07, 3891.82, 4012.19, 4136.27, 4264.19, 4396.05, 4531.98, 4672.10, 4816.53, 4965.40, 5118.86, 5277.59, 5440.81, 5609.08, 5782.56, 5961.40, 6145.78, 6335.85],
      120: [3548.60, 3658, 3771, 3888, 4008, 4132, 4260, 4392, 4528, 4668, 4812, 4961, 5114, 5273, 5436, 5604, 5777, 5956, 6140, 6330, 6526],
      132: [3615.70, 3727.53, 3842.81, 3961.66, 4084.19, 4210.50, 4340.74, 4475.02, 4613.47, 4756.23, 4903.42, 5055.18, 5211.64, 5372.94, 5538.45, 5709.74, 5886.33, 6068.38, 6256.06, 6449.55, 6649.02],
      144: [3770.80, 3887.42, 4007.65, 4131.60, 4259.38, 4391.12, 4526.95, 4666.99, 4811.38, 4960.25, 5113.72, 5271.92, 5435.00, 5603.08, 5776.03, 5954.67, 6138.83, 6328.69, 6524.43, 6726.21, 6934.24],
      156: [4138.20, 4266.19, 4398.13, 4534.15, 4674.39, 4818.95, 4967.98, 5121.60, 5279.95, 5443.18, 5611.42, 5784.82, 5963.53, 6147.71, 6338.80, 6534.85, 6736.96, 6945.32, 7160.12, 7381.57, 7609.86],
      168: [4244.90, 4376.19, 4511.53, 4651.06, 4794.91, 4943.21, 5096.09, 5253.70, 5416.19, 5583.70, 5756.39, 5934.42, 6117.96, 6307.18, 6502.24, 6703.34, 6910.66, 7124.39, 7344.74, 7571.89, 7806.08],
      180: [4315.30, 4448.76, 4586.35, 4728.20, 4874.43, 5025.19, 5180.61, 5340.83, 5506.01, 5676.30, 5851.86, 6032.84, 6219.42, 6411.78, 6610.08, 6814.52, 7025.27, 7242.55, 7466.55, 7697.47, 7935.54],
      192: [4423.10, 4559.90, 4700.92, 4846.31, 4996.20, 5150.72, 5310.02, 5474.25, 5643.56, 5818.10, 5998.04, 6183.55, 6374.79, 6571.95, 6775.21, 6984.75, 7200.77, 7423.48, 7653.07, 7889.76, 8133.77]
    }
  },
  insect: {
    productType: 'insect',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3158.10, 3255.77, 3356.47, 3460.28, 3567.29, 3677.62, 3791.36, 3908.62, 4029.51, 4154.13, 4282.61, 4415.06, 4551.61, 4692.38, 4837.51, 4987.12, 5141.36, 5300.37, 5464.30, 5633.30, 5807.53],
      96: [3256.00, 3356.70, 3460.52, 3567.54, 3677.88, 3791.63, 3908.89, 4029.79, 4154.42, 4282.91, 4415.37, 4551.93, 4692.71, 4837.84, 4987.47, 5141.72, 5300.74, 5464.68, 5633.69, 5807.93, 5987.56],
      108: [3317.60, 3420.21, 3525.99, 3635.04, 3747.46, 3863.36, 3982.85, 4106.03, 4233.02, 4363.94, 4498.90, 4638.04, 4781.49, 4929.37, 5081.83, 5238.99, 5401.03, 5568.07, 5740.28, 5917.81, 6100.84],
      120: [3414.40, 3520.00, 3628.87, 3741.10, 3856.80, 3976.09, 4099.06, 4225.83, 4356.53, 4491.27, 4630.17, 4773.37, 4921.00, 5073.20, 5230.10, 5391.86, 5558.62, 5730.53, 5907.76, 6090.48, 6278.84],
      132: [3474.90, 3582.37, 3693.17, 3807.39, 3925.14, 4046.54, 4171.69, 4300.71, 4433.72, 4570.85, 4712.21, 4857.95, 5008.20, 5163.09, 5322.77, 5487.40, 5657.11, 5832.07, 6012.44, 6198.40, 6390.10],
      144: [3624.50, 3736.60, 3852.16, 3971.30, 4094.13, 4220.75, 4351.29, 4485.86, 4624.60, 4767.63, 4915.08, 5067.09, 5223.81, 5385.37, 5551.93, 5723.64, 5900.66, 6083.15, 6271.29, 6465.25, 6665.20],
      156: [3998.60, 4122.27, 4249.76, 4381.20, 4516.70, 4656.39, 4800.40, 4948.87, 5101.93, 5259.72, 5422.39, 5590.09, 5762.98, 5941.22, 6124.97, 6314.40, 6509.69, 6711.02, 6918.58, 7132.55, 7353.15],
      168: [4089.80, 4216.29, 4346.69, 4481.12, 4619.71, 4762.59, 4909.89, 5061.74, 5218.29, 5379.68, 5546.06, 5717.59, 5894.42, 6076.72, 6264.66, 6458.42, 6658.16, 6864.08, 7076.38, 7295.23, 7520.86],
      180: [4154.70, 4283.20, 4415.67, 4552.23, 4693.02, 4838.17, 4987.80, 5142.06, 5301.10, 5465.05, 5634.07, 5808.32, 5987.96, 6173.15, 6364.08, 6560.90, 6763.82, 6973.01, 7188.67, 7410.99, 7640.20],
      192: [4255.90, 4387.53, 4523.22, 4663.12, 4807.34, 4956.02, 5109.30, 5267.31, 5430.22, 5598.17, 5771.31, 5949.80, 6133.81, 6323.52, 6519.09, 6720.71, 6928.57, 7142.86, 7363.77, 7591.52, 7826.30]
    }
  }
} as const;

const UCHANNEL_RATES = { MSRP: 12.75, DEALER: 9.16 };

const REMOTE_PRICING: Record<string, Record<string, number>> = {
  MSRP: {
    'alpha_pioneer_1ch': 125,
    'alpha_pioneer_4ch': 200,
    'alpha_pioneer_8ch_lcd': 275,
    'alpha_pioneer_16ch_lcd': 350,
    'keder_remote_15ch': 200,
  },
  DEALER: {
    'alpha_pioneer_1ch': 77,
    'alpha_pioneer_4ch': 82.5,
    'alpha_pioneer_8ch_lcd': 143,
    'alpha_pioneer_16ch_lcd': 165,
    'keder_remote_15ch': 82.5,
  }
};

// Pricing helper functions
// Use "round UP" method - find first bucket >= value (standard pricing behavior)
function getNextBucket(arr: readonly number[], val: number): number {
  // Find the first bucket that is >= the value
  for (const bucket of arr) {
    if (bucket >= val) {
      return bucket;
    }
  }
  // If value exceeds all buckets, return the largest bucket
  return arr[arr.length - 1];
}

/**
 * Get dealer price for a screen based on dimensions
 * - Heights below minimum (84") use the minimum height row price
 * - Widths below minimum (48") use the minimum width column price
 * - Heights/widths above maximum return null (requires custom quote)
 * - Uses "round UP" bucketing for values between buckets
 */
function getScreenPrice(productType: keyof typeof PRICING_MATRICES, width: number, height: number): number | null {
  const matrix = PRICING_MATRICES[productType];
  if (!matrix) return null;

  const minHeight = matrix.heights[0]; // 84
  const minWidth = matrix.widths[0];   // 48
  const maxHeight = matrix.heights[matrix.heights.length - 1]; // 192
  const maxWidth = matrix.widths[matrix.widths.length - 1];    // 288

  // Minimum orderable width is 30" (matches the Min Width manufacturing rule).
  // Widths 30–48 round up to the 48" pricing bucket; below 30 we don't quote.
  if (width > 0 && width < 30) return null;

  // Check upper bounds - exceeding max requires custom quote (RAD not yet launched)
  if (width > maxWidth || height > maxHeight) return null;

  // For dimensions below minimum, use the minimum bucket
  // For dimensions within range, find the next bucket >= value (round UP)
  const heightBucket = height <= minHeight ? minHeight : getNextBucket(matrix.heights, height);
  const widthBucket = width <= minWidth ? minWidth : getNextBucket(matrix.widths, width);

  const widthIdx = matrix.widths.indexOf(widthBucket);
  return matrix.prices[heightBucket as keyof typeof matrix.prices]?.[widthIdx] ?? null;
}

function getUChannelCost(heightInches: number, uChannelType: string): number {
  if (uChannelType === 'none') return 0;
  const sides = uChannelType === 'both_sides' ? 2 : 1;
  const feet = (heightInches / 12) * sides;
  return feet * UCHANNEL_RATES.DEALER;
}

function getRemotePrice(remoteType: string, qty: number = 1): number {
  const unitPrice = REMOTE_PRICING.DEALER[remoteType] ?? REMOTE_PRICING.DEALER['alpha_pioneer_1ch'];
  return unitPrice * qty;
}

function calculatePackaging(prePackSubtotal: number, qty: number, shippingMethod?: string): number {
  // Packaging only applies to freight shipments, not will call. Full rate on
  // every freight order — the large-order packaging discount was removed
  // 2026-09-18 (kept in sync with orders.service.ts).
  if (shippingMethod === 'willcall') return 0;
  return 82.5 * qty;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const SYSTEM_COLORS = [
  { value: 'black', label: 'Black' },
  { value: 'bronze', label: 'Bronze' },
  { value: 'white', label: 'White' },
  { value: 'tan', label: 'Tan' },
  { value: 'custom', label: 'Custom' },
];

// Screens narrower than this only fit the Alpha motor — the roller tube is too
// short for the Keder / Somfy motor head. 36" and up can take any of the three
// packages. Enforced server-side too (orders.service.assertMotorAllowedForWidths).
const ALPHA_ONLY_MAX_WIDTH = 36;

// Motor Package Options
const MOTOR_PACKAGES = [
  {
    value: 'none',
    label: 'No Motor',
    description: 'Screen only - customer reusing existing motor/remote',
    badge: null,
  },
  {
    value: 'keder',
    label: 'Keder Screens Package',
    description: 'Keder Screens motor with Keder remotes',
    badge: 'Default',
  },
  {
    value: 'alpha',
    label: 'Alpha Package',
    description: 'Alpha motor with Alpha remotes',
    badge: '+$125/unit',
  },
  {
    value: 'somfy',
    label: 'Somfy Package',
    description: 'Somfy motor with Somfy remotes',
    badge: null,
  },
];

// Legacy compatibility - maps to package value
const MOTOR_TYPES = ['Keder Screens', 'Alpha', 'Somfy'];

const ALPHA_REMOTES = [
  { value: 'none', label: 'None' },
  { value: 'alpha_pioneer_1ch', label: 'Alpha Pioneer 1-ch' },
  { value: 'alpha_pioneer_4ch', label: 'Alpha Pioneer 4-ch' },
  { value: 'alpha_pioneer_8ch_lcd', label: 'Alpha Pioneer 8-ch LCD' },
  { value: 'alpha_pioneer_16ch_lcd', label: 'Alpha Pioneer 16-ch LCD' },
];

const ALPHA_WALL_SWITCHES = [
  { value: 'none', label: 'None' },
  { value: 'alpha_rts_wall_switch', label: 'Alpha RTS Wall Switch' },
];

const SOMFY_REMOTES = [
  { value: 'none', label: 'None' },
  { value: 'somfy_situo_1ch', label: 'Somfy Situo 1-ch' },
  { value: 'somfy_situo_5ch', label: 'Somfy Situo 5-ch' },
  { value: 'somfy_telis_1ch', label: 'Somfy Telis 1-ch' },
  { value: 'somfy_telis_4ch', label: 'Somfy Telis 4-ch' },
  { value: 'somfy_telis_16ch', label: 'Somfy Telis 16-ch' },
];

const SOMFY_WALL_SWITCHES = [
  { value: 'none', label: 'None' },
  { value: 'somfy_rts_wall_switch', label: 'Somfy RTS Wall Switch' },
];

// Keder Screens remote options — 15ch only (1-ch removed 2026-06; physical
// product never existed). Wall switch is in KEDER_WALL_SWITCHES.
const KEDER_REMOTES = [
  { value: 'none', label: 'None' },
  { value: 'keder_remote_15ch', label: 'Keder Screens 15-ch Remote' },
];

const KEDER_WALL_SWITCHES = [
  { value: 'none', label: 'None' },
  { value: 'keder_wall_switch', label: 'Keder Screens Wall Switch' },
];

// Fallback fabric types/colors (used when API is unavailable)
const FALLBACK_FABRIC_TYPES = [
  { group: 'Solar', options: [
    { value: 'omega_pro_3', label: 'Omega Pro 3%', pricingCategory: 'solar94' },
    { value: 'omega_pro_5', label: 'Omega Pro 5%', pricingCategory: 'solar94' },
    { value: 'omega_pro_10', label: 'Omega Pro 10%', pricingCategory: 'solar94' },
    { value: 'twitchell_80', label: 'Twitchell 80', pricingCategory: 'solar94' },
    { value: 'twitchell_90', label: 'Twitchell 90', pricingCategory: 'solar94' },
    { value: 'twitchell_95', label: 'Twitchell 95', pricingCategory: 'solar94' },
  ]},
  { group: 'Solar Premium', options: [
    { value: 'twitchell_nano_95', label: 'Twitchell Nano 95', pricingCategory: 'solar99' },
    { value: 'twitchell_nano_97', label: 'Twitchell Nano 97', pricingCategory: 'solar99' },
    { value: 'twitchell_nano_99', label: 'Twitchell Nano 99', pricingCategory: 'solar99' },
    { value: 'twitchell_dimout_blackout', label: 'Twitchell Dimout', pricingCategory: 'solar99' },
  ]},
  { group: 'Vinyl', options: [
    { value: '502_vinyl', label: '502 Vinyl', pricingCategory: 'cvw' },
  ]},
  { group: 'Insect', options: [
    { value: 'twitchell_nano_55_insect', label: 'Twitchell Nano 55 Insect', pricingCategory: 'insect' },
  ]},
];

const FALLBACK_FABRIC_COLORS: Record<string, string[]> = {
  omega_pro_3: ['Brown/Taupe', 'White', 'White/Linen', 'White Gray', 'Light Gray', 'Black/Bronze', 'Gray/Black', 'Black', 'Charcoal/Taupe'],
  omega_pro_5: ['Brown/Taupe', 'White', 'White/Linen', 'White Gray', 'Light Gray', 'Black/Bronze', 'Gray/Black', 'Black', 'Charcoal/Taupe'],
  omega_pro_10: ['Brown/Taupe', 'White', 'White/Linen', 'White Gray', 'Light Gray', 'Black/Bronze', 'Gray/Black', 'Black', 'Charcoal/Taupe'],
  twitchell_80: ['Black'],
  twitchell_90: ['Black'],
  twitchell_95: ['Black'],
  twitchell_nano_95: ['Black'],
  twitchell_nano_97: ['Black'],
  twitchell_nano_99: ['Black'],
  twitchell_dimout_blackout: ['Black'],
  '502_vinyl': ['Black', 'Red (Poppy)', 'White', 'Blue (Midnight)', 'Anthracite', 'Grey (Concrete)', 'Boulder', 'Antelope', 'Tan (Pepper)', 'Hemp'],
  twitchell_nano_55_insect: ['Standard'],
};

// Build fabric types/colors from API catalog response
function buildFabricData(catalog: { fabricTypes: any[] }) {
  const groups: Record<string, { value: string; label: string; pricingCategory: string }[]> = {};
  const colors: Record<string, string[]> = {};

  for (const ft of catalog.fabricTypes) {
    const group = ft.group || 'Other';
    if (!groups[group]) groups[group] = [];
    groups[group].push({ value: ft.code, label: ft.name, pricingCategory: ft.pricingCategory });
    colors[ft.code] = ft.colors.map((c: any) => c.name);
  }

  const types = Object.entries(groups).map(([group, options]) => ({ group, options }));
  return { types, colors };
}

const U_CHANNEL_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'both_sides', label: 'Both Sides' },
  { value: 'left_side', label: 'Left Side Only' },
  { value: 'right_side', label: 'Right Side Only' },
];

// System Type options
type SystemType = 'box' | 'open_roll';

const SYSTEM_TYPE_OPTIONS: { value: SystemType; label: string }[] = [
  { value: 'box', label: 'Box' },
  { value: 'open_roll', label: 'Open Roll' },
];

// Track Mount type - simplified options
type TrackMountType = 'face' | 'inside' | 'inside_face' | 'inside_outside' | 'recessed';

// Track Mount dropdown options with labels
const TRACK_MOUNT_OPTIONS: { value: TrackMountType; label: string }[] = [
  { value: 'face', label: 'Face' },
  { value: 'inside', label: 'Inside' },
  { value: 'inside_face', label: 'Inside/Face' },
  { value: 'inside_outside', label: 'Inside/Outside' },
  { value: 'recessed', label: 'Recessed' },
];

// Helper function to get allowed U-Channel options based on Track Mount
function getUChannelOptionsForTrackMount(trackMount: TrackMountType): { value: string; label: string; disabled?: boolean }[] {
  switch (trackMount) {
    case 'face':
      // Face mount - only None allowed
      return [{ value: 'none', label: 'None' }];
    case 'inside':
      // Inside mount - None or Both
      return [
        { value: 'none', label: 'None' },
        { value: 'both_sides', label: 'Both' },
      ];
    case 'inside_face':
      // Inside/Face - None, Left, or Right
      return [
        { value: 'none', label: 'None' },
        { value: 'left_side', label: 'Left' },
        { value: 'right_side', label: 'Right' },
      ];
    case 'inside_outside':
      // Inside/Outside - None, Left, or Right
      return [
        { value: 'none', label: 'None' },
        { value: 'left_side', label: 'Left' },
        { value: 'right_side', label: 'Right' },
      ];
    case 'recessed':
      // Recessed/jamb - full u-channel options (it CAN ship with u-channel;
      // it doesn't have to be pre-installed). All four choices allowed.
      return U_CHANNEL_OPTIONS;
    default:
      return U_CHANNEL_OPTIONS;
  }
}

// Check if U-channel should be disabled for the given track mount
function isUChannelDisabled(trackMount: TrackMountType): boolean {
  // Face and Recessed only have "None" option but it's not disabled - user can still see it
  return false;
}

// Check if Track Mount includes face (for Screw Covers visibility)
function trackMountIncludesFace(trackMount: TrackMountType): boolean {
  return trackMount === 'face' || trackMount === 'inside_face' || trackMount === 'inside_outside';
}

function trackMountIncludesInside(trackMount: string): boolean {
  return trackMount === 'inside' || trackMount === 'inside_face' || trackMount === 'inside_outside';
}

// Get screw cover quantity based on track mount and u-channel
// Face: always 2 (both tracks)
// Inside/Face or Inside/Outside: 2 if U-Channel is None, 1 if U-Channel is Left/Right
function getScrewCoverQty(trackMount: TrackMountType, uChannel: string): number {
  if (trackMount === 'face') {
    return 2; // Always 2 for face mount
  }
  if (trackMount === 'inside_face' || trackMount === 'inside_outside') {
    // U-Channel None = 2 covers, Left/Right = 1 cover
    if (uChannel === 'none') {
      return 2;
    }
    return 1; // Left or Right u-channel means only 1 screw cover on opposite side
  }
  return 0; // No screw covers for other track mounts
}

// Get default screw covers value based on track mount and u-channel
function getDefaultScrewCovers(trackMount: TrackMountType, uChannel: string): boolean {
  if (trackMount === 'face') {
    return true; // Default Yes for Face
  }
  if (trackMount === 'inside_face' || trackMount === 'inside_outside') {
    // Default Yes if U-Channel is None, No if U-Channel is Left/Right
    return uChannel === 'none';
  }
  return false;
}

// Calculate screw covers price based on height and quantity
// $1.25 per linear foot is the dealer rate (no additional discount needed)
function calculateScrewCoversPrice(heightInches: number, qty: number = 2): number {
  // (height / 12) × qty tracks × $1.25 per foot
  return (heightInches / 12) * qty * 1.25;
}

// Helper function to get assembly note based on Track Mount and U-Channel
function getTrackAssemblyNote(trackMount: TrackMountType, uChannel: string): string | null {
  switch (trackMount) {
    case 'face':
      return 'Tracks ship assembled';
    case 'recessed':
      return 'Tracks ship NOT assembled';
    case 'inside':
      switch (uChannel) {
        case 'none':
          return 'Tracks ship NOT assembled';
        case 'both_sides':
          return 'Tracks ship assembled';
        default:
          return null;
      }
    case 'inside_face':
      // One side face (assembled), other side inside
      if (uChannel === 'left_side' || uChannel === 'right_side') {
        return `Face side ships assembled, ${uChannel === 'left_side' ? 'left' : 'right'} side ships pre-assembled with U-channel`;
      }
      return 'Face side ships assembled, inside side ships NOT assembled';
    case 'inside_outside':
      // One side outside (+3"), other side inside
      if (uChannel === 'left_side' || uChannel === 'right_side') {
        return `Outside adds +3", ${uChannel === 'left_side' ? 'left' : 'right'} side ships pre-assembled with U-channel`;
      }
      return 'Outside side adds +3", inside side ships NOT assembled';
    default:
      return null;
  }
}

// ============================================================================
// TYPES
// ============================================================================

interface WindowConfig {
  orientation: 'horizontal' | 'vertical' | 'grid';
  windowCount: number;
  windowColumns: number;
  windowWidth: string;
  windowHeight: string;
  symmetrical: boolean;
  borderTop: string;
  borderSide: string;
  borderBottom: string;
  dividerType: 'weld' | 'divider';
  dividerSize: string;
  // New fields for divider-based calculation
  horizontalDividers: number;  // Number of horizontal dividers (creates rows)
  verticalDividers: number;    // Number of vertical dividers (creates columns)
  // Height-from-ground inputs (user enters these, borders are calculated)
  windowBottomFromGround: string;  // Height from ground to bottom of window
  windowTopFromGround: string;     // Height from ground to top of window
}

interface ScreenUnit {
  id: string;
  // Dimensions
  widthTop: string;
  widthMiddle: string;
  widthBottom: string;
  heightLeft: string;
  heightCenter: string;
  heightRight: string;
  // Screen location (per-unit)
  screenLocation: string;
  // Installation Options (per-unit)
  systemType: SystemType;
  // Per-unit override: skip the auto-added Weight Bar Kit even when screen < 72"
  skipWeightBarKit?: boolean;
  installationType: 'wall' | 'ceiling';
  trackMount: TrackMountType;
  uChannel: string;
  applyUChannelDeduction: boolean;
  // Box Width Adjustment (for inside mounts — affects housing + roller tube only)
  boxWidthAdjustmentType: 'same' | 'smaller' | 'larger';
  boxWidthAdjustmentValue: string;
  applyBoxDeduction: boolean;
  screwCovers: boolean;
  screwCoverQty: number; // 1 or 2 based on track mount and u-channel
  // System Options (per-unit)
  systemColor: string;
  ralNumber: string; // RAL color number when systemColor is 'custom'
  motorSide: 'right' | 'left';
  brushSize: '1inch' | '2inch';
  biasedCut: boolean;
  biasAmount: string;
  biasSide: 'left' | 'right';
  // Per-component color overrides
  useCustomColors: boolean;
  componentColors: {
    housingColor: string;
    housingRalNumber: string;
    leftTrackColor: string;
    leftTrackRalNumber: string;
    rightTrackColor: string;
    rightTrackRalNumber: string;
    weightBarColor: string;
    weightBarRalNumber: string;
    leftUChannelColor: string;
    leftUChannelRalNumber: string;
    rightUChannelColor: string;
    rightUChannelRalNumber: string;
  };
  // Fabric Options (per-unit)
  fabricType: string;
  fabricColor: string;
  // Window panel config
  includeWindowPanel: boolean;
  windowConfig: WindowConfig;
}

interface OrderFormData {
  // Dealer info (read-only from auth)
  dealerCompany: string;
  contactName: string;
  email: string;
  phone: string;

  // Customer info (who the screens are for)
  customerName: string;
  customerAddress: string;
  customerCity: string;
  customerState: string;
  customerZip: string;
  customerPhone: string;
  customerEmail: string;

  // Customer lead linking (CRM)
  customerLeadId: string;

  // Order details
  orderDate: string;
  neededByDate: string;
  poNumber: string;
  sidemark: string;
  orderedBy: string;
  screenLocation: string;
  // Rush flag — when true, +25% surcharge of screen subtotal (after dealer
  // discount) added to the order AND production-floor work cards prioritize
  // this order. Dealer must double-confirm via modal before this gets set.
  isRush: boolean;

  // Shipping method
  shippingMethod: 'ship' | 'willcall';
  shippingAddressSource: 'dealer' | 'customer' | 'custom' | 'willcall';
  shipStreet: string;
  shipCity: string;
  shipState: string;
  shipZip: string;

  // Freight options
  liftgateDelivery: boolean;
  residentialDelivery: boolean;
  insideDelivery: boolean;

  // Installation options
  sameInstallationForAll: boolean;
  sameSystemType: boolean;
  sameInstallType: boolean;
  sameTrackMount: boolean;
  sameUchannel: boolean;
  sameScrewCovers: boolean;
  sameBrushSize: boolean;
  globalSystemType: SystemType;
  globalInstallationType: 'wall' | 'ceiling';
  globalTrackMount: TrackMountType;
  globalUChannel: string;
  applyUChannelDeduction: boolean;
  globalScrewCovers: boolean;

  // System options
  motorType: 'Keder Screens' | 'Alpha' | 'Somfy' | 'No Motor';
  systemColor: string;
  customRalNumber: string;

  // Controllers
  remoteType: string;
  remoteQty: number;
  wallSwitchType: string;
  wallSwitchQty: number;

  // Fabric
  sameFabricForAll: boolean;
  globalFabricType: string;
  globalFabricColor: string;

  // Window panels
  includeWindowPanels: boolean;

  // Units
  units: ScreenUnit[];

  // Additional
  accessories: string;
  comments: string;

  // Sales tracking (for commission calculation)
  customerSellPrice?: string;

  // Authorization
  authorizedBy: string;
}

const createEmptyWindowConfig = (): WindowConfig => ({
  orientation: 'horizontal',
  windowCount: 1,
  windowColumns: 1,
  windowWidth: '',
  windowHeight: '',
  symmetrical: false,
  borderTop: '4',
  borderSide: '4',
  borderBottom: '4',
  dividerType: 'weld',
  dividerSize: '4',
  horizontalDividers: 0,
  verticalDividers: 0,
  windowBottomFromGround: '',
  windowTopFromGround: '',
});

// DimensionInput imported from @/components/DimensionInput

const createEmptyUnit = (): ScreenUnit => ({
  id: Math.random().toString(36).substr(2, 9),
  // Dimensions - empty for new unit
  widthTop: '',
  widthMiddle: '',
  widthBottom: '',
  heightLeft: '',
  heightCenter: '',
  heightRight: '',
  // Screen location
  screenLocation: '',
  // Installation Options
  systemType: 'box',
  installationType: 'wall',
  trackMount: 'face',
  uChannel: 'none',
  applyUChannelDeduction: false,
  boxWidthAdjustmentType: 'same',
  boxWidthAdjustmentValue: '',
  applyBoxDeduction: true,
  screwCovers: true, // Default Yes for Face mount
  screwCoverQty: 2, // Face mount = 2 tracks
  // System Options
  systemColor: 'black',
  ralNumber: '',
  motorSide: 'right',
  brushSize: '1inch',
  biasedCut: false,
  biasAmount: '',
  biasSide: 'left',
  // Per-component colors
  useCustomColors: false,
  componentColors: {
    housingColor: '',
    housingRalNumber: '',
    leftTrackColor: '',
    leftTrackRalNumber: '',
    rightTrackColor: '',
    rightTrackRalNumber: '',
    weightBarColor: '',
    weightBarRalNumber: '',
    leftUChannelColor: '',
    leftUChannelRalNumber: '',
    rightUChannelColor: '',
    rightUChannelRalNumber: '',
  },
  // Fabric Options
  fabricType: 'omega_pro_3',
  fabricColor: 'Black',
  // Window panel
  includeWindowPanel: false,
  windowConfig: createEmptyWindowConfig(),
});

// ============================================================================
// WINDOW DESIGNER COMPONENT
// ============================================================================

interface WindowDesignerProps {
  unitNum: number;
  screenWidth: number;
  screenHeight: number;
  trackMount: TrackMountType;
  motorSide: 'left' | 'right';
  uChannel: string;
  biasedCut: boolean;
  biasAmount: number;
  biasSide?: 'left' | 'right'; // which side is LONGER (dropdown selection)
  heightLeft?: number;
  heightRight?: number;
  config: WindowConfig;
  onChange: (config: WindowConfig) => void;
  isPreviewCollapsed?: boolean;
  onTogglePreview?: () => void;
}

function WindowDesigner({ unitNum, screenWidth, screenHeight, trackMount, motorSide, uChannel, biasedCut, biasAmount, biasSide, heightLeft, heightRight, config, onChange, isPreviewCollapsed = false, onTogglePreview }: WindowDesignerProps) {
  // Calculate usable area - same deductions for both inside and face mount
  const trackDeduction = 6;
  const topBoxDeduction = 5.75;
  const bottomBarDeduction = 2.5;

  const borderTop = parseFloat(config.borderTop) || 4;
  const borderSide = parseFloat(config.borderSide) || 4;
  const borderBottom = parseFloat(config.borderBottom) || 4;
  const dividerWidth = config.dividerType === 'divider' ? (parseFloat(config.dividerSize) || 4) : 0;

  // Calculate layout based on dividers
  const horizontalDividers = config.horizontalDividers || 0;
  const verticalDividers = config.verticalDividers || 0;

  // Determine rows and columns based on layout
  // Weld vs Divider is just how windows are joined - doesn't affect count
  let cols = 1;
  let rows = 1;
  if (config.orientation === 'horizontal') {
    // Horizontal layout = windows side by side (columns)
    cols = verticalDividers + 1;
    rows = 1;
  } else if (config.orientation === 'vertical') {
    // Vertical layout = windows side by side in columns
    cols = verticalDividers + 1;
    rows = 1;
  } else if (config.orientation === 'grid') {
    // Grid uses both rows and columns
    rows = horizontalDividers + 1;
    cols = verticalDividers + 1;
  }

  const windowCount = cols * rows;

  // Max window dimensions based on layout
  // Horizontal layout (windows stacked): each pane max 106"w x 52"h
  // Vertical layout (windows side by side): each pane max 52"w x 106"h
  const maxWindowWidth = config.orientation === 'vertical' || (config.orientation === 'grid' && cols > 1) ? 52 : 106;
  const maxWindowHeight = config.orientation === 'horizontal' || (config.orientation === 'grid' && rows > 1) ? 52 : 106;

  // Auto-calculate window sizes based on height-from-ground inputs
  // AUTOMATICALLY ADDS WINDOWS when calculated size exceeds max
  const autoCalculate = () => {
    // Total usable space (screen minus hardware deductions)
    const totalUsableWidth = screenWidth - trackDeduction;
    const totalUsableHeight = screenHeight - topBoxDeduction - bottomBarDeduction;

    // Parse height-from-ground inputs
    const windowBottomHeight = parseFloat(config.windowBottomFromGround) || 0;
    const windowTopHeight = parseFloat(config.windowTopFromGround) || 0;

    // Divider width - 0 for weld (no space between windows), actual width for fabric divider
    const calcDividerWidth = config.dividerType === 'divider' ? (parseFloat(config.dividerSize) || 4) : 0;

    let calcBorderBottom = Math.max(4, borderBottom);
    let calcBorderTop = Math.max(4, borderTop);
    let calcBorderSide = Math.max(4, borderSide);

    // STEP 1: Determine window area based on height inputs
    let windowAreaHeight: number;
    let windowAreaWidth: number;

    if (windowBottomHeight > 0 && windowTopHeight > windowBottomHeight) {
      // Both heights provided - window area is defined
      calcBorderBottom = Math.max(4, windowBottomHeight);
      windowAreaHeight = windowTopHeight - windowBottomHeight;
      calcBorderTop = Math.max(4, totalUsableHeight - calcBorderBottom - windowAreaHeight);
    } else if (windowBottomHeight > 0) {
      // Only bottom provided - use remaining space minus minimum top border
      calcBorderBottom = Math.max(4, windowBottomHeight);
      calcBorderTop = 4; // minimum top border
      windowAreaHeight = totalUsableHeight - calcBorderBottom - calcBorderTop;
    } else {
      // No heights provided - use defaults with minimum borders
      windowAreaHeight = totalUsableHeight - calcBorderTop - calcBorderBottom;
    }

    windowAreaWidth = totalUsableWidth - (calcBorderSide * 2);

    // STEP 2: Get user's selected window count first
    let requiredRows = 1;
    let requiredCols = 1;

    if (config.orientation === 'horizontal') {
      // Horizontal: windows side by side (columns) via verticalDividers
      requiredRows = 1;
      requiredCols = (config.verticalDividers || 0) + 1;
    } else if (config.orientation === 'vertical') {
      // Vertical: user selects columns via verticalDividers
      requiredRows = 1;
      requiredCols = (config.verticalDividers || 0) + 1;
    } else if (config.orientation === 'grid') {
      // Grid: user selects both
      requiredRows = (config.horizontalDividers || 0) + 1;
      requiredCols = (config.verticalDividers || 0) + 1;
    }

    // STEP 3: Determine max window dimensions based on orientation and dividers
    // For horizontal: 106" wide, 52" tall
    // For vertical: 52" wide, 106" tall
    // For grid: depends on number of rows/cols - only limited in dimension with multiple windows
    const maxWidth = config.orientation === 'vertical' || (config.orientation === 'grid' && requiredCols > 1) ? 52 : 106;
    const maxHeight = config.orientation === 'horizontal' || (config.orientation === 'grid' && requiredRows > 1) ? 52 : 106;

    // Keep user's divider counts - don't recalculate
    const newHorizontalDividers = config.horizontalDividers || 0;
    const newVerticalDividers = config.verticalDividers || 0;

    // Calculate total divider space
    const horizDividerSpace = newHorizontalDividers * calcDividerWidth;
    const vertDividerSpace = newVerticalDividers * calcDividerWidth;

    // STEP 5: Calculate window dimensions based on user's selection
    let calcWidth = (windowAreaWidth - vertDividerSpace) / requiredCols;
    let calcHeight = (windowAreaHeight - horizDividerSpace) / requiredRows;

    // Round window dimensions to nearest 1/8"
    calcWidth = Math.floor(calcWidth * 8) / 8;
    calcHeight = Math.floor(calcHeight * 8) / 8;

    // STEP 6: Cap window dimensions at max and add extra to top/side borders
    let extraTopBorder = 0;
    let extraSideBorder = 0;

    if (calcHeight > maxHeight) {
      // Cap height and add extra to top border
      const excessHeight = (calcHeight - maxHeight) * requiredRows;
      extraTopBorder = excessHeight;
      calcHeight = maxHeight;
    }

    if (calcWidth > maxWidth) {
      // Cap width and add extra to side borders (split evenly)
      const excessWidth = (calcWidth - maxWidth) * requiredCols;
      extraSideBorder = excessWidth / 2;
      calcWidth = maxWidth;
    }

    // Add extra space to borders
    calcBorderTop = calcBorderTop + extraTopBorder;
    calcBorderSide = calcBorderSide + extraSideBorder;

    // Round borders to nearest 1/8"
    calcBorderTop = Math.floor(calcBorderTop * 8) / 8;
    calcBorderBottom = Math.floor(calcBorderBottom * 8) / 8;
    calcBorderSide = Math.floor(calcBorderSide * 8) / 8;

    // Ensure positive values
    calcWidth = Math.max(0, calcWidth);
    calcHeight = Math.max(0, calcHeight);

    // Recalculate actual window area height after capping
    const actualWindowAreaHeight = (calcHeight * requiredRows) + horizDividerSpace;

    // Calculate windowTopFromGround based on actual window area
    let calcWindowTop = config.windowTopFromGround;
    if (windowBottomHeight > 0 && (!windowTopHeight || windowTopHeight <= windowBottomHeight)) {
      calcWindowTop = (calcBorderBottom + actualWindowAreaHeight).toString();
    }

    const totalWindows = requiredRows * requiredCols;

    onChange({
      ...config,
      windowWidth: calcWidth > 0 ? calcWidth.toString() : '',
      windowHeight: calcHeight > 0 ? calcHeight.toString() : '',
      windowCount: totalWindows,
      windowColumns: requiredCols,
      borderTop: calcBorderTop.toString(),
      borderBottom: calcBorderBottom.toString(),
      borderSide: calcBorderSide.toString(),
      windowTopFromGround: calcWindowTop,
      horizontalDividers: newHorizontalDividers,
      verticalDividers: newVerticalDividers,
      // DON'T change dividerType - let user choose weld vs fabric divider
    });
  };

  // Validation
  const windowWidth = parseFloat(config.windowWidth) || 0;
  const windowHeight = parseFloat(config.windowHeight) || 0;
  const errors: string[] = [];
  const warnings: string[] = [];

  if (windowWidth > maxWindowWidth) {
    errors.push(`Window width ${windowWidth}" exceeds max ${maxWindowWidth}" for this layout`);
  }
  if (windowHeight > maxWindowHeight) {
    errors.push(`Window height ${windowHeight}" exceeds max ${maxWindowHeight}" for this layout`);
  }
  if (borderTop < 4) errors.push('Top border minimum is 4"');
  if (borderSide < 4) errors.push('Side border minimum is 4"');
  if (borderBottom < 4) errors.push('Bottom border minimum is 4"');

  // Check if dimensions seem unusual
  if (windowWidth > 0 && windowWidth < 12) {
    warnings.push('Window width seems narrow (< 12")');
  }
  if (windowHeight > 0 && windowHeight < 12) {
    warnings.push('Window height seems short (< 12")');
  }

  const usableWidth = screenWidth - trackDeduction - (borderSide * 2);
  const usableHeight = screenHeight - topBoxDeduction - bottomBarDeduction - borderTop - borderBottom;
  const dividerSize = dividerWidth;
  const requiredWidth = (cols * windowWidth) + ((cols - 1) * dividerWidth);
  const requiredHeight = (rows * windowHeight) + ((rows - 1) * dividerWidth);

  if (windowWidth > 0 && requiredWidth > usableWidth + 0.5) {
    errors.push(`Total width ${requiredWidth.toFixed(1)}" exceeds usable ${usableWidth.toFixed(1)}"`);
  }
  if (windowHeight > 0 && requiredHeight > usableHeight + 0.5) {
    errors.push(`Total height ${requiredHeight.toFixed(1)}" exceeds usable ${usableHeight.toFixed(1)}"`);
  }

  return (
    <div className="mt-3 p-4 bg-green-900/20 border border-green-500 rounded-lg">
      <h4 className="text-green-400 font-semibold mb-3">Window Panel Designer - Unit #{unitNum}</h4>

      <p className="text-gray-400 text-sm mb-4">
        Strataglass Clear Vinyl used by default. Call for other options.
      </p>

      {/* LAYOUT SECTION - Window Orientation */}
      <div className="mb-4">
        <h5 className="text-gray-300 font-medium text-sm mb-2 uppercase tracking-wide">Layout</h5>
        <p className="text-gray-500 text-xs mb-3">Choose how windows are arranged</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onChange({
              ...config,
              orientation: 'horizontal',
              // Clear fields for recalculation but DON'T change dividerType
              windowBottomFromGround: '',
              windowTopFromGround: '',
              windowWidth: '',
              windowHeight: '',
              borderTop: '',
              borderSide: '',
              borderBottom: '',
            })}
            className={clsx(
              'flex-1 px-3 py-2 text-sm rounded border transition-colors',
              config.orientation === 'horizontal'
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
            )}
          >
            Horizontal
            <span className="block text-[10px] opacity-70">Rows</span>
            <span className="block text-[10px] opacity-50">max 106" × 52"</span>
          </button>
          <button
            type="button"
            onClick={() => onChange({
              ...config,
              orientation: 'vertical',
              // Clear fields for recalculation but DON'T change dividerType
              windowBottomFromGround: '',
              windowTopFromGround: '',
              windowWidth: '',
              windowHeight: '',
              borderTop: '',
              borderSide: '',
              borderBottom: '',
            })}
            className={clsx(
              'flex-1 px-3 py-2 text-sm rounded border transition-colors',
              config.orientation === 'vertical'
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
            )}
          >
            Vertical
            <span className="block text-[10px] opacity-70">Columns</span>
            <span className="block text-[10px] opacity-50">max 52" × 106"</span>
          </button>
          <button
            type="button"
            onClick={() => onChange({
              ...config,
              orientation: 'grid',
              horizontalDividers: Math.max(1, config.horizontalDividers || 1),
              verticalDividers: Math.max(1, config.verticalDividers || 1),
              // Clear fields for recalculation but DON'T change dividerType
              windowBottomFromGround: '',
              windowTopFromGround: '',
              windowWidth: '',
              windowHeight: '',
              borderTop: '',
              borderSide: '',
              borderBottom: '',
            })}
            className={clsx(
              'flex-1 px-3 py-2 text-sm rounded border transition-colors',
              config.orientation === 'grid'
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
            )}
          >
            Grid
            <span className="block text-[10px] opacity-70">Rows × Columns</span>
            <span className="block text-[10px] opacity-50">max 106" × 52" or 52" × 106"</span>
          </button>
        </div>
      </div>

      {/* GRID OPTIONS - Rows and Columns (shown when Grid is selected) */}
      {config.orientation === 'grid' && (
        <div className="mb-4">
          <h5 className="text-gray-300 font-medium text-sm mb-2 uppercase tracking-wide">Grid Size</h5>
          <div className="p-3 bg-gray-800/50 rounded-lg space-y-4">
            {/* Rows Selection */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Number of Rows</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => onChange({
                      ...config,
                      horizontalDividers: n - 1,
                      windowBottomFromGround: '',
                      windowTopFromGround: '',
                      windowWidth: '',
                      windowHeight: '',
                      borderTop: '',
                      borderSide: '',
                      borderBottom: ''
                    })}
                    className={clsx(
                      'w-10 h-10 rounded border text-sm font-medium transition-colors',
                      (config.horizontalDividers || 0) + 1 === n
                        ? 'bg-green-600 border-green-500 text-white'
                        : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Columns Selection */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Number of Columns</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => onChange({
                      ...config,
                      verticalDividers: n - 1,
                      windowBottomFromGround: '',
                      windowTopFromGround: '',
                      windowWidth: '',
                      windowHeight: '',
                      borderTop: '',
                      borderSide: '',
                      borderBottom: ''
                    })}
                    className={clsx(
                      'w-10 h-10 rounded border text-sm font-medium transition-colors',
                      (config.verticalDividers || 0) + 1 === n
                        ? 'bg-green-600 border-green-500 text-white'
                        : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid summary */}
            <div className="p-2 bg-blue-900/30 border border-blue-500 rounded text-sm">
              <span className="text-blue-400 font-medium">Grid: </span>
              <span className="text-white font-bold">
                {(config.horizontalDividers || 0) + 1} rows × {(config.verticalDividers || 0) + 1} columns = {((config.horizontalDividers || 0) + 1) * ((config.verticalDividers || 0) + 1)} windows
              </span>
            </div>
          </div>
        </div>
      )}

      {/* DIVIDERS SECTION - Weld vs Fabric Divider (shown for ALL layouts) */}
      <div className="mb-4">
        <h5 className="text-gray-300 font-medium text-sm mb-2 uppercase tracking-wide">Divider Type</h5>
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            onClick={() => onChange({
              ...config,
              dividerType: 'weld',
              // Keep divider counts - weld vs fabric is just how windows are joined
              windowBottomFromGround: '',
              windowTopFromGround: '',
              windowWidth: '',
              windowHeight: '',
              borderTop: '',
              borderSide: '',
              borderBottom: '',
            })}
            className={clsx(
              'flex-1 px-3 py-2 text-sm rounded border transition-colors',
              config.dividerType === 'weld'
                ? 'bg-green-600 border-green-500 text-white'
                : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
            )}
          >
            Weld
            <span className="block text-[10px] opacity-70">Welded seams between windows</span>
          </button>
          <button
            type="button"
            onClick={() => onChange({
              ...config,
              dividerType: 'divider',
              // Keep divider counts - weld vs fabric is just how windows are joined
              windowBottomFromGround: '',
              windowTopFromGround: '',
              windowWidth: '',
              windowHeight: '',
              borderTop: '',
              borderSide: '',
              borderBottom: '',
            })}
            className={clsx(
              'flex-1 px-3 py-2 text-sm rounded border transition-colors',
              config.dividerType === 'divider'
                ? 'bg-green-600 border-green-500 text-white'
                : 'bg-gray-800 border-gray-600 text-gray-300 hover:border-gray-500'
            )}
          >
            Fabric Divider
            <span className="block text-[10px] opacity-70">Fabric strips between windows</span>
          </button>
        </div>

        {/* Number of windows for horizontal/vertical layouts */}
        {config.orientation === 'horizontal' && (
          <div className="p-3 bg-gray-800/50 rounded-lg mb-3">
            <label className="block text-xs text-gray-400 mb-2">
              Number of Windows (columns)
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5, 6].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => onChange({ ...config, verticalDividers: n - 1, windowBottomFromGround: '', windowTopFromGround: '', windowWidth: '', windowHeight: '', borderTop: '', borderSide: '', borderBottom: '' })}
                  className={clsx(
                    'w-10 h-10 rounded border text-sm font-medium transition-colors',
                    (config.verticalDividers || 0) + 1 === n
                      ? 'bg-green-600 border-green-500 text-white'
                      : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {config.orientation === 'vertical' && (
          <div className="p-3 bg-gray-800/50 rounded-lg mb-3">
            <label className="block text-xs text-gray-400 mb-2">
              Number of Windows (columns)
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5, 6].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => onChange({ ...config, verticalDividers: n - 1, windowBottomFromGround: '', windowTopFromGround: '', windowWidth: '', windowHeight: '', borderTop: '', borderSide: '', borderBottom: '' })}
                  className={clsx(
                    'w-10 h-10 rounded border text-sm font-medium transition-colors',
                    (config.verticalDividers || 0) + 1 === n
                      ? 'bg-green-600 border-green-500 text-white'
                      : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-gray-500'
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Divider Width - only for fabric divider */}
        {config.dividerType === 'divider' && (
          <div className="p-3 bg-gray-800/50 rounded-lg">
            <label className="block text-xs text-gray-400 mb-1">Divider Width (in)</label>
            <input
              type="number"
              step="0.125"
              min="4"
              max="12"
              value={config.dividerSize}
              onChange={(e) => onChange({ ...config, dividerSize: e.target.value })}
              onWheel={(e) => e.currentTarget.blur()}
              className="input-field w-32 text-sm"
              placeholder="4"
            />
            <span className="text-xs text-gray-500 ml-2">Min: 4"</span>
          </div>
        )}

        {/* Window count summary for all layouts */}
        {config.orientation !== 'grid' && (
          <div className="mt-3 p-2 bg-blue-900/30 border border-blue-500 rounded text-sm">
            <span className="text-blue-400 font-medium">Total Windows: </span>
            <span className="text-white font-bold">
              {config.orientation === 'horizontal' ? (config.verticalDividers || 0) + 1 : (config.verticalDividers || 0) + 1}
            </span>
            <span className="text-gray-400 text-xs ml-2">
              (1 included, each additional = $175)
            </span>
          </div>
        )}
      </div>

      {/* WINDOW POSITION SECTION */}
      <div className="mb-4">
        <h5 className="text-gray-300 font-medium text-sm mb-2 uppercase tracking-wide">Window Position</h5>
        <p className="text-gray-500 text-xs mb-3">Enter heights from ground level to define window placement</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Bottom of Window from Ground (in)</label>
            <input
              type="number"
              step="0.125"
              min="4"
              value={config.windowBottomFromGround}
              onChange={(e) => {
                const bottomHeight = e.target.value;
                // Auto-calculate borderBottom (accounting for weight bar deduction)
                const calcBorderBottom = Math.max(4, parseFloat(bottomHeight) || 4);
                onChange({
                  ...config,
                  windowBottomFromGround: bottomHeight,
                  borderBottom: calcBorderBottom.toString()
                });
              }}
              onWheel={(e) => e.currentTarget.blur()}
              className="input-field w-full text-sm"
              placeholder="e.g., 36"
            />
            {parseFloat(config.windowBottomFromGround) > 0 && parseFloat(config.windowBottomFromGround) < 4 && (
              <p className="text-red-500 text-[10px] mt-1 font-medium">4" minimum required</p>
            )}
            {!(parseFloat(config.windowBottomFromGround) > 0 && parseFloat(config.windowBottomFromGround) < 4) && (
              <p className="text-gray-600 text-[10px] mt-1">Where the clear vinyl starts (4" min)</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Top of Window from Ground (in)</label>
            <input
              type="number"
              step="0.125"
              min="0"
              value={config.windowTopFromGround}
              onChange={(e) => {
                const topHeight = e.target.value;
                onChange({
                  ...config,
                  windowTopFromGround: topHeight
                });
              }}
              onWheel={(e) => e.currentTarget.blur()}
              className="input-field w-full text-sm"
              placeholder="e.g., 84"
            />
            <p className="text-gray-600 text-[10px] mt-1">Where the clear vinyl ends</p>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Side Borders (in)</label>
            <input
              type="number"
              step="0.125"
              value={config.borderSide}
              onChange={(e) => onChange({ ...config, borderSide: e.target.value })}
              onWheel={(e) => e.currentTarget.blur()}
              className="input-field w-full text-sm"
              placeholder="4"
            />
            {parseFloat(config.borderSide) > 0 && parseFloat(config.borderSide) < 4 && (
              <p className="text-red-500 text-[10px] mt-1 font-medium">4" minimum required</p>
            )}
            {!(parseFloat(config.borderSide) > 0 && parseFloat(config.borderSide) < 4) && (
              <p className="text-gray-600 text-[10px] mt-1">Fabric on left & right sides</p>
            )}
          </div>
        </div>

        {/* Calculated borders display */}
        {config.windowBottomFromGround && config.windowTopFromGround && (
          <div className="mt-3 p-2 bg-gray-800/50 rounded text-xs">
            <div className="text-gray-400">
              <span className="font-medium text-gray-300">Calculated: </span>
              Bottom border: <span className={parseFloat(config.borderBottom) < 4 ? 'text-red-500 font-medium' : ''}>{config.borderBottom}"</span> |
              Window area: {(parseFloat(config.windowTopFromGround) - parseFloat(config.windowBottomFromGround)).toFixed(1)}" |
              Top border: <span className={parseFloat(config.borderTop) < 4 ? 'text-red-500 font-medium' : ''}>{config.borderTop}"</span>
            </div>
            {(parseFloat(config.borderBottom) < 4 || parseFloat(config.borderTop) < 4 || parseFloat(config.borderSide) < 4) && (
              <p className="text-red-500 font-medium mt-1">4" minimum required on all borders</p>
            )}
          </div>
        )}
      </div>

      {/* UPDATE PREVIEW BUTTON */}
      <div className="mb-4">
        <button
          type="button"
          onClick={autoCalculate}
          disabled={!(parseFloat(config.windowBottomFromGround) > 0 || parseFloat(config.windowTopFromGround) > 0)}
          className={`w-full px-4 py-3 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 ${
            (parseFloat(config.windowBottomFromGround) > 0 || parseFloat(config.windowTopFromGround) > 0)
              ? 'bg-green-600 text-white hover:bg-green-500'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Update Preview
        </button>
        <p className="text-xs text-gray-500 text-center mt-1">
          {(parseFloat(config.windowBottomFromGround) > 0 || parseFloat(config.windowTopFromGround) > 0)
            ? 'Auto-calculates window sizes based on borders and dividers'
            : 'Enter Window Position (bottom or top from ground) first'}
        </p>
      </div>

      {/* Calculated Window Dimensions Display */}
      {windowWidth > 0 && windowHeight > 0 && (
        <div className="mb-4 p-3 bg-blue-900/30 border border-blue-500 rounded-lg">
          <h5 className="text-blue-400 font-medium text-sm mb-2">Calculated Window Dimensions</h5>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-400">Each Window:</span>
              <span className="text-white ml-2 font-mono">{windowWidth}" × {windowHeight}"</span>
            </div>
            <div>
              <span className="text-gray-400">Total Windows:</span>
              <span className="text-white ml-2 font-mono">{windowCount}</span>
            </div>
          </div>
          {(windowWidth > maxWindowWidth || windowHeight > maxWindowHeight) && (
            <p className="text-red-400 text-xs mt-2">
              ⚠️ Max pane size for this layout: {maxWindowWidth}" × {maxWindowHeight}"
            </p>
          )}
        </div>
      )}

      {/* Visual Preview - Enhanced with Housing, Weight Bar, Motor, U-Channel */}
      <div className="bg-gray-900 border-2 border-green-500 rounded p-4 mb-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-gray-300 font-medium text-sm">Window Panel Preview</h4>
          {onTogglePreview && (
            <button
              type="button"
              onClick={onTogglePreview}
              className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded bg-gray-700 hover:bg-gray-600"
            >
              {isPreviewCollapsed ? 'Show' : 'Hide'}
            </button>
          )}
        </div>
        {isPreviewCollapsed ? (
          <div className="text-center text-gray-400 text-sm py-2">
            {screenWidth}" × {screenHeight}" | {config.windowCount} window{config.windowCount > 1 ? 's' : ''} | {config.orientation}
          </div>
        ) : (
        <>
        <div className="flex justify-center">
          {(() => {
            // U-channel indicators
            const hasLeftUChannel = uChannel === 'both_sides' || uChannel === 'left_side';
            const hasRightUChannel = uChannel === 'both_sides' || uChannel === 'right_side';

            // Scale calculations - LARGER preview
            const maxDisplayWidth = 450;
            const maxDisplayHeight = 320;
            const scaleX = maxDisplayWidth / screenWidth;
            const scaleY = maxDisplayHeight / screenHeight;
            const scale = Math.min(scaleX, scaleY, 3);

            const displayWidth = screenWidth * scale;
            const displayHeight = screenHeight * scale;

            // Actual component sizes (scaled) - used for positioning
            const actualHousingH = topBoxDeduction * scale;
            const actualWeightBarH = bottomBarDeduction * scale;
            const actualTrackW = (trackDeduction / 2) * scale;

            // Visual minimums for labels (only affects display, not positioning)
            const housingH = Math.max(actualHousingH, 16);
            const weightBarH = Math.max(actualWeightBarH, 12);
            const trackW = Math.max(actualTrackW, 10); // Same deductions for both mount types

            // Border sizes (scaled)
            const borderT = (parseFloat(config.borderTop) || 4) * scale;
            const borderS = (parseFloat(config.borderSide) || 4) * scale;
            const borderB = (parseFloat(config.borderBottom) || 4) * scale;

            // Window sizes (scaled)
            const winW = windowWidth * scale;
            const winH = windowHeight * scale;
            const divW = dividerSize * scale;

            // Usable area - use ACTUAL scaled sizes for correct content positioning
            const usableTop = actualHousingH;
            const usableBottom = displayHeight - actualWeightBarH;
            const usableLeft = actualTrackW;
            const usableRight = displayWidth - actualTrackW;

            // Window area (inside borders)
            const windowAreaTop = usableTop + borderT;
            const windowAreaLeft = usableLeft + borderS;
            const windowAreaWidth = Math.max(0, (usableRight - usableLeft) - (borderS * 2));
            const windowAreaHeight = Math.max(0, (usableBottom - usableTop) - borderT - borderB);

            // Total windows block size
            const totalWindowsW = (cols * winW) + ((cols - 1) * divW);
            const totalWindowsH = (rows * winH) + ((rows - 1) * divW);

            // Center windows in available area
            const windowsOffsetX = windowAreaLeft + Math.max(0, (windowAreaWidth - totalWindowsW) / 2);
            const windowsOffsetY = windowAreaTop + Math.max(0, (windowAreaHeight - totalWindowsH) / 2);

            return (
              <div className="relative" style={{ width: displayWidth + 70, height: displayHeight + 50 }}>
                {/* Width dimension at top */}
                <div className="absolute top-0 left-8 right-8 flex justify-center text-sm text-keder-yellow font-bold">
                  <span>{screenWidth}"</span>
                </div>

                {/* Height dimension on right */}
                <div
                  className="absolute right-0 flex items-center justify-center"
                  style={{ top: 25, height: displayHeight }}
                >
                  <div className="transform rotate-90 text-sm text-keder-yellow font-bold whitespace-nowrap">
                    {screenHeight}"
                  </div>
                </div>

                {/* Main preview container */}
                <div
                  className="absolute border-2 border-gray-600 bg-gray-800 overflow-hidden"
                  style={{ left: 25, top: 20, width: displayWidth, height: displayHeight }}
                >
                {/* HOUSING at top with Motor indicator */}
                <div
                  className="absolute left-0 right-0 bg-gray-600 flex items-center justify-center rounded-t z-20"
                  style={{ top: 0, height: Math.max(actualHousingH, 14) }}
                >
                  <span className="text-[10px] text-gray-200 font-semibold">HOUSING</span>
                  {/* Motor indicator ON the housing */}
                  <div
                    className={clsx(
                      'absolute bg-yellow-500 text-black text-[8px] font-bold px-1.5 py-0.5 rounded',
                      motorSide === 'left' ? 'left-2' : 'right-2'
                    )}
                  >
                    M
                  </div>
                </div>

                {/* WEIGHT BAR at bottom - with bias cut support */}
                {biasedCut && biasAmount > 0 ? (
                  <svg
                    className="absolute bottom-0 left-0 right-0 overflow-visible z-20"
                    style={{ height: Math.max(actualWeightBarH, 10) + 12 }}
                    viewBox={`0 0 ${displayWidth} ${Math.max(actualWeightBarH, 10) + 12}`}
                    preserveAspectRatio="none"
                  >
                    {(() => {
                      const barH = Math.max(actualWeightBarH, 10);
                      // Scale bias to be more visible
                      const biasVisual = Math.min(biasAmount * 3, 12);
                      // Bar droops lower on the taller side (more fabric = weight bar hangs lower).
                      // Use the "Longer Side" dropdown (biasSide) as the source of truth;
                      // fall back to heightLeft vs heightRight when biasSide isn't set
                      // (heights are often equal — dropdown is the real authority).
                      const leftTaller = biasSide
                        ? biasSide === 'left'
                        : (heightLeft || 0) > (heightRight || 0);
                      const leftY = leftTaller ? biasVisual : 0;
                      const rightY = leftTaller ? 0 : biasVisual;

                      return (
                        <>
                          <polygon
                            points={`0,${leftY} ${displayWidth},${rightY} ${displayWidth},${rightY + barH} 0,${leftY + barH}`}
                            fill="#4b5563"
                            stroke="#374151"
                            strokeWidth="1"
                          />
                          <text
                            x={displayWidth / 2}
                            y={(leftY + rightY) / 2 + barH / 2 + 4}
                            textAnchor="middle"
                            fill="#e5e7eb"
                            fontSize="10"
                            fontWeight="600"
                          >
                            WEIGHT BAR
                          </text>
                          {/* Bias indicator — shows on the taller/drooping side */}
                          <text
                            x={leftTaller ? 12 : displayWidth - 12}
                            y={Math.max(leftY, rightY) + barH + 10}
                            textAnchor="middle"
                            fill="#f59e0b"
                            fontSize="10"
                            fontWeight="bold"
                          >
                            {biasAmount}"↓
                          </text>
                        </>
                      );
                    })()}
                  </svg>
                ) : (
                  <div
                    className="absolute left-0 right-0 bg-gray-500 flex items-center justify-center rounded-b z-20"
                    style={{ bottom: 0, height: Math.max(actualWeightBarH, 10) }}
                  >
                    <span className="text-[10px] text-gray-200 font-semibold">WEIGHT BAR</span>
                  </div>
                )}

                {/* Left track - same size for both mount types */}
                <div
                  className={clsx(
                    'absolute left-0 bg-gray-700 z-10',
                    hasLeftUChannel && 'border-l-4 border-blue-400'
                  )}
                  style={{
                    top: actualHousingH,
                    width: Math.max(actualTrackW, 8),
                    bottom: actualWeightBarH
                  }}
                >
                  <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[7px] text-gray-300 -rotate-90 whitespace-nowrap font-medium">TRACK</span>
                  {hasLeftUChannel && (
                    <div className="absolute -left-4 top-1/2 transform -translate-y-1/2 -rotate-90 text-[8px] text-blue-400 whitespace-nowrap font-bold">
                      U-CH
                    </div>
                  )}
                </div>

                {/* Right track - same size for both mount types */}
                <div
                  className={clsx(
                    'absolute right-0 bg-gray-700 z-10',
                    hasRightUChannel && 'border-r-4 border-blue-400'
                  )}
                  style={{
                    top: actualHousingH,
                    width: Math.max(actualTrackW, 8),
                    bottom: actualWeightBarH
                  }}
                >
                  <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[7px] text-gray-300 rotate-90 whitespace-nowrap font-medium">TRACK</span>
                  {hasRightUChannel && (
                    <div className="absolute -right-4 top-1/2 transform -translate-y-1/2 rotate-90 text-[8px] text-blue-400 whitespace-nowrap font-bold">
                      U-CH
                    </div>
                  )}
                </div>

                {/* Top border (green) */}
                <div
                  className="absolute bg-green-800/60 border-b-2 border-dashed border-green-400 flex items-center justify-center overflow-hidden"
                  style={{
                    top: actualHousingH,
                    left: actualTrackW,
                    width: displayWidth - (actualTrackW * 2),
                    height: borderT,
                  }}
                >
                  <span className="text-[10px] text-green-300 font-semibold">{config.borderTop}"</span>
                </div>

                {/* Bottom border (green) */}
                <div
                  className="absolute bg-green-800/60 border-t-2 border-dashed border-green-400 flex items-center justify-center overflow-hidden"
                  style={{
                    bottom: actualWeightBarH,
                    left: actualTrackW,
                    width: displayWidth - (actualTrackW * 2),
                    height: borderB,
                  }}
                >
                  <span className="text-[10px] text-green-300 font-semibold">{config.borderBottom}"</span>
                </div>

                {/* Left border (green) */}
                <div
                  className="absolute bg-green-800/60 border-r-2 border-dashed border-green-400 flex items-center justify-center overflow-hidden"
                  style={{
                    top: actualHousingH + borderT,
                    left: actualTrackW,
                    width: borderS,
                    height: Math.max(0, (usableBottom - usableTop) - borderT - borderB),
                  }}
                >
                  <span className="text-[9px] text-green-300 font-semibold transform -rotate-90 whitespace-nowrap">{config.borderSide}"</span>
                </div>

                {/* Right border (green) */}
                <div
                  className="absolute bg-green-800/60 border-l-2 border-dashed border-green-400 flex items-center justify-center overflow-hidden"
                  style={{
                    top: actualHousingH + borderT,
                    right: actualTrackW,
                    width: borderS,
                    height: Math.max(0, (usableBottom - usableTop) - borderT - borderB),
                  }}
                >
                  <span className="text-[9px] text-green-300 font-semibold transform rotate-90 whitespace-nowrap">{config.borderSide}"</span>
                </div>

                {/* Fabric area background (inside borders) */}
                <div
                  className="absolute bg-gray-900/80"
                  style={{
                    left: windowAreaLeft,
                    top: windowAreaTop,
                    width: windowAreaWidth,
                    height: windowAreaHeight,
                  }}
                />

                {/* Windows grid */}
                {windowWidth > 0 && windowHeight > 0 ? (
                  <>
                    {Array.from({ length: rows }).map((_, rowIdx) =>
                      Array.from({ length: cols }).map((_, colIdx) => {
                        const windowIdx = rowIdx * cols + colIdx;
                        if (windowIdx >= config.windowCount) return null;

                        const x = windowsOffsetX + (colIdx * (winW + divW));
                        const y = windowsOffsetY + (rowIdx * (winH + divW));

                        return (
                          <React.Fragment key={`window-${rowIdx}-${colIdx}`}>
                            {/* Window */}
                            <div
                              className="absolute bg-blue-500/40 border-2 border-blue-400 flex items-center justify-center"
                              style={{ left: x, top: y, width: winW, height: winH }}
                            >
                              <span className="text-[11px] text-blue-200 text-center leading-tight font-semibold">
                                {windowWidth}"×{windowHeight}"
                              </span>
                            </div>

                            {/* Vertical divider (if not last column) */}
                            {config.dividerType === 'divider' && colIdx < cols - 1 && windowIdx < config.windowCount - 1 && (
                              <div
                                className="absolute bg-green-600/60 flex items-center justify-center"
                                style={{ left: x + winW, top: y, width: divW, height: winH }}
                              >
                                <span className="text-[8px] text-green-200 transform -rotate-90 font-medium">{config.dividerSize}"</span>
                              </div>
                            )}

                            {/* Horizontal divider (if not last row) */}
                            {config.dividerType === 'divider' && rowIdx < rows - 1 && (rowIdx + 1) * cols < config.windowCount && (
                              <div
                                className="absolute bg-green-600/60 flex items-center justify-center"
                                style={{ left: x, top: y + winH, width: winW, height: divW }}
                              >
                                <span className="text-[8px] text-green-200 font-medium">{config.dividerSize}"</span>
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </>
                ) : (
                  <div
                    className="absolute flex items-center justify-center text-gray-500 text-xs"
                    style={{
                      left: windowAreaLeft,
                      top: windowAreaTop,
                      width: windowAreaWidth,
                      height: windowAreaHeight,
                    }}
                  >
                    Click Update Preview<br/>or enter window size
                  </div>
                )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Legend */}
        <div className="mt-3 flex flex-wrap gap-3 justify-center text-[9px]">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-gray-600 rounded"></div>
            <span className="text-gray-400">Housing/Bar</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-800/50 border border-dashed border-green-500 rounded"></div>
            <span className="text-gray-400">Borders</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-500/30 border-2 border-blue-400 rounded"></div>
            <span className="text-gray-400">Windows</span>
          </div>
          {config.dividerType === 'divider' && (
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-600/50 rounded"></div>
              <span className="text-gray-400">Dividers</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-500 rounded flex items-center justify-center text-[6px] text-black font-bold">M</div>
            <span className="text-gray-400">Motor</span>
          </div>
          {(uChannel !== 'none') && (
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 border-l-2 border-blue-400 bg-gray-700 rounded"></div>
              <span className="text-gray-400">U-Channel</span>
            </div>
          )}
          {biasedCut && (
            <div className="flex items-center gap-1">
              <span className="text-yellow-500">↓</span>
              <span className="text-gray-400">Bias Cut</span>
            </div>
          )}
        </div>

        {/* Screen info bar */}
        <div className="mt-2 text-center text-gray-400 text-xs">
          {screenWidth}"W × {screenHeight}"H | {TRACK_MOUNT_OPTIONS.find(opt => opt.value === trackMount)?.label || trackMount} Mount | Motor: {motorSide === 'left' ? 'Left' : 'Right'}
          {biasedCut && biasAmount > 0 && ` | Bias: ${biasAmount}"`}
          {uChannel !== 'none' && ` | U-Ch: ${uChannel.replace('_', ' ')}`}
        </div>
        </>
        )}
      </div>

      {/* Validation errors */}
      {errors.length > 0 && (
        <div className="bg-red-900/30 border border-red-500 rounded p-2 mb-3">
          {errors.map((err, i) => (
            <div key={i} className="text-red-400 text-sm">* {err}</div>
          ))}
        </div>
      )}

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="bg-yellow-900/30 border border-yellow-500 rounded p-2 mb-3">
          {warnings.map((warn, i) => (
            <div key={i} className="text-yellow-400 text-sm">⚠️ {warn}</div>
          ))}
        </div>
      )}

      {/* Summary */}
      <div className="bg-green-900/30 border border-green-600 rounded p-2">
        <span className="text-green-400 font-medium text-sm">Summary: </span>
        <span className="text-gray-300 text-sm">
          {windowWidth > 0 && windowHeight > 0 ? (
            <>
              {windowCount} window{windowCount > 1 ? 's' : ''} @ {windowWidth}" x {windowHeight}" each
              {rows > 1 && cols > 1 && ` (${cols} cols x ${rows} rows)`}
              {rows > 1 && cols === 1 && ` (${rows} rows)`}
              {cols > 1 && rows === 1 && ` (${cols} columns)`}
              {' | '}{config.dividerType === 'weld' ? 'Welded (no dividers)' : `${config.dividerSize}" dividers`}
              {' | '}Total: {requiredWidth.toFixed(1)}"W x {requiredHeight.toFixed(1)}"H
            </>
          ) : (
            'Click "Update Preview" to calculate window sizes'
          )}
        </span>
      </div>

      {/* Design review notice */}
      <div className="mt-3 p-2 bg-yellow-900/30 border border-yellow-500 rounded text-yellow-400 text-sm">
        Window Designs require review and approval before production.
      </div>
    </div>
  );
}

// ============================================================================
// SCREEN UNIT VISUAL RENDERER
// ============================================================================

interface ScreenUnitVisualRendererProps {
  widthTop: number;
  widthMiddle: number;
  widthBottom: number;
  heightLeft: number;
  heightCenter: number;
  heightRight: number;
  motorSide: 'left' | 'right';
  trackMount: TrackMountType;
  installationType: 'wall' | 'ceiling';
  uChannel: string;
  biasedCut: boolean;
  biasAmount: number;
  biasSide?: 'left' | 'right'; // which side is LONGER (matches dropdown)
  boxWidthAdjustment?: number;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isExpertMode?: boolean;
}

function ScreenUnitVisualRenderer({
  widthTop,
  widthMiddle,
  widthBottom,
  heightLeft,
  heightCenter,
  heightRight,
  motorSide,
  trackMount,
  installationType,
  uChannel,
  biasedCut,
  biasAmount,
  boxWidthAdjustment = 0,
  biasSide,
  isCollapsed = false,
  onToggleCollapse,
  isExpertMode = false,
}: ScreenUnitVisualRendererProps) {
  // Calculate dimensions based on installation type and track mount
  // Wall → use largest (max) measurements
  // Ceiling/Recessed + Inside/Recessed track mount → use smallest (min) measurements
  // Track mounts that require smallest: inside, inside_face_left, inside_face_right, recessed
  // Track mounts that use largest: face, inside_outside_left, inside_outside_right
  const usesInsideMount = ['inside', 'inside_face_left', 'inside_face_right', 'recessed'].includes(trackMount);
  const useSmallest = installationType === 'ceiling' && usesInsideMount;

  const widthValues = [widthTop || 0, widthMiddle || 0, widthBottom || 0].filter(v => v > 0);
  const heightValues = [heightLeft || 0, heightCenter || 0, heightRight || 0].filter(v => v > 0);

  const displayWidth = widthValues.length > 0
    ? (useSmallest ? Math.min(...widthValues) : Math.max(...widthValues))
    : 0;
  const displayHeight = heightValues.length > 0
    ? (useSmallest ? Math.min(...heightValues) : Math.max(...heightValues))
    : 0;

  // Keep max values for reference
  const maxWidth = Math.max(widthTop || 0, widthMiddle || 0, widthBottom || 0);
  const maxHeight = Math.max(heightLeft || 0, heightCenter || 0, heightRight || 0);

  if (displayWidth === 0 || displayHeight === 0) {
    return (
      <div className="p-4 bg-gray-800/50 border border-gray-600 rounded-lg text-center">
        <p className="text-gray-400 text-sm">Enter dimensions to see screen preview</p>
      </div>
    );
  }

  // U-channel indicator
  const hasLeftUChannel = uChannel === 'both_sides' || uChannel === 'left_side';
  const hasRightUChannel = uChannel === 'both_sides' || uChannel === 'right_side';

  // Calculate final system size for display based on track mount type
  // Face = +6" total (3" per side) - both sides face mount
  // Inside/Outside Left = +3" (left side outside)
  // Inside/Outside Right = +3" (right side outside)
  // All others (Inside, Inside/Face Left, Inside/Face Right, Recessed) = no addition
  const getTrackMountAddition = (): { total: number; description: string } => {
    switch (trackMount) {
      case 'face':
        return { total: 6, description: '+6" (3" per side face mount)' };
      case 'inside_outside_left':
        return { total: 3, description: '+3" (left side outside)' };
      case 'inside_outside_right':
        return { total: 3, description: '+3" (right side outside)' };
      default:
        return { total: 0, description: '' };
    }
  };
  const trackMountInfo = getTrackMountAddition();
  const trackMountAddition = trackMountInfo.total;
  const finalSystemWidth = maxWidth + trackMountAddition;
  const finalSystemHeight = maxHeight; // Height not affected by track mount

  return (
    <div className="p-4 bg-gray-800/50 border border-gray-600 rounded-lg">
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-gray-300 font-medium text-sm">Screen Preview</h4>
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded bg-gray-700 hover:bg-gray-600"
          >
            {isCollapsed ? 'Show' : 'Hide'}
          </button>
        )}
      </div>

      {!isCollapsed && (
        <>
          {/* Dimension Labels - Opening Size vs Calculated Final Size */}
          <div className="mb-3 p-3 bg-gray-900/50 rounded border border-gray-700">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-2 text-sm">
              <div>
                <span className="text-gray-400">{isExpertMode ? 'System Size: ' : 'Opening Size: '}</span>
                <span className="text-white font-semibold">{maxWidth}" × {maxHeight}"</span>
              </div>
              {trackMountAddition > 0 && !isExpertMode && (
                <div>
                  <span className="text-gray-400">Calculated Final Size: </span>
                  <span className="text-keder-yellow font-semibold">{finalSystemWidth}" × {finalSystemHeight}"</span>
                  <span className="text-gray-500 text-xs ml-1">({trackMountInfo.description})</span>
                </div>
              )}
            </div>
            {boxWidthAdjustment !== 0 && (
              <div className="flex gap-4 text-sm mt-2">
                <div>
                  <span className="text-gray-400">System Width: </span>
                  <span className="text-white font-semibold">{finalSystemWidth}"</span>
                </div>
                <div>
                  <span className="text-gray-400">Box Width: </span>
                  <span className="text-blue-400 font-semibold">{(finalSystemWidth + boxWidthAdjustment).toFixed(2)}"</span>
                  <span className="text-gray-500 text-xs ml-1">({boxWidthAdjustment > 0 ? '+' : ''}{boxWidthAdjustment}" adjustment)</span>
                </div>
              </div>
            )}
            <p className="text-gray-500 text-xs mt-2">
              {isExpertMode
                ? 'Exact mode: width × height as entered — no mount-type additions (e.g., +3" outside-mount). The ¼" box deduction (if you checked it above) still applies to the box width.'
                : 'Preview shows opening dimensions. Factory calculates final system size at production.'}
            </p>
          </div>

          {(() => {
            // LARGER preview - 400x280 max
            // Use displayWidth/displayHeight from component scope (based on install type)
            const maxCanvasWidth = 400;
            const maxCanvasHeight = 280;
            const scaleX = maxCanvasWidth / displayWidth;
            const scaleY = maxCanvasHeight / displayHeight;
            const scale = Math.min(scaleX, scaleY, 3);

            const scaledWidth = displayWidth * scale;
            const scaledHeight = displayHeight * scale;

            // Component sizes - larger for visibility (same for both mount types)
            const housingHeight = 18;
            const weightBarHeight = 14;
            const trackWidth = 12; // Same track width for both mount types

            return (
              <div className="flex justify-center">
                <div className="relative" style={{ width: scaledWidth + 60, height: scaledHeight + 70 }}>
                  {/* Height dimensions at top (L/C/R positions) */}
                  <div className="absolute top-0 left-8 right-8 flex justify-between text-xs text-gray-400">
                    <span>L: {heightLeft || '-'}"</span>
                    <span>C: {heightCenter || '-'}"</span>
                    <span>R: {heightRight || '-'}"</span>
                  </div>

                  {/* Width dimensions on left (T/M/B positions) */}
                  <div className="absolute left-0 flex flex-col justify-between text-xs text-gray-400" style={{ top: 25, height: scaledHeight }}>
                    <span className="transform -rotate-90 origin-left whitespace-nowrap ml-1">{widthTop || '-'}"T</span>
                    <span className="transform -rotate-90 origin-left whitespace-nowrap ml-1">{widthMiddle || '-'}"M</span>
                    <span className="transform -rotate-90 origin-left whitespace-nowrap ml-1">{widthBottom || '-'}"B</span>
                  </div>

                  {/* Main screen container */}
                  <div
                    className="absolute border-2 border-gray-500 rounded bg-gray-900 overflow-hidden"
                    style={{
                      left: 30,
                      top: 20,
                      width: scaledWidth,
                      height: scaledHeight,
                    }}
                  >
                    {/* Housing box at top with Motor indicator */}
                    <div
                      className="absolute top-0 left-0 right-0 bg-gray-600 flex items-center justify-center rounded-t z-10"
                      style={{ height: housingHeight }}
                    >
                      <span className="text-[10px] text-gray-200 font-semibold">HOUSING</span>
                      {/* Motor indicator ON the housing */}
                      <div
                        className={clsx(
                          'absolute bg-yellow-500 text-black text-[8px] font-bold px-1.5 py-0.5 rounded',
                          motorSide === 'left' ? 'left-2' : 'right-2'
                        )}
                      >
                        M
                      </div>
                    </div>

                    {/* Left Track */}
                    <div
                      className={clsx(
                        'absolute left-0 bg-gray-700 z-10',
                        hasLeftUChannel && 'border-l-4 border-blue-400'
                      )}
                      style={{
                        top: housingHeight,
                        width: trackWidth,
                        height: scaledHeight - housingHeight - weightBarHeight,
                      }}
                    >
                      <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[7px] text-gray-300 -rotate-90 whitespace-nowrap">TRACK</span>
                      {hasLeftUChannel && (
                        <div className="absolute -left-5 top-1/2 transform -translate-y-1/2 -rotate-90 text-[8px] text-blue-400 whitespace-nowrap font-bold">
                          U-CH
                        </div>
                      )}
                    </div>

                    {/* Right Track */}
                    <div
                      className={clsx(
                        'absolute right-0 bg-gray-700 z-10',
                        hasRightUChannel && 'border-r-4 border-blue-400'
                      )}
                      style={{
                        top: housingHeight,
                        width: trackWidth,
                        height: scaledHeight - housingHeight - weightBarHeight,
                      }}
                    >
                      <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[7px] text-gray-300 rotate-90 whitespace-nowrap">TRACK</span>
                      {hasRightUChannel && (
                        <div className="absolute -right-5 top-1/2 transform -translate-y-1/2 rotate-90 text-[8px] text-blue-400 whitespace-nowrap font-bold">
                          U-CH
                        </div>
                      )}
                    </div>

                    {/* Fabric area */}
                    <div
                      className="absolute bg-green-800/60 border border-green-600"
                      style={{
                        left: trackWidth,
                        right: trackWidth,
                        top: housingHeight,
                        bottom: weightBarHeight,
                      }}
                    >
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-green-400 text-sm font-medium">FABRIC</span>
                      </div>
                    </div>

                    {/* Weight bar at bottom - angled when biased */}
                    {biasedCut && biasAmount > 0 ? (
                      <svg
                        className="absolute bottom-0 left-0 right-0 overflow-visible z-10"
                        style={{ height: weightBarHeight + 12 }}
                        viewBox={`0 0 ${scaledWidth} ${weightBarHeight + 12}`}
                        preserveAspectRatio="none"
                      >
                        {(() => {
                          // Source of truth = the "Longer Side" dropdown (biasSide).
                          // When biasSide isn't set, fall back to comparing entered
                          // left vs right heights. Without the dropdown signal the
                          // dealer enters 120/120/120 + biasSide='left' + bias 3"
                          // and the renderer falsely defaulted to right-is-taller.
                          const leftIsHigher = biasSide
                            ? biasSide === 'left'
                            : (heightLeft || 0) > (heightRight || 0);
                          const biasVisual = Math.min(biasAmount * 3, 12);
                          // When left is taller, weight bar hangs LOWER on left (higher Y = lower on screen)
                          const leftY = leftIsHigher ? biasVisual : 0;
                          const rightY = leftIsHigher ? 0 : biasVisual;

                          return (
                            <>
                              <polygon
                                points={`0,${leftY} ${scaledWidth},${rightY} ${scaledWidth},${rightY + weightBarHeight} 0,${leftY + weightBarHeight}`}
                                fill="#4b5563"
                                stroke="#374151"
                                strokeWidth="1"
                              />
                              <text
                                x={scaledWidth / 2}
                                y={(leftY + rightY) / 2 + weightBarHeight / 2 + 4}
                                textAnchor="middle"
                                fill="#e5e7eb"
                                fontSize="10"
                                fontWeight="600"
                              >
                                WEIGHT BAR
                              </text>
                              <text
                                x={leftIsHigher ? 15 : scaledWidth - 15}
                                y={Math.max(leftY, rightY) + weightBarHeight + 10}
                                textAnchor="middle"
                                fill="#f59e0b"
                                fontSize="10"
                                fontWeight="bold"
                              >
                                {biasAmount}"↓
                              </text>
                            </>
                          );
                        })()}
                      </svg>
                    ) : (
                      <div
                        className="absolute bottom-0 left-0 right-0 bg-gray-500 flex items-center justify-center rounded-b z-10"
                        style={{ height: weightBarHeight }}
                      >
                        <span className="text-[10px] text-gray-200 font-semibold">WEIGHT BAR</span>
                      </div>
                    )}
                  </div>

                  {/* Height dimension on right */}
                  <div
                    className="absolute right-0 flex items-center justify-center"
                    style={{ top: 20, height: scaledHeight }}
                  >
                    <div className="transform rotate-90 text-sm text-keder-yellow font-bold whitespace-nowrap">
                      {displayHeight}"
                    </div>
                  </div>

                  {/* Width dimension at bottom */}
                  <div
                    className="absolute text-center text-sm text-keder-yellow font-bold"
                    style={{ left: 30, right: 30, bottom: 0 }}
                  >
                    {displayWidth}"
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Legend */}
          <div className="mt-3 flex flex-wrap gap-3 justify-center text-[10px]">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-600 rounded"></div>
              <span className="text-gray-400">Housing</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-800 border border-green-600 rounded"></div>
              <span className="text-gray-400">Fabric</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-700 rounded"></div>
              <span className="text-gray-400">Track</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-500 rounded flex items-center justify-center text-[6px] text-black font-bold">M</div>
              <span className="text-gray-400">Motor ({motorSide})</span>
            </div>
            {(hasLeftUChannel || hasRightUChannel) && (
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 border-2 border-blue-400 rounded"></div>
                <span className="text-gray-400">U-Channel</span>
              </div>
            )}
            {biasedCut && biasAmount > 0 && (
              <div className="flex items-center gap-1">
                <span className="text-yellow-500">↓</span>
                <span className="text-gray-400">Bias ({biasAmount}")</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Collapsed summary */}
      {isCollapsed && (
        <div className="text-center text-gray-400 text-sm">
          Opening: {maxWidth}" × {maxHeight}" | {TRACK_MOUNT_OPTIONS.find(opt => opt.value === trackMount)?.label || trackMount} mount | Motor: {motorSide}
          {trackMountAddition > 0 && !isExpertMode && <span className="text-keder-yellow"> | Final: {finalSystemWidth}" × {finalSystemHeight}"</span>}
          {biasedCut && ` | Bias: ${biasAmount}"`}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// AUTO-POPULATE BIAS HELPER
// ============================================================================

/**
 * Calculate recommended bias amount based on height measurements
 * Returns the bias amount (capped at 4") or 0 if no bias needed
 */
function calculateRecommendedBias(
  heightLeft: number,
  heightCenter: number,
  heightRight: number
): { needsBias: boolean; biasAmount: number; biasDescription: string } {
  const left = heightLeft || 0;
  const center = heightCenter || 0;
  const right = heightRight || 0;

  // Need at least two measurements to calculate
  const validMeasurements = [left, center, right].filter(h => h > 0);
  if (validMeasurements.length < 2) {
    return { needsBias: false, biasAmount: 0, biasDescription: '' };
  }

  // Calculate the maximum difference between any two measurements
  const maxHeight = Math.max(left, center, right);
  const minHeight = Math.min(...validMeasurements);
  const heightDiff = maxHeight - minHeight;

  // If difference is less than 0.25", no bias needed
  if (heightDiff < 0.25) {
    return { needsBias: false, biasAmount: 0, biasDescription: '' };
  }

  // Cap bias at 4" maximum
  const biasAmount = Math.min(heightDiff, 4);

  // Determine which side is shorter
  let biasDescription = '';
  if (left < right) {
    biasDescription = `Left side ${(right - left).toFixed(2)}" shorter`;
  } else if (right < left) {
    biasDescription = `Right side ${(left - right).toFixed(2)}" shorter`;
  } else {
    biasDescription = `Center differs by ${heightDiff.toFixed(2)}"`;
  }

  return { needsBias: true, biasAmount, biasDescription };
}

/**
 * Detect width variance and calculate box width adjustment.
 *
 * Box width base = system width base (min for inside mount).
 * The "Larger" radio adds the top-vs-min difference (capped at 0.75").
 * This way the radio always reflects the actual relationship between box and system width.
 *
 * RULE: Box can be at most 0.75" wider than system width (smallest measurement).
 */
function calculateRecommendedBoxAdjustment(
  widthTop: number, widthMiddle: number, widthBottom: number
): {
  needsAdjustment: boolean;
  adjustmentAmount: number;
  adjustmentDescription: string;
  exceedsMaxVariance: boolean;
  minWidth: number;
  variance: number;
} {
  const validWidths = [widthTop, widthMiddle, widthBottom].filter(w => w > 0);
  const noResult = { needsAdjustment: false, adjustmentAmount: 0, adjustmentDescription: '', exceedsMaxVariance: false, minWidth: 0, variance: 0 };
  if (validWidths.length < 2) return noResult;

  const minWidth = Math.min(...validWidths);
  const maxWidth = Math.max(...validWidths);
  const widthDiff = maxWidth - minWidth;

  // No significant variance
  if (widthDiff < 0.25) return { ...noResult, minWidth, variance: widthDiff };

  // Build description showing the variance
  let adjustmentDescription = '';
  if (widthTop > 0 && widthBottom > 0 && widthTop !== widthBottom) {
    const wider = widthTop > widthBottom ? 'Top' : 'Bottom';
    adjustmentDescription = `${wider} is ${Math.abs(widthTop - widthBottom).toFixed(2)}" wider`;
  } else {
    adjustmentDescription = `Width varies by ${widthDiff.toFixed(2)}"`;
  }

  // Check the 0.75" max variance rule
  const MAX_BOX_OVER_MIN = 0.75;
  const topOverMin = widthTop > minWidth ? widthTop - minWidth : 0;
  const exceedsMaxVariance = topOverMin > MAX_BOX_OVER_MIN;

  // Adjustment = how much larger the box should be vs system width (min)
  // Capped at 0.75". When top > min, box needs to be larger to match the top opening.
  let adjustmentAmount = 0;
  if (topOverMin > 0) {
    adjustmentAmount = Math.min(topOverMin, MAX_BOX_OVER_MIN);
  } else if (widthTop > 0 && widthTop < minWidth) {
    // Rare: top is narrower than bottom — suggest smaller
    adjustmentAmount = -(minWidth - widthTop);
  }

  adjustmentDescription += ` — box ${exceedsMaxVariance ? 'capped +0.75"' : adjustmentAmount > 0 ? `+${adjustmentAmount.toFixed(2)}" larger` : 'same as'} system width (${minWidth}")`;

  return { needsAdjustment: true, adjustmentAmount, adjustmentDescription, exceedsMaxVariance, minWidth, variance: topOverMin };
}

// ============================================================================
// COMPONENT
// ============================================================================

export function NewOrderPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get dealer discount from user profile (applied internally, never shown to dealers)
  const dealerDiscount = useMemo(() => {
    const u = user as any;
    return u?.discountPercent || u?.discount_percent || u?.discountRate || 0;
  }, [user]);

  const [priceMultiplier, setPriceMultiplier] = useState(1);
  const [openRollDiscountPercent, setOpenRollDiscountPercent] = useState(4);
  // Will-call is locked out unless the dealer is in TOLA (TX/OK/LA/AR) AND not
  // an auto-consolidate dealer. Backend resolves the rule + returns one bool.
  const [willCallAllowed, setWillCallAllowed] = useState(true);
  const [autoConsolidateDealer, setAutoConsolidateDealer] = useState(false);
  const [dealerState, setDealerState] = useState<string | null>(null);

  // Opt-in consolidation: dealer can choose to bundle this new order with
  // existing in-flight orders. On payment, the backend groups them all into
  // one shipment. Dealer acknowledges they're delaying shipping.
  const [eligibleToBundle, setEligibleToBundle] = useState<any[]>([]);
  const [bundleWithIds, setBundleWithIds] = useState<string[]>([]);
  const [bundleAcknowledged, setBundleAcknowledged] = useState(false);
  const [showBundleSection, setShowBundleSection] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showInvoiceConfirm, setShowInvoiceConfirm] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [calculatedPricing, setCalculatedPricing] = useState<any>(null);
  const [taxPreview, setTaxPreview] = useState(0);
  const [taxRatePct, setTaxRatePct] = useState(0);
  // Tax preview useEffect lives further down, after formData is declared
  // (must reference formData.shipState in deps without hoisting through TDZ).
  const [pendingAutoCalculate, setPendingAutoCalculate] = useState(false);

  // Promo code state — dealer types a code on the review section, we
  // validate against POST /promotions/codes/validate, show the applied
  // discount, and include it in the order submit DTO.
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoCheck, setPromoCheck] = useState<{
    checking: boolean;
    valid: boolean | null;
    discount: number;
    error: string | null;
  }>({ checking: false, valid: null, discount: 0, error: null });
  const [freightData, setFreightData] = useState<{
    method: 'shipping' | 'will_call';
    freightviewShipmentId?: string;
    selectedQuoteId?: string;
    selectedCarrierName?: string;
    selectedCarrierCode?: string;
    freightQuotes?: any[];
    freightTotalAmount?: number;
  } | null>(null);
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());
  const [collapsedPreviews, setCollapsedPreviews] = useState<Set<string>>(new Set());
  // Expert mode - persistent toggle (localStorage)
  const [isExpertMode, setIsExpertMode] = useState(() => {
    return localStorage.getItem('expertMode') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('expertMode', isExpertMode ? 'true' : 'false');
  }, [isExpertMode]);

  const [fractionMode, setFractionMode] = useState(() => {
    return localStorage.getItem('fractionMode') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('fractionMode', fractionMode ? 'true' : 'false');
  }, [fractionMode]);

  // Incomplete measurements disclaimer state
  const [incompleteUnitsToAcknowledge, setIncompleteUnitsToAcknowledge] = useState<number[]>([]);
  const [currentAcknowledgeIndex, setCurrentAcknowledgeIndex] = useState(0);
  const [acknowledgedUnits, setAcknowledgedUnits] = useState<Set<number>>(new Set());
  const [pendingSubmitAction, setPendingSubmitAction] = useState<'submit' | null>(null);
  const [orderTermsAccepted, setOrderTermsAccepted] = useState(false);
  // Rush confirmation modal — keeps dealer from accidentally checking RUSH.
  const [rushConfirmOpen, setRushConfirmOpen] = useState(false);

  // Dynamic fabric catalog from API (falls back to hardcoded)
  const [FABRIC_TYPES, setFabricTypes] = useState(FALLBACK_FABRIC_TYPES);
  const [FABRIC_COLORS, setFabricColors] = useState<Record<string, string[]>>(FALLBACK_FABRIC_COLORS);
  useEffect(() => {
    fabricSettingsApi.getCatalog().then(catalog => {
      const { types, colors } = buildFabricData(catalog);
      if (types.length > 0) {
        setFabricTypes(types);
        setFabricColors(colors);
      }
    }).catch(() => { /* use fallbacks */ });
  }, []);

  // Lead times from system settings
  const [leadTimes, setLeadTimes] = useState<LeadTimes>({
    solar: DEFAULT_LEAD_TIME_DAYS,
    premiumSolar: DEFAULT_LEAD_TIME_DAYS,
    cvw: DEFAULT_LEAD_TIME_DAYS,
    insect: DEFAULT_LEAD_TIME_DAYS,
  });


  // Fetch price increase + tariff multiplier on mount
  useEffect(() => {
    const pmToken = localStorage.getItem('auth_token');
    fetch(`${import.meta.env.VITE_API_URL || '/api'}/system-settings/pricing`, {
      headers: pmToken ? { 'Authorization': `Bearer ${pmToken}` } : {},
    })
      .then(res => res.json())
      .then(data => {
        const pi = data.price_increase_percent || 0;
        const tariff = data.tariff_percent || 0;
        const firmAdj = data.firm_price_adjustment_percent || 0;
        setPriceMultiplier((1 + pi / 100) * (1 + tariff / 100) * (1 + firmAdj / 100));
        if (data.open_roll_discount_percent !== undefined) {
          setOpenRollDiscountPercent(data.open_roll_discount_percent);
        }
      })
      .catch(err => console.error('Failed to fetch pricing settings:', err));

    // Fetch dealer's shipping rules — drives will-call lockout UI.
    import('../services/api').then(({ dealerAccountApi, shipmentsApi }) => {
      dealerAccountApi.getMyAccountStatus()
        .then(s => {
          setAutoConsolidateDealer(s.autoConsolidateOrders === true);
          setWillCallAllowed(s.willCallAllowed !== false);
          setDealerState(s.dealerState ?? null);
        })
        .catch(() => {});
      // Load orders eligible to bundle with this new one.
      shipmentsApi.getEligibleToBundle()
        .then(list => setEligibleToBundle(Array.isArray(list) ? list : []))
        .catch(() => setEligibleToBundle([]));
    });
  }, []);

  // Fetch lead times on mount
  useEffect(() => {
    // Fetch lead times from system settings
    const ltToken = localStorage.getItem('auth_token');
    fetch(`${import.meta.env.VITE_API_URL || '/api'}/system-settings/lead-times`, {
      headers: ltToken ? { 'Authorization': `Bearer ${ltToken}` } : {},
    })
      .then(res => res.json())
      .then(data => {
        setLeadTimes({
          solar: data.solar ?? DEFAULT_LEAD_TIME_DAYS,
          premiumSolar: data.premiumSolar ?? DEFAULT_LEAD_TIME_DAYS,
          cvw: data.cvw ?? DEFAULT_LEAD_TIME_DAYS,
          insect: data.insect ?? DEFAULT_LEAD_TIME_DAYS,
        });
      })
      .catch(err => console.error('Failed to fetch lead times:', err));
  }, []);

  const today = new Date().toISOString().split('T')[0];

  // Customer duplicate check state
  const [customerCheckLoading, setCustomerCheckLoading] = useState(false);
  const [customerDuplicateWarning, setCustomerDuplicateWarning] = useState<string | null>(null);

  // Store dealer address for auto-populate
  const [dealerAddress, setDealerAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
  });

  const [formData, setFormData] = useState<OrderFormData>({
    dealerCompany: '',
    contactName: '',
    email: '',
    phone: '',
    customerName: '',
    customerAddress: '',
    customerCity: '',
    customerState: '',
    customerZip: '',
    customerPhone: '',
    customerEmail: '',
    customerLeadId: '',
    orderDate: today,
    neededByDate: '',
    poNumber: '',
    sidemark: '',
    orderedBy: '',
    screenLocation: '',
    isRush: false,
    shippingMethod: 'ship',
    shippingAddressSource: 'custom',
    shipStreet: '',
    shipCity: '',
    shipState: '',
    shipZip: '',
    liftgateDelivery: false,
    residentialDelivery: false,
    insideDelivery: false,
    sameInstallationForAll: true,
    sameSystemType: true,
    sameInstallType: true,
    sameTrackMount: true,
    sameUchannel: true,
    sameScrewCovers: true,
    sameBrushSize: true,
    globalSystemType: 'box',
    globalInstallationType: 'wall',
    globalTrackMount: 'face',
    globalUChannel: 'none',
    applyUChannelDeduction: false,
    globalScrewCovers: true,
    motorType: 'Keder Screens',
    systemColor: 'black',
    customRalNumber: '',
    remoteType: 'none',
    remoteQty: 0,
    wallSwitchType: 'none',
    wallSwitchQty: 0,
    sameFabricForAll: true,
    globalFabricType: 'omega_pro_3',
    globalFabricColor: 'Black',
    includeWindowPanels: false,
    units: [],
    accessories: '',
    comments: '',
    customerSellPrice: '',
    authorizedBy: '',
  });

  // Reactive tax preview based on calculated pricing + ship-to.
  // Lives here (not next to calculatedPricing state) because it depends on
  // formData.shipState which is declared above.
  useEffect(() => {
    if (!calculatedPricing) { setTaxPreview(0); return; }
    // Taxable base = everything pre-tax. TX taxes freight + packaging when
    // bundled with taxable goods, so they're included.
    const taxableBase = (calculatedPricing.subtotal || 0)
      - (calculatedPricing.openRollDiscount || 0)
      + (calculatedPricing.packaging || 0)
      + (calculatedPricing.freight || 0)
      + (calculatedPricing.rushSurcharge || 0);
    if (taxableBase <= 0) { setTaxPreview(0); return; }
    const isWillCall = !!calculatedPricing.freightInfo?.willCall;
    const params = new URLSearchParams({
      taxableAmount: String(taxableBase),
      willCall: isWillCall ? 'true' : 'false',
    });
    if (!isWillCall && formData.shipState) params.append('shipToState', formData.shipState);
    const token = localStorage.getItem('auth_token') || localStorage.getItem('admin_auth_token');
    fetch(`${import.meta.env.VITE_API_URL || '/api'}/tax/preview?${params}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : { tax: 0, ratePercent: 0 })
      .then(d => { setTaxPreview(parseFloat(d.tax) || 0); setTaxRatePct(parseFloat(d.ratePercent) || 0); })
      .catch(() => { setTaxPreview(0); setTaxRatePct(0); });
  }, [calculatedPricing, formData.shipState]);

  // Calculate earliest available date based on lead time for selected fabric
  // Use the longest lead time across all units (or first unit if same fabric for all)
  const currentLeadTimeDays = useMemo(() => {
    if (formData.units.length === 0) {
      // No units yet, use global fabric type or default
      const fabricOption = FABRIC_TYPES.flatMap(g => g.options).find(f => f.value === formData.globalFabricType);
      const pricingCategory = fabricOption?.pricingCategory || 'solar94';
      return getLeadTimeForFabric(pricingCategory, leadTimes);
    }

    // Find the longest lead time across all units
    let maxLeadTime = 0;
    for (const unit of formData.units) {
      const fabricOption = FABRIC_TYPES.flatMap(g => g.options).find(f => f.value === unit.fabricType);
      const pricingCategory = fabricOption?.pricingCategory || 'solar94';
      const unitLeadTime = getLeadTimeForFabric(pricingCategory, leadTimes);
      if (unitLeadTime > maxLeadTime) {
        maxLeadTime = unitLeadTime;
      }
    }
    return maxLeadTime || DEFAULT_LEAD_TIME_DAYS;
  }, [formData.units, formData.globalFabricType, leadTimes]);

  const earliestAvailableDate = useMemo(() => {
    return addBusinessDays(new Date(), currentLeadTimeDays);
  }, [currentLeadTimeDays]);

  const earliestDateString = formatDateForInput(earliestAvailableDate);
  const earliestDateDisplay = formatDateForDisplay(earliestAvailableDate);

  // Check if any units have dimension errors (exceed max allowed)
  const hasAnyDimensionErrors = useMemo(() => {
    return formData.units.some(unit => {
      const screenWidth = Math.max(
        parseFloat(unit.widthTop) || 0,
        parseFloat(unit.widthMiddle) || 0,
        parseFloat(unit.widthBottom) || 0
      );
      const screenHeight = Math.max(
        parseFloat(unit.heightLeft) || 0,
        parseFloat(unit.heightCenter) || 0,
        parseFloat(unit.heightRight) || 0
      );
      return screenWidth > MAX_SCREEN_WIDTH || screenHeight > MAX_SCREEN_HEIGHT;
    });
  }, [formData.units]);

  // Required field CSS class: red when empty, green when filled, error overrides
  const reqClass = (value: string, errorKey?: string) => {
    if (errorKey && (fieldErrors as any)[errorKey]) return 'req-error';
    return value.trim() ? 'req-filled' : 'req-empty';
  };

  // Phone OR Email — green if either is filled
  const reqClassPhoneEmail = (errorKey?: string) => {
    if (errorKey && (fieldErrors as any)[errorKey]) return 'req-error';
    return (formData.customerPhone.trim() || formData.customerEmail.trim()) ? 'req-filled' : 'req-empty';
  };

  // Screens under 36" only fit the Alpha motor (the roller tube is too short
  // for the Keder / Somfy motor head). The form carries ONE motor package for
  // the whole order, so a single narrow unit locks the order to Alpha.
  // System width = the SMALLEST measurement (the tube has to fit the narrowest
  // point) — same basis the box-width calc uses.
  const alphaOnlyUnits = useMemo<number[]>(() => {
    const idx: number[] = [];
    formData.units.forEach((u, i) => {
      const widths = [
        parseFloat(u.widthTop) || 0,
        parseFloat(u.widthMiddle) || 0,
        parseFloat(u.widthBottom) || 0,
      ].filter(w => w > 0);
      if (widths.length && Math.min(...widths) < ALPHA_ONLY_MAX_WIDTH) idx.push(i + 1);
    });
    return idx;
  }, [formData.units]);
  const isAlphaOnlyOrder = alphaOnlyUnits.length > 0;

  // Entering a sub-36" width flips the order to Alpha automatically (Keder /
  // Somfy are disabled below). Remote + wall-switch pickers reset because their
  // options are package-specific; pricing clears so the +$125/unit Alpha
  // upgrade is picked up on the next calculate.
  useEffect(() => {
    if (!isAlphaOnlyOrder) return;
    if (formData.motorType !== 'Keder Screens' && formData.motorType !== 'Somfy') return;
    setFormData(prev => ({ ...prev, motorType: 'Alpha', remoteType: 'none', wallSwitchType: 'none' }));
    setCalculatedPricing(null);
  }, [isAlphaOnlyOrder, formData.motorType]);

  // Walk the required fields in order and return the first one missing as a
  // human-readable label. Returns null when everything's complete. Drives both
  // the boolean `areRequiredFieldsComplete` and the specific message shown on
  // the disabled submit button — generic "Complete required fields above"
  // was leaving dealers staring at the form with no idea which field was
  // blocking them (especially carrier selection on non-TOLA accounts).
  const firstMissingRequiredField = useMemo<string | null>(() => {
    if (!formData.sidemark.trim()) return 'Sidemark';
    if (!formData.orderedBy.trim()) return 'Ordered By';
    if (!formData.customerName.trim()) return 'Customer Name';
    if (!formData.customerPhone.trim() && !formData.customerEmail.trim()) {
      return 'Customer Phone or Email';
    }
    if (!formData.customerState.trim()) return 'Customer State';
    if (!formData.customerZip.trim()) return 'Customer ZIP';
    if (formData.units.length === 0) return 'At least one screen unit';
    // Minimum screen width is 30" (matches the Min Width manufacturing rule).
    for (let i = 0; i < formData.units.length; i++) {
      const u = formData.units[i];
      const w = Math.max(parseFloat(u.widthTop) || 0, parseFloat(u.widthMiddle) || 0, parseFloat(u.widthBottom) || 0);
      if (w > 0 && w < 30) return `Screen #${i + 1} width must be at least 30"`;
    }
    // Under 36" = Alpha motor only. The selector auto-switches, so this is just
    // a backstop (mirrors orders.service.assertMotorAllowedForWidths).
    if (isAlphaOnlyOrder && (formData.motorType === 'Keder Screens' || formData.motorType === 'Somfy')) {
      return `Alpha motor package (screen #${alphaOnlyUnits[0]} is under ${ALPHA_ONLY_MAX_WIDTH}")`;
    }
    // If SHIPPING is the chosen method, a carrier must be selected — for every
    // dealer, TOLA or not. (TOLA dealers who aren't shipping should pick Will
    // Call.) Mirrors the server-side guard in
    // orders.service.assertCarrierSelectedForShipping — immediate feedback
    // instead of a 400 on submit.
    if (formData.shippingMethod === 'ship') {
      const carrier = (freightData?.selectedCarrierName || '').trim();
      const cost = Number(freightData?.freightTotalAmount) || 0;
      if (!carrier && cost <= 0) return 'Shipping Carrier (select a quote) — or switch to Will Call';
    }
    return null;
  }, [
    formData.sidemark, formData.orderedBy, formData.customerName,
    formData.customerPhone, formData.customerEmail,
    formData.customerState, formData.customerZip, formData.units,
    formData.shippingMethod, freightData?.selectedCarrierName, freightData?.freightTotalAmount,
    formData.motorType, isAlphaOnlyOrder, alphaOnlyUnits,
  ]);

  const areRequiredFieldsComplete = firstMissingRequiredField === null;

  // Check if authorization section is complete
  const isAuthorizationComplete = useMemo(() => {
    const hasAuthorizedBy = formData.authorizedBy.trim().length > 0;
    const hasValidDate = formData.neededByDate && formData.neededByDate >= earliestDateString;
    const hasAcceptedTerms = orderTermsAccepted;
    return hasAuthorizedBy && hasValidDate && hasAcceptedTerms;
  }, [formData.authorizedBy, formData.neededByDate, earliestDateString, orderTermsAccepted]);

  // Form is ready for submit when both required fields AND authorization are complete
  const isFormReadyForSubmit = areRequiredFieldsComplete && isAuthorizationComplete;

  // Add first unit automatically on mount (matching reference form behavior)
  useEffect(() => {
    const firstUnit = createEmptyUnit();
    setFormData(prev => ({
      ...prev,
      units: prev.units.length === 0 ? [firstUnit] : prev.units,
    }));
    setExpandedUnits(prev => new Set(prev).add(firstUnit.id));
  }, []);

  // Load dealer info from auth
  useEffect(() => {
    if (user) {
      const u = user as any;
      const firstName = u.firstName || u.first_name || '';
      const lastName = u.lastName || u.last_name || '';
      const fullName = firstName && lastName ? `${firstName} ${lastName}` : user.name || '';

      // Get company name (try all possible field names)
      const company = u.companyName || u.company || u.dealerName || '';

      // Get phone (coalesced field or individual fields)
      const phone = u.phone || u.phoneWork || u.phone_work || u.phoneMobile || u.phone_mobile || u.phoneHome || u.phone_home || '';

      // Get address fields (try camelCase and snake_case). Prefer the
      // dealer's shipping address on file when one exists (from JotForm
      // "Shipping same as Billing? = No"); fall back to billing.
      const shipObj: any = u.shippingAddress || u.shipping_address || null;
      const shipStreet = shipObj?.street || '';
      const shipCity = shipObj?.city || '';
      const shipState = shipObj?.state || '';
      const shipZip = shipObj?.zip || '';

      const street = shipStreet || u.addressStreet || u.address_street || u.address?.street || '';
      const city   = shipCity   || u.addressCity   || u.address_city   || u.address?.city   || '';
      const state  = shipState  || u.addressState  || u.address_state  || u.address?.state  || '';
      const zip    = shipZip    || u.addressZip    || u.address_zip    || u.address?.zip    || '';

      // Store dealer address for auto-populate (already resolved to
      // shipping-preferred if a distinct shipping address is on file).
      setDealerAddress({ street, city, state, zip });

      setFormData(prev => {
        // When resuming a draft via EditOrderPage, the saved formData has
        // already populated shippingAddressSource (e.g. 'willcall'). The
        // dealer-auth context resolves asynchronously and this effect can
        // fire AFTER the edit-data effect, silently overwriting the saved
        // choice back to 'dealer'. Preserve the saved value once it's set
        // to anything other than the form's initial 'custom' default.
        const preserveShipping =
          prev.shippingAddressSource && prev.shippingAddressSource !== 'custom';
        return {
          ...prev,
          dealerCompany: company,
          contactName: fullName,
          email: user.email || '',
          phone: phone,
          orderedBy: fullName,
          shippingAddressSource: preserveShipping
            ? prev.shippingAddressSource
            : (street ? 'dealer' : 'custom'),
          shipStreet: preserveShipping ? prev.shipStreet : street,
          shipCity:   preserveShipping ? prev.shipCity   : city,
          shipState:  preserveShipping ? prev.shipState  : state,
          shipZip:    preserveShipping ? prev.shipZip    : zip,
        };
      });
    }
  }, [user]);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [expiredQuoteNotice, setExpiredQuoteNotice] = useState<string | null>(null);
  const [editOrderId, setEditOrderId] = useState<string | null>(null);
  const [editOrderNumber, setEditOrderNumber] = useState<string | null>(null);
  const [editOriginalStatus, setEditOriginalStatus] = useState<string | null>(null);

  // Load order data for editing from sessionStorage
  useEffect(() => {
    const editData = sessionStorage.getItem('editOrderData');
    if (editData) {
      try {
        const data = JSON.parse(editData);
        console.log('[NewOrderPage] Loaded edit data from sessionStorage:', data);
        sessionStorage.removeItem('editOrderData'); // Clear after loading

        setIsEditMode(true);
        setEditOrderId(data.orderId);
        setEditOrderNumber(data.orderNumber);
        setEditOriginalStatus(data.order?.status || null);

        // Restore expert mode from edit data
        if (data.expertMode !== undefined) {
          setIsExpertMode(data.expertMode);
        }

        // Load form data if available
        const savedForm = data.formData;
        console.log('[NewOrderPage] Saved form data:', savedForm);
        console.log('[NewOrderPage] Units from saved form:', savedForm?.units);
        console.log('[NewOrderPage] Units count:', savedForm?.units?.length || 0);

        if (savedForm && Object.keys(savedForm).length > 0) {
          // Helper for measurements - handle 0 as empty (0 means "not measured")
          const normalizeMeasurement = (val: any) => {
            if (val === 0 || val === '0' || val === null || val === undefined || val === '') return '';
            return String(val);
          };

          // If units exist, make sure they have all required fields
          const loadedUnits = (savedForm.units || []).map((unit: any) => ({
            // Generate ID if missing
            id: unit.id || Math.random().toString(36).substr(2, 9),
            // Measurements - normalize 0 to empty string
            widthTop: normalizeMeasurement(unit.widthTop),
            widthMiddle: normalizeMeasurement(unit.widthMiddle),
            widthBottom: normalizeMeasurement(unit.widthBottom),
            heightLeft: normalizeMeasurement(unit.heightLeft),
            heightCenter: normalizeMeasurement(unit.heightCenter),
            heightRight: normalizeMeasurement(unit.heightRight),
            // Screen location (per-unit)
            screenLocation: unit.screenLocation || '',
            // Installation options
            installationType: unit.installationType || 'wall',
            trackMount: unit.trackMount || 'face',
            uChannel: unit.uChannel || 'none',
            applyUChannelDeduction: unit.applyUChannelDeduction || false,
            boxWidthAdjustmentType: unit.boxWidthAdjustmentType
              || ((unit.boxWidthAdjustment == null || unit.boxWidthAdjustment === 0) ? 'same'
                : unit.boxWidthAdjustment < 0 ? 'smaller' : 'larger'),
            boxWidthAdjustmentValue: unit.boxWidthAdjustmentValue
              || ((unit.boxWidthAdjustment == null || unit.boxWidthAdjustment === 0) ? ''
                : Math.abs(unit.boxWidthAdjustment).toString()),
            applyBoxDeduction: unit.applyBoxDeduction ?? true,
            // System options
            systemColor: unit.systemColor || savedForm.systemColor || 'black',
            motorSide: unit.motorSide || 'right',
            brushSize: unit.brushSize || '1inch',
            biasedCut: unit.biasedCut || false,
            biasAmount: String(unit.biasAmount || ''),
            biasSide: unit.biasSide || 'left',
            // Per-component colors
            useCustomColors: !!(unit.componentColors && Object.keys(unit.componentColors).length > 0),
            componentColors: {
              housingColor: unit.componentColors?.housingColor || '',
              housingRalNumber: unit.componentColors?.housingRalNumber || '',
              leftTrackColor: unit.componentColors?.leftTrackColor || '',
              leftTrackRalNumber: unit.componentColors?.leftTrackRalNumber || '',
              rightTrackColor: unit.componentColors?.rightTrackColor || '',
              rightTrackRalNumber: unit.componentColors?.rightTrackRalNumber || '',
              weightBarColor: unit.componentColors?.weightBarColor || '',
              weightBarRalNumber: unit.componentColors?.weightBarRalNumber || '',
              leftUChannelColor: unit.componentColors?.leftUChannelColor || '',
              leftUChannelRalNumber: unit.componentColors?.leftUChannelRalNumber || '',
              rightUChannelColor: unit.componentColors?.rightUChannelColor || '',
              rightUChannelRalNumber: unit.componentColors?.rightUChannelRalNumber || '',
            },
            // Fabric
            fabricType: unit.fabricType || savedForm.globalFabricType || 'omega_pro_3',
            fabricColor: unit.fabricColor || savedForm.globalFabricColor || 'Black',
            // Window panel
            includeWindowPanel: unit.includeWindowPanel || false,
            windowConfig: unit.windowConfig || createEmptyWindowConfig(),
          }));

          console.log('[NewOrderPage] Processed units for form:', loadedUnits);

          setFormData(prev => ({
            ...prev,
            ...savedForm,
            // Use processed units
            units: loadedUnits.length > 0 ? loadedUnits : prev.units,
          }));

          // Expand all loaded units
          if (loadedUnits.length > 0) {
            setExpandedUnits(new Set(loadedUnits.map((u: any) => u.id)));
            console.log('[NewOrderPage] Expanded units:', loadedUnits.map((u: any) => u.id));
          }

          // Load freight data if available
          if (data.freightData) {
            console.log('[NewOrderPage] Loading freight data:', data.freightData);
            setFreightData(data.freightData);
          }

          // Restore terms acceptance — dealer already accepted on original submission
          if (savedForm.authorizedBy || data.order?.status === 'needs_correction') {
            setOrderTermsAccepted(true);
          }

          // Fix stale neededByDate — if saved date is in the past, bump to earliest valid date
          if (savedForm.neededByDate && savedForm.neededByDate < earliestDateString) {
            setFormData(prev => ({ ...prev, neededByDate: earliestDateString }));
          }

          const orderLabel = data.orderNumber || data.orderId;
          setSuccess(data.order?.status === 'needs_correction'
            ? `Correcting order ${orderLabel} — make your changes and save to resubmit`
            : `Editing order ${orderLabel}`);
          // Auto-trigger price calculation so Submit for Invoicing button is available
          setPendingAutoCalculate(true);
        }
      } catch (err) {
        console.error('[NewOrderPage] Failed to load edit data:', err);
      }
    }
  }, []);

  // Auto-calculate pricing when loading a draft for editing
  useEffect(() => {
    if (pendingAutoCalculate && isEditMode && formData.units.some(u => u.widthTop || u.widthBottom)) {
      setPendingAutoCalculate(false);
      // Small delay to let form state settle
      const timer = setTimeout(() => {
        handleCalculatePricing();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [pendingAutoCalculate, isEditMode, formData.units]);

  // Auto-recalc when RUSH flag flips. Only re-fires if pricing has already
  // been calculated at least once — avoids running on form mount.
  useEffect(() => {
    if (!calculatedPricing) return;
    const timer = setTimeout(() => { handleCalculatePricing(); }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.isRush]);

  // Default the new-order shipping dropdown. Precedence:
  //   1. Persisted user choice (localStorage 'lastShippingSource')
  //   2. Will Call — for TOLA-eligible dealers (TX/OK/LA/AR, non auto-consolidate)
  //   3. Whatever the user-effect set (dealer if street known, else custom)
  // Only fires when:
  //  - not in edit mode (existing orders keep their persisted choice)
  //  - the dealer hasn't already moved the dropdown off the auto defaults
  //    set by the user-effect ('dealer' or 'custom')
  // willCallAllowed is also a dep so the effect re-evaluates once the API
  // resolves; 'willcall' is only honored if the dealer is currently eligible.
  useEffect(() => {
    if (isEditMode) return;
    const stored = (() => {
      try { return localStorage.getItem('lastShippingSource'); } catch { return null; }
    })();
    const validPicks = new Set(['dealer', 'customer', 'custom', 'willcall']);
    const preferred = stored && validPicks.has(stored)
      ? (stored === 'willcall' && !willCallAllowed ? null : stored)
      : (willCallAllowed ? 'willcall' : null);
    if (!preferred) return;
    setFormData(prev => {
      if (prev.shippingAddressSource !== 'dealer' && prev.shippingAddressSource !== 'custom') {
        return prev;
      }
      const next: any = { ...prev, shippingAddressSource: preferred };
      if (preferred === 'willcall') next.shippingMethod = 'willcall';
      if (preferred === 'dealer') {
        next.shippingMethod = 'ship';
        next.shipStreet = dealerAddress.street;
        next.shipCity = dealerAddress.city;
        next.shipState = dealerAddress.state;
        next.shipZip = dealerAddress.zip;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [willCallAllowed, isEditMode, dealerAddress.street]);

  // ?action=pay → dealer arrived from "Submit & Pay" on a draft. Scroll the
  // form straight to the Authorization section so they don't have to hunt for
  // the submit button buried at the bottom of the order config.
  useEffect(() => {
    if (!isEditMode) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') !== 'pay') return;
    // Wait for the draft data to populate + the page to render
    const timer = setTimeout(() => {
      const el = document.getElementById('authorization-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 800);
    return () => clearTimeout(timer);
  }, [isEditMode]);

  // Load quote from Quick Estimate if pushed
  useEffect(() => {
    const quoteData = sessionStorage.getItem('quoteToOrder');
    if (quoteData) {
      try {
        const savedQuote = JSON.parse(quoteData);
        sessionStorage.removeItem('quoteToOrder'); // Clear after loading

        // Quotes are valid 30 days. If this one is past its window, warn that
        // the order prices at current rates — the quoted total won't be honored.
        if (savedQuote.createdAt) {
          const validUntil = savedQuote.validUntil
            ? new Date(savedQuote.validUntil)
            : (() => { const d = new Date(savedQuote.createdAt); d.setDate(d.getDate() + 30); return d; })();
          const expired = savedQuote.isExpired ?? (Date.now() > validUntil.getTime());
          if (expired) {
            setExpiredQuoteNotice(
              `This quote expired ${validUntil.toLocaleDateString()}. Quotes are valid for 30 days — ` +
              `this order is priced at current rates, which may differ from the original quote.`
            );
          }
        }

        // Map quote screens to order units
        if (savedQuote.configuration?.screens?.length > 0) {
          const units = savedQuote.configuration.screens.map((screen: any, index: number) => ({
            ...createEmptyUnit(),
            id: Math.random().toString(36).substr(2, 9),
            widthTop: String(screen.width || ''),
            widthMiddle: String(screen.width || ''),
            widthBottom: String(screen.width || ''),
            heightLeft: String(screen.height || ''),
            heightCenter: String(screen.height || ''),
            heightRight: String(screen.height || ''),
            uChannel: screen.uChannel ? 'both_sides' : 'none',
          }));

          // Map product type to fabric type
          const productType = savedQuote.configuration.productType;
          let fabricType = 'omega_pro_3'; // default solar94
          if (productType === 'solar99') fabricType = 'ultra_99';
          else if (productType === 'cvw') fabricType = 'cvw_20';
          else if (productType === 'insect') fabricType = 'insect';

          // Map motor package to order form motor type
          const cfg = savedQuote.configuration;
          const motorMap: Record<string, string> = {
            alpha: 'Alpha', somfy: 'Somfy', keder: 'Keder Screens', none: 'No Motor'
          };
          const motorType = motorMap[cfg.motorPackage as string] || 'Keder Screens';

          setFormData(prev => ({
            ...prev,
            units,
            globalFabricType: fabricType,
            sameFabricForAll: true,
            motorType: motorType as any,
            remoteType: cfg.remoteType || 'none',
            remoteQty: cfg.remoteQty ?? (cfg.extraRemote ? 2 : 1),
            wallSwitchType: cfg.wallSwitchType || 'none',
            wallSwitchQty: cfg.wallSwitchQty || 0,
          }));

          // Expand all loaded units
          setExpandedUnits(new Set(units.map((u: any) => u.id)));

          setSuccess(`Loaded ${units.length} screen(s) from Quick Estimate`);
        }
      } catch (err) {
        console.error('Failed to load quote:', err);
      }
    }
  }, []);

  // Load lead data if converting from Leads page
  useEffect(() => {
    const leadData = sessionStorage.getItem('leadConvertData');
    if (leadData) {
      try {
        const lead = JSON.parse(leadData);
        sessionStorage.removeItem('leadConvertData');

        setFormData(prev => ({
          ...prev,
          customerName: lead.customerName || prev.customerName,
          customerEmail: lead.customerEmail || prev.customerEmail,
          customerPhone: lead.customerPhone || prev.customerPhone,
          customerAddress: lead.customerAddress || prev.customerAddress,
          customerCity: lead.customerCity || prev.customerCity,
          customerState: lead.customerState || prev.customerState,
          customerZip: lead.customerZip || prev.customerZip,
        }));

        setSuccess(`Customer info loaded from lead: ${lead.customerName || 'Unknown'}`);
      } catch (err) {
        console.error('Failed to load lead data:', err);
      }
    }
  }, []);

  // Load data from Price Builder if pushed
  useEffect(() => {
    const pbData = sessionStorage.getItem('priceBuilderToOrder');
    if (pbData) {
      try {
        const data = JSON.parse(pbData);
        sessionStorage.removeItem('priceBuilderToOrder');

        // Pre-fill customer info
        if (data.customerInfo) {
          setFormData(prev => ({
            ...prev,
            customerName: data.customerInfo.name || prev.customerName,
            customerEmail: data.customerInfo.email || prev.customerEmail,
            customerPhone: data.customerInfo.phone || prev.customerPhone,
            customerAddress: data.customerInfo.address || prev.customerAddress,
            customerCity: data.customerInfo.city || prev.customerCity,
            customerState: data.customerInfo.state || prev.customerState,
            customerZip: data.customerInfo.zip || prev.customerZip,
          }));
        }

        // Pre-fill screens
        if (data.screens?.length > 0) {
          const units = data.screens.map((screen: any) => ({
            ...createEmptyUnit(),
            id: Math.random().toString(36).substr(2, 9),
            widthTop: String(screen.width || ''),
            widthMiddle: String(screen.width || ''),
            widthBottom: String(screen.width || ''),
            heightLeft: String(screen.height || ''),
            heightCenter: String(screen.height || ''),
            heightRight: String(screen.height || ''),
            uChannel: screen.uChannel ? 'both_sides' : 'none',
          }));

          let fabricType = 'omega_pro_3';
          if (data.productType === 'solar99') fabricType = 'ultra_99';
          else if (data.productType === 'cvw') fabricType = 'cvw_20';
          else if (data.productType === 'insect') fabricType = 'insect';

          // Map motor package to order form motor type
          const pbMotorMap: Record<string, string> = {
            alpha: 'Alpha', somfy: 'Somfy', keder: 'Keder Screens', none: 'No Motor'
          };
          const pbMotorType = pbMotorMap[data.motorPackage as string] || 'Keder Screens';

          setFormData(prev => ({
            ...prev,
            units,
            globalFabricType: fabricType,
            sameFabricForAll: true,
            motorType: pbMotorType as any,
            remoteType: data.remoteType || 'none',
            remoteQty: data.remoteQty ?? (data.extraRemote ? 2 : 1),
            wallSwitchType: data.wallSwitchType || 'none',
            wallSwitchQty: data.wallSwitchQty || 0,
          }));

          setExpandedUnits(new Set(units.map((u: any) => u.id)));
        }

        const screenCount = data.screens?.filter((s: any) => s.width > 0 && s.height > 0).length || 0;
        const parts = [];
        if (data.customerInfo?.name) parts.push('customer info');
        if (screenCount > 0) parts.push(`${screenCount} screen(s)`);
        if (parts.length > 0) setSuccess(`Loaded ${parts.join(' + ')} from Price Builder`);

        // Auto-calculate customerSellPrice from pricing breakdown + commission settings
        if (data.pricing) {
          dealerCommissionSettingsApi.get().then(settings => {
            const p = data.pricing;
            let commissionable = p.kederScreensOnly || 0;

            // Add custom items if enabled
            if (settings.commission_include_custom_items) {
              commissionable += p.customItemsTotal || 0;
            }

            // Apply proportional discount
            const subtotalBeforeDiscount = (p.kederScreensOnly || 0) + (p.customItemsTotal || 0);
            if (subtotalBeforeDiscount > 0 && (p.totalDiscountAmount || 0) > 0) {
              const ratio = commissionable / subtotalBeforeDiscount;
              commissionable -= (p.totalDiscountAmount || 0) * ratio;
            }

            // Add installation if enabled (not subject to discount)
            if (settings.commission_include_installation) {
              commissionable += p.installTotal || 0;
            }

            // Add shipping if enabled (not subject to discount)
            if (settings.commission_include_shipping) {
              commissionable += p.shippingCost || 0;
            }

            commissionable = Math.round(commissionable * 100) / 100;
            if (commissionable > 0) {
              setFormData(prev => ({ ...prev, customerSellPrice: String(commissionable) }));
            }
          }).catch(() => {
            // Non-blocking — if commission settings unavailable, skip auto-fill
          });
        }
      } catch (err) {
        console.error('Failed to load Price Builder data:', err);
      }
    }
  }, []);

  // Load accessories returned from PartsPage + restore form state
  const [linkedAccessories, setLinkedAccessories] = useState<any[]>([]);

  useEffect(() => {
    // Restore form state saved before navigating to Parts page
    const pendingData = localStorage.getItem('pendingOrderAccessories');
    if (pendingData) {
      try {
        const pending = JSON.parse(pendingData);
        if (pending.formData && pending.timestamp && Date.now() - pending.timestamp < 2 * 60 * 60 * 1000) {
          setFormData(prev => ({ ...prev, ...pending.formData }));
        }
      } catch (err) {
        console.error('Failed to restore form state:', err);
      }
    }

    const accessoriesData = localStorage.getItem('orderAccessoriesCart');
    if (accessoriesData) {
      try {
        const data = JSON.parse(accessoriesData);
        // Check if data is fresh (within 1 hour)
        if (data.timestamp && Date.now() - data.timestamp < 60 * 60 * 1000) {
          setLinkedAccessories(data.items || []);
          setSuccess(`Added ${data.items?.length || 0} accessory item(s) to your order`);
        }
      } catch (err) {
        console.error('Failed to load accessories:', err);
      }
    }

    // Clear after loading both
    if (pendingData || accessoriesData) {
      localStorage.removeItem('orderAccessoriesCart');
      localStorage.removeItem('pendingOrderAccessories');
    }
  }, []);

  // Get available fabric colors for current fabric type
  const getAvailableColors = (fabricType: string) => {
    return FABRIC_COLORS[fabricType] || [];
  };

  const updateFormField = (field: keyof OrderFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setCalculatedPricing(null);
    // Clear field error when user types
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle shipping address source change with auto-populate
  const handleShippingSourceChange = (source: 'dealer' | 'customer' | 'custom' | 'willcall') => {
    setCalculatedPricing(null);

    // Persist the user's choice so future new orders default to it (per-browser,
    // same pattern as the dark/light theme toggle). Only written from this
    // handler — automatic defaults (TOLA willcall) never overwrite the user's
    // last explicit pick.
    try { localStorage.setItem('lastShippingSource', source); } catch {}

    if (source === 'dealer') {
      // Auto-populate from dealer address
      setFormData(prev => ({
        ...prev,
        shippingAddressSource: 'dealer',
        shippingMethod: 'ship',
        shipStreet: dealerAddress.street,
        shipCity: dealerAddress.city,
        shipState: dealerAddress.state,
        shipZip: dealerAddress.zip,
      }));
    } else if (source === 'customer') {
      // Auto-populate from customer info
      setFormData(prev => ({
        ...prev,
        shippingAddressSource: 'customer',
        shippingMethod: 'ship',
        shipStreet: prev.customerAddress,
        shipCity: prev.customerCity,
        shipState: prev.customerState,
        shipZip: prev.customerZip,
      }));
    } else if (source === 'willcall') {
      // Will Call / Pickup
      setFormData(prev => ({
        ...prev,
        shippingAddressSource: 'willcall',
        shippingMethod: 'willcall',
        shipStreet: '',
        shipCity: '',
        shipState: '',
        shipZip: '',
      }));
    } else {
      // Custom - clear fields for manual entry
      setFormData(prev => ({
        ...prev,
        shippingAddressSource: 'custom',
        shippingMethod: 'ship',
        shipStreet: '',
        shipCity: '',
        shipState: '',
        shipZip: '',
      }));
    }
  };

  // Validate required fields
  const validateForm = (requireSignature = true): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.sidemark.trim()) {
      errors.sidemark = 'PO / Sidemark / Job Name is required';
    }
    if (!formData.orderedBy.trim()) {
      errors.orderedBy = 'Ordered By is required';
    }
    if (!formData.customerName.trim()) {
      errors.customerName = 'Customer Name is required';
    }
    // Phone OR Email required (at least one)
    if (!formData.customerPhone.trim() && !formData.customerEmail.trim()) {
      errors.customerPhone = 'Phone or Email is required';
      errors.customerEmail = 'Phone or Email is required';
    }
    // State and ZIP required
    if (!formData.customerState.trim()) {
      errors.customerState = 'State is required';
    }
    if (!formData.customerZip.trim()) {
      errors.customerZip = 'ZIP Code is required';
    }
    if (formData.shippingMethod === 'ship') {
      if (!formData.shipStreet.trim()) errors.shipStreet = 'Street address is required';
      if (!formData.shipCity.trim()) errors.shipCity = 'City is required';
      if (!formData.shipState.trim()) errors.shipState = 'State is required';
      if (!formData.shipZip.trim()) errors.shipZip = 'ZIP code is required';
    }
    if (formData.units.length === 0) {
      errors.units = 'At least one screen unit is required';
    }

    // Authorization section validation (only required for full submit, not drafts)
    if (requireSignature) {
      if (!formData.authorizedBy.trim()) {
        errors.authorization = 'Please enter your full name';
      }
      if (!formData.neededByDate) {
        errors.neededByDate = 'Please select a date needed by';
      } else if (formData.neededByDate < earliestDateString) {
        errors.neededByDate = `Selected date is before production lead time. Earliest: ${earliestDateDisplay}`;
      }
      if (!orderTermsAccepted) {
        errors.termsAccepted = 'Please agree to the terms';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Save order as draft (without payment or signature)
  const handleSaveDraft = async () => {
    // Validate but don't require signature for drafts
    if (!validateForm(false)) {
      setError('Please fill in all required fields');
      return;
    }

    setSavingDraft(true);
    setError(null);
    setSuccess(null);

    try {
      // Build order items from units
      const items = formData.units
        .filter(u => (u.widthTop || u.widthMiddle || u.widthBottom) && (u.heightLeft || u.heightCenter || u.heightRight))
        .map(unit => {
          const fabricType = formData.sameFabricForAll ? formData.globalFabricType : unit.fabricType;
          const fabric = FABRIC_TYPES.flatMap(g => g.options).find(f => f.value === fabricType);

          // Determine screw covers based on track mount and propagation settings
          const trackMount = formData.sameTrackMount ? formData.globalTrackMount : unit.trackMount;
          const uChannel = formData.sameUchannel ? (formData.units[0]?.uChannel || formData.globalUChannel) : unit.uChannel;
          const screwCovers = trackMountIncludesFace(trackMount)
            ? (formData.sameScrewCovers ? formData.globalScrewCovers : unit.screwCovers)
            : false;
          const screwCoverQty = screwCovers ? getScrewCoverQty(trackMount, uChannel) : 0;

          return {
            productType: fabric?.pricingCategory || 'solar94',
            fabricType: fabricType,
            fabricColor: formData.sameFabricForAll ? formData.globalFabricColor : unit.fabricColor,
            widthTop: parseFloat(unit.widthTop) || undefined,
            widthMiddle: parseFloat(unit.widthMiddle) || undefined,
            widthBottom: parseFloat(unit.widthBottom) || undefined,
            heightLeft: parseFloat(unit.heightLeft) || undefined,
            heightCenter: parseFloat(unit.heightCenter) || undefined,
            heightRight: parseFloat(unit.heightRight) || undefined,
            motorSide: unit.motorSide,
            uChannel: uChannel,
            applyUChannelDeduction: unit.applyUChannelDeduction,
            boxWidthAdjustment: unit.boxWidthAdjustmentType === 'same' ? null
              : unit.boxWidthAdjustmentType === 'smaller' ? -(parseFloat(unit.boxWidthAdjustmentValue) || 0)
              : (parseFloat(unit.boxWidthAdjustmentValue) || 0),
            applyBoxDeduction: unit.applyBoxDeduction,
            expertMode: isExpertMode,
            biasedCut: unit.biasedCut,
            biasedCutInches: unit.biasedCut ? parseFloat(unit.biasAmount) || undefined : undefined,
            biasedCutSide: unit.biasedCut ? unit.biasSide : undefined,
            systemType: formData.sameSystemType ? formData.globalSystemType : unit.systemType,
            skipWeightBarKit: !!unit.skipWeightBarKit,
            installationType: formData.sameInstallType ? formData.globalInstallationType : unit.installationType,
            trackMount: trackMount,
            systemColor: unit.systemColor,
            ralNumber: unit.systemColor === 'custom' ? unit.ralNumber : undefined,
            componentColors: unit.useCustomColors ? (() => {
              const cc: any = {};
              const fc = unit.systemColor;
              if (unit.componentColors.housingColor && unit.componentColors.housingColor !== fc) {
                cc.housingColor = unit.componentColors.housingColor;
                if (unit.componentColors.housingColor === 'custom' && unit.componentColors.housingRalNumber) cc.housingRalNumber = unit.componentColors.housingRalNumber;
              }
              if (unit.componentColors.leftTrackColor && unit.componentColors.leftTrackColor !== fc) {
                cc.leftTrackColor = unit.componentColors.leftTrackColor;
                if (unit.componentColors.leftTrackColor === 'custom' && unit.componentColors.leftTrackRalNumber) cc.leftTrackRalNumber = unit.componentColors.leftTrackRalNumber;
              }
              if (unit.componentColors.rightTrackColor && unit.componentColors.rightTrackColor !== fc) {
                cc.rightTrackColor = unit.componentColors.rightTrackColor;
                if (unit.componentColors.rightTrackColor === 'custom' && unit.componentColors.rightTrackRalNumber) cc.rightTrackRalNumber = unit.componentColors.rightTrackRalNumber;
              }
              if (unit.componentColors.weightBarColor && unit.componentColors.weightBarColor !== fc) {
                cc.weightBarColor = unit.componentColors.weightBarColor;
                if (unit.componentColors.weightBarColor === 'custom' && unit.componentColors.weightBarRalNumber) cc.weightBarRalNumber = unit.componentColors.weightBarRalNumber;
              }
              if (unit.componentColors.leftUChannelColor && unit.componentColors.leftUChannelColor !== fc) {
                cc.leftUChannelColor = unit.componentColors.leftUChannelColor;
                if (unit.componentColors.leftUChannelColor === 'custom' && unit.componentColors.leftUChannelRalNumber) cc.leftUChannelRalNumber = unit.componentColors.leftUChannelRalNumber;
              }
              if (unit.componentColors.rightUChannelColor && unit.componentColors.rightUChannelColor !== fc) {
                cc.rightUChannelColor = unit.componentColors.rightUChannelColor;
                if (unit.componentColors.rightUChannelColor === 'custom' && unit.componentColors.rightUChannelRalNumber) cc.rightUChannelRalNumber = unit.componentColors.rightUChannelRalNumber;
              }
              return Object.keys(cc).length > 0 ? cc : undefined;
            })() : undefined,
            screwCovers: screwCovers,
            screwCoverQty: screwCoverQty,
            brushSize: unit.brushSize,
            // Screen location (per-unit)
            screenLocation: unit.screenLocation || undefined,
            // Window panel — must be included in draft saves too, otherwise every
            // Save Draft strips windows from order_items even though formData.units
            // still has the rich windowConfig. (Confirm Order also sends these.)
            windowPanel: unit.includeWindowPanel,
            windowData: unit.includeWindowPanel ? {
              orientation: unit.windowConfig.orientation,
              windowCount: unit.windowConfig.windowCount,
              dividerType: unit.windowConfig.dividerType,
              dividerSize: unit.windowConfig.dividerSize,
              horizontalDividers: unit.windowConfig.horizontalDividers,
              verticalDividers: unit.windowConfig.verticalDividers,
              windowBottomFromGround: unit.windowConfig.windowBottomFromGround,
              windowTopFromGround: unit.windowConfig.windowTopFromGround,
              borderTop: unit.windowConfig.borderTop,
              borderSide: unit.windowConfig.borderSide,
              borderBottom: unit.windowConfig.borderBottom,
            } : undefined,
          };
        });

      // Create the draft order with all form data
      const orderData = {
        status: 'draft',
        contactId: (user as any)?.contactId || (user as any)?.id,
        dealerId: (user as any)?.dealerId,
        sidemark: formData.sidemark,
        poNumber: formData.poNumber,
        notes: formData.comments,
        neededByDate: formData.neededByDate || undefined,
        isRush: formData.isRush || false,
        shippingMethod: (freightData?.method === 'will_call' || formData.shippingMethod === 'willcall') ? 'will_call' : 'shipping',
        // Freight data from carrier selection
        freightviewShipmentId: freightData?.freightviewShipmentId,
        selectedQuoteId: freightData?.selectedQuoteId,
        selectedCarrierName: freightData?.selectedCarrierName,
        selectedCarrierCode: freightData?.selectedCarrierCode,
        freightQuotes: freightData?.freightQuotes,
        freightTotalAmount: freightData?.freightTotalAmount,
        shipToAddress: formData.shippingMethod === 'willcall' ? undefined : {
          street: formData.shipStreet,
          city: formData.shipCity,
          state: formData.shipState,
          zip: formData.shipZip,
        },
        // Which ship-to was chosen ('dealer' | 'customer' | 'willcall'). Only the
        // resulting address used to be sent, so the factory could not tell a
        // drop-ship to a homeowner from a delivery to the dealer's warehouse.
        shippingAddressSource: formData.shippingAddressSource || undefined,
        dealerInfo: {
          company: formData.dealerCompany,
          contactName: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          orderedBy: formData.orderedBy,
        },
        installationType: formData.globalInstallationType,
        trackMount: formData.globalTrackMount,
        systemColor: formData.systemColor,
        brushSize: formData.units[0]?.brushSize === '2inch' ? '2"' : '1"',
        fabricType: formData.globalFabricType,
        fabricColor: formData.globalFabricColor,
        motorType: formData.motorType.toLowerCase(),
        controllerType: formData.remoteType !== 'none' ? formData.remoteType : undefined,
        controllerQty: formData.remoteQty,
        wallSwitchType: formData.wallSwitchType !== 'none' ? formData.wallSwitchType : undefined,
        wallSwitchQty: formData.wallSwitchQty,
        accessories: formData.accessories || [],
        linkedAccessories: linkedAccessories.length > 0 ? linkedAccessories : undefined,
        // Per-dealer promo code, only send when the dealer typed one AND it
        // passed client-side validation. Server re-validates on submit.
        promoCode: (promoCheck.valid && promoCodeInput.trim()) ? promoCodeInput.trim() : undefined,
        liftgateDelivery: formData.liftgateDelivery,
        residentialDelivery: formData.residentialDelivery,
        insideDelivery: formData.insideDelivery,
        items,
        // Customer lead linking (CRM)
        customerLeadId: formData.customerLeadId || undefined,
        customerLeadName: formData.customerName || undefined,
        customerLeadEmail: formData.customerEmail || undefined,
        customerLeadPhone: formData.customerPhone || undefined,
        customerLeadAddress: formData.customerAddress ?
          `${formData.customerAddress}, ${formData.customerCity}, ${formData.customerState} ${formData.customerZip}` : undefined,
        customerSellPrice: formData.customerSellPrice ? parseFloat(formData.customerSellPrice) : undefined,
        // Dealer opt-in: bundle this new order with these existing orders on payment.
        // Only sent when dealer acknowledged the shipping-delay warning.
        consolidateWithOrderIds:
          bundleWithIds.length > 0 && bundleAcknowledged ? bundleWithIds : undefined,
        // Store full form data for restoration (as object, not string)
        formData: { ...formData },
      };

      console.log('[Save Draft] Order data:', orderData);
      console.log('[Save Draft] Freight data included:', {
        freightviewShipmentId: orderData.freightviewShipmentId,
        selectedQuoteId: orderData.selectedQuoteId,
        selectedCarrierName: orderData.selectedCarrierName,
        freightTotalAmount: orderData.freightTotalAmount,
      });

      let order;
      if (isEditMode && editOrderId) {
        // Update existing order (exclude status — not allowed in PATCH, use /orders/:id/status instead)
        const { status: _status, ...updateData } = orderData;
        order = await ordersApi.update(editOrderId, updateData);
        const isResubmit = editOriginalStatus === 'needs_correction';
        setSuccess(isResubmit ? 'Order corrected and resubmitted for review' : 'Order updated successfully');
      } else {
        // Create new order
        order = await ordersApi.create(orderData);
        // Switch to edit mode so subsequent saves update instead of creating duplicates
        setIsEditMode(true);
        setEditOrderId(order.id);
        setEditOrderNumber(order.orderNumber || order.order_number);
        setSuccess('Order saved as draft');
      }

      const isResubmit = editOriginalStatus === 'needs_correction';
      setTimeout(() => {
        navigate(`/orders/${order.id || editOrderId}`, {
          state: { message: isEditMode
            ? (isResubmit ? 'Order corrected and resubmitted for review.' : 'Order updated successfully.')
            : 'Order saved as draft. You can continue editing and submit payment later.' }
        });
      }, 1000);
    } catch (err: any) {
      console.error('Failed to save draft:', err);
      setError(err.response?.data?.message || 'Failed to save draft. Please try again.');
    } finally {
      setSavingDraft(false);
    }
  };

  const updateUnit = (index: number, field: keyof ScreenUnit, value: any) => {
    setFormData(prev => {
      const newUnits = prev.units.map((unit, i) => {
        // Determine if this unit should be updated
        let shouldUpdate = (i === index);

        // PROPAGATION LOGIC: If editing Unit 1 (index 0) and propagation is enabled for this field type,
        // also update all other units
        if (index === 0 && i !== 0) {
          // System Type propagates when sameSystemType is true
          if (prev.sameSystemType && field === 'systemType') {
            shouldUpdate = true;
          }

          // Installation Type propagates when sameInstallType is true
          if (prev.sameInstallType && field === 'installationType') {
            shouldUpdate = true;
          }

          // Track Mount propagates when sameTrackMount is true
          if (prev.sameTrackMount && field === 'trackMount') {
            shouldUpdate = true;
          }

          // U-Channel propagates when sameUchannel is true
          if (prev.sameUchannel && (field === 'uChannel' || field === 'applyUChannelDeduction')) {
            shouldUpdate = true;
          }

          // Box Width Adjustment propagates when sameTrackMount is true
          if (prev.sameTrackMount && (field === 'boxWidthAdjustmentType' || field === 'boxWidthAdjustmentValue' || field === 'applyBoxDeduction')) {
            shouldUpdate = true;
          }

          // Screw Covers propagates when sameScrewCovers is true
          if (prev.sameScrewCovers && field === 'screwCovers') {
            shouldUpdate = true;
          }

          // Fabric fields propagate when sameFabricForAll is true
          const fabricFields: (keyof ScreenUnit)[] = ['fabricType', 'fabricColor'];
          if (prev.sameFabricForAll && fabricFields.includes(field)) {
            shouldUpdate = true;
          }

          // Brush Size propagates when sameBrushSize is true
          if (prev.sameBrushSize && field === 'brushSize') {
            shouldUpdate = true;
          }
        }

        if (!shouldUpdate) return unit;

        const updatedUnit = { ...unit, [field]: value };

        // Auto-populate bias when height measurements change
        if (field === 'heightLeft' || field === 'heightCenter' || field === 'heightRight') {
          const heightLeft = field === 'heightLeft' ? parseFloat(value) || 0 : parseFloat(unit.heightLeft) || 0;
          const heightCenter = field === 'heightCenter' ? parseFloat(value) || 0 : parseFloat(unit.heightCenter) || 0;
          const heightRight = field === 'heightRight' ? parseFloat(value) || 0 : parseFloat(unit.heightRight) || 0;

          const biasCalc = calculateRecommendedBias(heightLeft, heightCenter, heightRight);

          // Only auto-fill bias amount if user has already enabled biased cut
          // Default is NO biased cut - most screens are not biased
          if (biasCalc.needsBias && updatedUnit.biasedCut) {
            updatedUnit.biasAmount = biasCalc.biasAmount.toString();
          }
        }

        // Auto-populate box width adjustment when width measurements change OR track mount changes to inside
        const shouldAutoPopulateBoxAdj = (field === 'widthTop' || field === 'widthMiddle' || field === 'widthBottom' || field === 'trackMount');
        if (shouldAutoPopulateBoxAdj && trackMountIncludesInside(updatedUnit.trackMount)) {
          const wT = field === 'widthTop' ? parseFloat(value as string) || 0 : parseFloat(unit.widthTop) || 0;
          const wM = field === 'widthMiddle' ? parseFloat(value as string) || 0 : parseFloat(unit.widthMiddle) || 0;
          const wB = field === 'widthBottom' ? parseFloat(value as string) || 0 : parseFloat(unit.widthBottom) || 0;
          const boxCalc = calculateRecommendedBoxAdjustment(wT, wM, wB);
          // Auto-set radio based on variance: top > min → "Larger", top < min → "Smaller"
          if (boxCalc.needsAdjustment && boxCalc.adjustmentAmount !== 0) {
            const isLarger = boxCalc.adjustmentAmount > 0;
            updatedUnit.boxWidthAdjustmentType = isLarger ? 'larger' : 'smaller';
            updatedUnit.boxWidthAdjustmentValue = Math.abs(boxCalc.adjustmentAmount).toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
          } else if (!boxCalc.needsAdjustment) {
            // No variance — reset to same
            updatedUnit.boxWidthAdjustmentType = 'same';
            updatedUnit.boxWidthAdjustmentValue = '';
          }
        }

        // When fabric type changes, reset to first available color
        if (field === 'fabricType') {
          const colors = FABRIC_COLORS[value] || [];
          updatedUnit.fabricColor = colors[0] || '';
        }

        return updatedUnit;
      });

      // Keep global fields in sync when unit 0 changes with propagation on
      const updated = { ...prev, units: newUnits };
      if (index === 0) {
        if (prev.sameUchannel && field === 'uChannel') {
          updated.globalUChannel = value as string;
        }
        if (prev.sameScrewCovers && field === 'screwCovers') {
          updated.globalScrewCovers = value as boolean;
        }
        if (prev.sameTrackMount && field === 'trackMount') {
          updated.globalTrackMount = value as string;
        }
        if (prev.sameInstallType && field === 'installationType') {
          updated.globalInstallationType = value as string;
        }
        if (prev.sameSystemType && field === 'systemType') {
          updated.globalSystemType = value as string;
        }
        if (prev.sameFabricForAll && field === 'fabricType') {
          updated.globalFabricType = value as string;
          // Also reset global color to first available for the new fabric
          const colors = FABRIC_COLORS[value as string] || [];
          updated.globalFabricColor = colors[0] || '';
        }
        if (prev.sameFabricForAll && field === 'fabricColor') {
          updated.globalFabricColor = value as string;
        }
      }
      return updated;
    });
    setCalculatedPricing(null);
  };

  const updateUnitWindowConfig = (index: number, config: WindowConfig) => {
    setFormData(prev => ({
      ...prev,
      units: prev.units.map((unit, i) =>
        i === index ? { ...unit, windowConfig: config } : unit
      ),
    }));
  };

  const addUnit = () => {
    // Clone from previous unit if exists, otherwise create empty
    const prevUnit = formData.units[formData.units.length - 1];
    const newUnit: ScreenUnit = prevUnit
      ? {
          ...prevUnit,
          id: Math.random().toString(36).substr(2, 9),
          // Clear dimensions - user must enter for new unit
          widthTop: '',
          widthMiddle: '',
          widthBottom: '',
          heightLeft: '',
          heightCenter: '',
          heightRight: '',
          // Clear screen location - user enters per unit
          screenLocation: '',
          // Reset bias - it's dimension-dependent, must be recalculated per unit
          biasedCut: false,
          biasAmount: '',
          biasSide: 'left',
          // Reset window config for new unit
          windowConfig: createEmptyWindowConfig(),
          // INHERIT all other options: installation, system, fabric
        }
      : createEmptyUnit();

    setFormData(prev => ({ ...prev, units: [...prev.units, newUnit] }));
    setExpandedUnits(prev => new Set(prev).add(newUnit.id));
  };

  const removeUnit = (index: number) => {
    setFormData(prev => ({
      ...prev,
      units: prev.units.filter((_, i) => i !== index),
    }));
    setCalculatedPricing(null);
  };

  const duplicateUnit = (index: number) => {
    const unitToCopy = formData.units[index];
    const newUnit = { ...unitToCopy, id: Math.random().toString(36).substr(2, 9) };
    setFormData(prev => ({
      ...prev,
      units: [...prev.units.slice(0, index + 1), newUnit, ...prev.units.slice(index + 1)],
    }));
    setExpandedUnits(prev => new Set(prev).add(newUnit.id));
  };

  const toggleUnitExpanded = (id: string) => {
    const wasExpanded = expandedUnits.has(id);
    setExpandedUnits(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    // When collapsing, scroll the unit header into view so user doesn't end up at bottom
    if (wasExpanded) {
      requestAnimationFrame(() => {
        const el = document.getElementById(`unit-header-${id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  };

  // Check if a unit has incomplete measurements (not all 6 filled)
  const hasIncompleteMeasurements = (unit: ScreenUnit): boolean => {
    const hasWidth = unit.widthTop && unit.widthMiddle && unit.widthBottom;
    const hasHeight = unit.heightLeft && unit.heightCenter && unit.heightRight;
    // A unit is incomplete if it has ANY measurements but not ALL 6
    const hasAnyMeasurement = unit.widthTop || unit.widthMiddle || unit.widthBottom ||
                              unit.heightLeft || unit.heightCenter || unit.heightRight;
    return hasAnyMeasurement && !(hasWidth && hasHeight);
  };

  // Get list of unit indices with incomplete measurements
  const getIncompleteUnits = (): number[] => {
    return formData.units
      .map((unit, index) => hasIncompleteMeasurements(unit) ? index : -1)
      .filter(index => index !== -1);
  };

  // Handle acknowledgment of incomplete measurements for current unit
  const handleAcknowledgeIncomplete = () => {
    const currentUnitIndex = incompleteUnitsToAcknowledge[currentAcknowledgeIndex];
    setAcknowledgedUnits(prev => new Set(prev).add(currentUnitIndex));

    if (currentAcknowledgeIndex < incompleteUnitsToAcknowledge.length - 1) {
      // More units to acknowledge
      setCurrentAcknowledgeIndex(prev => prev + 1);
    } else {
      // All units acknowledged, proceed with the pending action
      setIncompleteUnitsToAcknowledge([]);
      setCurrentAcknowledgeIndex(0);
      if (pendingSubmitAction === 'submit') {
        executeSubmit();
      }
      setPendingSubmitAction(null);
    }
  };

  // Cancel acknowledgment flow
  const handleCancelAcknowledge = () => {
    setIncompleteUnitsToAcknowledge([]);
    setCurrentAcknowledgeIndex(0);
    setPendingSubmitAction(null);
  };

  // Validate promo code on blur — calls POST /promotions/codes/validate
  // with the current pre-promo goods base so the dealer sees the exact
  // discount they'll get. Runs client-side; final validation still runs
  // server-side on submit.
  const validatePromoCode = async () => {
    const trimmed = promoCodeInput.trim();
    if (!trimmed) {
      setPromoCheck({ checking: false, valid: null, discount: 0, error: null });
      return;
    }
    if (!calculatedPricing) {
      setPromoCheck({ checking: false, valid: null, discount: 0, error: 'Calculate price first' });
      return;
    }
    setPromoCheck({ checking: true, valid: null, discount: 0, error: null });
    try {
      // Pre-promo goods base = subtotal - dealer discount - open roll discount.
      // calculatedPricing.subtotal already has dealer discount applied per the
      // existing pricing helper; subtract open roll then.
      const discountableSubtotal = Math.max(0,
        (calculatedPricing.subtotal || 0) - (calculatedPricing.openRollDiscount || 0)
      );
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/promotions/codes/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ code: trimmed, discountableSubtotal }),
      });
      const data = await res.json();
      if (!res.ok || !data.valid) {
        setPromoCheck({
          checking: false, valid: false, discount: 0,
          error: data.error || data.message || 'Invalid code',
        });
      } else {
        setPromoCheck({
          checking: false, valid: true,
          discount: Number(data.computedDiscount) || 0,
          error: null,
        });
      }
    } catch (e: any) {
      setPromoCheck({ checking: false, valid: false, discount: 0, error: e.message || 'Check failed' });
    }
  };

  // Calculate pricing using actual matrices
  const handleCalculatePricing = async () => {
    const validUnits = formData.units.filter(u =>
      (u.widthTop || u.widthMiddle || u.widthBottom) &&
      (u.heightLeft || u.heightCenter || u.heightRight)
    );

    if (validUnits.length === 0) {
      setError('Please add at least one unit with dimensions');
      return;
    }

    // Minimum screen width is 30" — don't quote below that.
    const underMin = formData.units.findIndex(u => {
      const w = Math.max(parseFloat(u.widthTop) || 0, parseFloat(u.widthMiddle) || 0, parseFloat(u.widthBottom) || 0);
      return w > 0 && w < 30;
    });
    if (underMin >= 0) {
      setError(`Screen #${underMin + 1} width must be at least 30". We don't build screens narrower than 30 inches.`);
      return;
    }

    // Check for dimension errors
    if (hasAnyDimensionErrors) {
      setError(`One or more units exceed maximum dimensions (${MAX_SCREEN_WIDTH}" x ${MAX_SCREEN_HEIGHT}"). Please correct before calculating.`);
      return;
    }

    setError(null);
    setCalculatingPrice(true);

    try {
      let screenTotal = 0;
      let uChannelTotal = 0;
      let uChannelFeet = 0;

      const screenItems = validUnits.map((unit, i) => {
        const width = Math.max(
          parseFloat(unit.widthTop) || 0,
          parseFloat(unit.widthMiddle) || 0,
          parseFloat(unit.widthBottom) || 0
        );
        const height = Math.max(
          parseFloat(unit.heightLeft) || 0,
          parseFloat(unit.heightCenter) || 0,
          parseFloat(unit.heightRight) || 0
        );

        // Get fabric type to determine pricing category
        const fabricType = formData.sameFabricForAll ? formData.globalFabricType : unit.fabricType;
        const fabric = FABRIC_TYPES.flatMap(g => g.options).find(f => f.value === fabricType);
        const fabricPricingCategory = (fabric?.pricingCategory || 'solar94') as keyof typeof PRICING_MATRICES;
        // Business rule: any unit with a vinyl window panel uses the CVW matrix,
        // regardless of the fabric. (Solar fabric + window = CVW base price.)
        const pricingCategory = unit.includeWindowPanel ? 'cvw' : fabricPricingCategory;

        // Get MSRP price from matrix, apply price increase + tariff, then dealer discount
        const rawMsrp = getScreenPrice(pricingCategory, width, height) || 0;
        const msrpPrice = rawMsrp * priceMultiplier;
        const basePrice = msrpPrice * (1 - dealerDiscount / 100);
        screenTotal += basePrice;

        // U-Channel calculation
        const uChannel = formData.sameUchannel ? (formData.units[0]?.uChannel || formData.globalUChannel) : unit.uChannel;
        const uChannelCost = getUChannelCost(height, uChannel);
        uChannelTotal += uChannelCost;
        if (uChannel !== 'none') {
          const sides = uChannel === 'both_sides' ? 2 : 1;
          uChannelFeet += (height / 12) * sides;
        }

        // Custom frame color upcharge
        let customFrameUpcharge = 0;
        if (unit.systemColor === 'custom') {
          customFrameUpcharge = width > 144 ? 895 : 495;
        }

        // Custom component color upcharge ($245 per component with custom color)
        let customComponentCount = 0;
        let customComponentUpcharge = 0;
        if (unit.useCustomColors && unit.componentColors) {
          const cc = unit.componentColors;
          const fc = unit.systemColor;
          if (cc.housingColor && cc.housingColor !== fc && cc.housingColor === 'custom') customComponentCount++;
          if (cc.weightBarColor && cc.weightBarColor !== fc && cc.weightBarColor === 'custom') customComponentCount++;
          if (cc.leftTrackColor && cc.leftTrackColor !== fc && cc.leftTrackColor === 'custom') customComponentCount++;
          if (cc.rightTrackColor && cc.rightTrackColor !== fc && cc.rightTrackColor === 'custom') customComponentCount++;
          if (cc.leftUChannelColor && cc.leftUChannelColor !== fc && cc.leftUChannelColor === 'custom') customComponentCount++;
          if (cc.rightUChannelColor && cc.rightUChannelColor !== fc && cc.rightUChannelColor === 'custom') customComponentCount++;
          customComponentUpcharge = customComponentCount * 245;
        }

        // Extra windows cost: 1 window included, each additional = $175
        const windowCount = unit.includeWindowPanel ? (unit.windowConfig?.windowCount || 1) : 0;
        const extraWindowsCost = unit.includeWindowPanel ? Math.max(0, windowCount - 1) * 175 : 0;

        // Screw covers cost - only if track mount includes face and screwCovers is true
        const trackMount = formData.sameTrackMount ? formData.globalTrackMount : unit.trackMount;
        const hasScrewCovers = (formData.sameScrewCovers ? formData.globalScrewCovers : unit.screwCovers) && trackMountIncludesFace(trackMount);
        const screwCoverQty = hasScrewCovers ? getScrewCoverQty(trackMount, uChannel) : 0;
        const screwCoversCost = hasScrewCovers ? calculateScrewCoversPrice(height, screwCoverQty) : 0;

        return {
          unit: i + 1,
          width,
          height,
          pricingCategory,
          basePrice,
          uChannelCost,
          customFrameUpcharge,
          customComponentCount,
          customComponentUpcharge,
          ralNumber: unit.ralNumber || '',
          systemColor: unit.systemColor,
          windowCount,
          extraWindowsCost,
          screwCoversCost,
          hasScrewCovers,
          totalPrice: basePrice + uChannelCost + customFrameUpcharge + customComponentUpcharge + extraWindowsCost + screwCoversCost,
        };
      });

      // Calculate total custom frame upcharge
      const customFrameTotal = screenItems.reduce((sum, item) => sum + item.customFrameUpcharge, 0);

      // Calculate total custom component color upcharge
      const customComponentTotal = screenItems.reduce((sum, item) => sum + item.customComponentUpcharge, 0);

      // Calculate total extra windows cost
      const extraWindowsTotal = screenItems.reduce((sum, item) => sum + item.extraWindowsCost, 0);

      // Calculate total screw covers cost
      const screwCoversTotal = screenItems.reduce((sum, item) => sum + item.screwCoversCost, 0);

      // Remote pricing based on remote type selected
      const remoteTotal = formData.remoteQty > 0 && formData.remoteType !== 'none'
        ? getRemotePrice(formData.remoteType, formData.remoteQty)
        : 0;

      // Wall switch pricing
      const wallSwitchTotal = formData.wallSwitchQty > 0 ? 82.5 * formData.wallSwitchQty : 0;

      // Somfy upgrade fee - $275 per motor (per screen unit)
      const isSomfyPackage = formData.motorType === 'Somfy';
      const somfyUpgradeTotal = isSomfyPackage ? validUnits.length * 275 : 0;

      // Alpha upgrade fee - $125 per motor (Keder is the default)
      const isAlphaPackage = formData.motorType === 'Alpha';
      const alphaUpgradeTotal = isAlphaPackage ? validUnits.length * 125 : 0;

      // Weight Bar Kit ($99) auto-adds for each screen <72" wide unless
      // dealer ticks "Skip kit". Backend re-validates from the same threshold.
      const weightBarKitPrice = 99;
      const weightBarKitCount = validUnits.filter(u => {
        const mw = Math.max(parseFloat(u.widthTop) || 0, parseFloat(u.widthMiddle) || 0, parseFloat(u.widthBottom) || 0);
        return mw > 0 && mw < 72 && !(u as any).skipWeightBarKit;
      }).length;
      const weightBarKitTotal = weightBarKitCount * weightBarKitPrice;

      // Packaging calculation (only for freight, not will call)
      const packaging = calculatePackaging(screenTotal, validUnits.length, formData.shippingMethod);

      // Freight estimate
      let freight = 0;
      let freightInfo: any = null;

      if (formData.shippingMethod === 'willcall') {
        freightInfo = { willCall: true, carrier: 'Will Call', service: 'Pickup at Lavon, TX' };
      } else if (formData.shipZip && formData.shipZip.length === 5) {
        // Each screen ships in its OWN box/skid (no pallets): 12"W × 14"H,
        // L = longest dim of that unit + 10". Weight = L(ft) × 14.5 lb/ft
        // (calibrated to actual BOL skid weights, incl. skid + strapping).
        const boxes = screenItems.map(s => {
          const len = Math.round(Math.max(s.width, s.height) + 10);
          return { length: len, width: 12, height: 14, weight: Math.round((len / 12) * 14.5) };
        });
        const length = Math.round(Math.max(...boxes.map(b => b.length))); // longest box (display/fallback)
        const shipWidth = 12;
        const shipHeight = 14;
        const weight = boxes.reduce((sum, b) => sum + b.weight, 0);
        const pallets = boxes.length; // one skid per screen

        try {
          const freightResult = await freightApi.estimate({
            destination_zip: formData.shipZip,
            // Full address goes through so Freightview shows real dealer info
            // in the quote-list UI and auto-saves the contact (no contacts API).
            destination_company: formData.dealerCompany || undefined,
            destination_address: formData.shipStreet || undefined,
            destination_city: formData.shipCity || undefined,
            destination_state: formData.shipState || undefined,
            destination_contact: formData.contactName || undefined,
            destination_phone: formData.phone || undefined,
            // Attach order # as FV reference when editing an existing order.
            // New orders don't have a number until save, so this is null.
            order_number: editOrderNumber || undefined,
            length: Math.round(length),
            width: shipWidth,
            height: Math.round(shipHeight),
            weight,
            pallets,
            boxes, // per-screen skids → each sent to FreightView as its own line item
            freight_class: 85,
            accessorials: {
              liftgate: formData.liftgateDelivery,
              residential: formData.residentialDelivery,
              inside: formData.insideDelivery,
            },
          });

          if (freightResult.success) {
            freight = freightResult.rate;
            freightInfo = {
              carrier: freightResult.carrier,
              service: freightResult.service,
              transitTime: freightResult.transitTime,
              rate: freightResult.rate,
              fallback: freightResult.fallback,
              // Include all quotes data for carrier selection
              freightviewShipmentId: freightResult.freightviewShipmentId,
              allQuotes: freightResult.allQuotes,
            };
            // Auto-populate freightData with cheapest carrier if quotes available
            if (freightResult.allQuotes?.length) {
              const cheapest = freightResult.allQuotes[0];
              setFreightData({
                method: 'shipping',
                freightviewShipmentId: freightResult.freightviewShipmentId,
                selectedQuoteId: cheapest.quoteId,
                selectedCarrierName: cheapest.carrierName,
                selectedCarrierCode: cheapest.carrierCode,
                freightQuotes: freightResult.allQuotes,
                freightTotalAmount: cheapest.totalAmount,
              });
            }
          }
        } catch (err) {
          console.error('Freight estimate error:', err);
          // Fallback estimate
          freight = 350;
          freightInfo = { carrier: 'Estimate', service: 'LTL', transitTime: '3-7 days', fallback: true };
        }
      }

      // Accessories total (fabric panels, replacements from PartsPage)
      const accessoriesTotal = linkedAccessories.reduce((sum, item) => sum + ((item.unitPrice || 0) * (item.quantity || 1)), 0);

      // Open roll discount: 4% off dealer cost for open roll units
      const openRollScreenTotal = screenItems
        .filter((_, i) => {
          const unit = validUnits[i];
          const st = formData.sameSystemType ? formData.globalSystemType : unit.systemType;
          return st === 'open_roll';
        })
        .reduce((sum, item) => sum + item.basePrice, 0);
      const openRollDiscount = openRollScreenTotal * (openRollDiscountPercent / 100);

      const subtotal = screenTotal + uChannelTotal + remoteTotal + wallSwitchTotal + customFrameTotal + customComponentTotal + extraWindowsTotal + screwCoversTotal + somfyUpgradeTotal + alphaUpgradeTotal + weightBarKitTotal + accessoriesTotal;

      // Rush surcharge — 25% of the goods base (matches recalculateOrderTotals
      // in orders.service.ts). Base = everything that lands in order_items
      // (screens already net of dealer discount + u-channel + screen extras),
      // minus the open-roll discount. Accessories that live in order_accessories
      // (remotes, wall switches, somfy/alpha upgrades, linked parts) are NOT
      // in the base, matching backend.
      const rushBase = screenTotal + uChannelTotal + customFrameTotal + customComponentTotal + extraWindowsTotal + screwCoversTotal + weightBarKitTotal - openRollDiscount;
      const rushSurcharge = formData.isRush ? Math.round(rushBase * 0.25 * 100) / 100 : 0;

      const total = subtotal - openRollDiscount + packaging + freight + rushSurcharge;

      setCalculatedPricing({
        screens: screenItems,
        screenTotal,
        uChannelTotal,
        uChannelFeet,
        customFrameTotal,
        customComponentTotal,
        extraWindowsTotal,
        screwCoversTotal,
        weightBarKitTotal,
        weightBarKitCount,
        remoteTotal,
        wallSwitchTotal,
        somfyUpgradeTotal,
        alphaUpgradeTotal,
        accessoriesTotal,
        openRollDiscount,
        packaging,
        freight,
        freightInfo,
        rushSurcharge,
        subtotal,
        total,
      });
    } finally {
      setCalculatingPrice(false);
    }
  };

  // Check for incomplete measurements and trigger acknowledgment flow if needed
  const handleConfirmOrder = async () => {
    console.log('[Confirm Order] Button clicked');

    if (!orderTermsAccepted) {
      setError('Please agree to the order terms and conditions');
      return;
    }

    if (!formData.sidemark.trim()) {
      setError('PO / Sidemark / Job Name is required');
      return;
    }
    if (!formData.orderedBy.trim()) {
      setError('Ordered By is required');
      return;
    }
    if (!formData.authorizedBy.trim()) {
      setError('Please enter your full name in Authorization');
      return;
    }
    if (!formData.neededByDate) {
      setError('Please select a Date Needed By');
      return;
    }
    if (formData.neededByDate < earliestDateString) {
      setError(`Selected date is before production lead time. Earliest: ${earliestDateDisplay}`);
      return;
    }
    if (!calculatedPricing) {
      setError('Please calculate price first');
      return;
    }

    // Check for incomplete measurements
    const incompleteUnits = getIncompleteUnits();
    if (incompleteUnits.length > 0) {
      // Start acknowledgment flow
      setIncompleteUnitsToAcknowledge(incompleteUnits);
      setCurrentAcknowledgeIndex(0);
      setPendingSubmitAction('submit');
      return;
    }

    // No incomplete measurements, proceed directly
    executeSubmit();
  };

  // Actual submission logic (called after acknowledgment if needed)
  const executeSubmit = async (invoiceRequested = false) => {
    console.log('[Confirm Order] Executing submission');
    setSaving(true);
    setError(null);

    try {
      // Build order items from units
      const items = formData.units
        .filter(u => (u.widthTop || u.widthMiddle || u.widthBottom) && (u.heightLeft || u.heightCenter || u.heightRight))
        .map(unit => {
          const fabricType = formData.sameFabricForAll ? formData.globalFabricType : unit.fabricType;
          const fabric = FABRIC_TYPES.flatMap(g => g.options).find(f => f.value === fabricType);

          // Get fabric info for this unit
          const unitFabricType = formData.sameFabricForAll ? formData.globalFabricType : unit.fabricType;
          const unitFabricColor = formData.sameFabricForAll ? formData.globalFabricColor : unit.fabricColor;

          // Determine screw covers based on track mount and u-channel
          const trackMount = formData.sameTrackMount ? formData.globalTrackMount : unit.trackMount;
          const uChannel = formData.sameUchannel ? (formData.units[0]?.uChannel || formData.globalUChannel) : unit.uChannel;
          const screwCovers = trackMountIncludesFace(trackMount)
            ? (formData.sameScrewCovers ? formData.globalScrewCovers : unit.screwCovers)
            : false;
          const screwCoverQty = screwCovers ? getScrewCoverQty(trackMount, uChannel) : 0;

          return {
            productType: fabric?.pricingCategory || 'solar94',
            // Raw opening measurements only - factory calculates final dimensions at production
            widthTop: parseFloat(unit.widthTop) || undefined,
            widthMiddle: parseFloat(unit.widthMiddle) || undefined,
            widthBottom: parseFloat(unit.widthBottom) || undefined,
            heightLeft: parseFloat(unit.heightLeft) || undefined,
            heightCenter: parseFloat(unit.heightCenter) || undefined,
            heightRight: parseFloat(unit.heightRight) || undefined,
            // Configuration options
            motorSide: unit.motorSide,
            uChannel: uChannel,
            applyUChannelDeduction: unit.applyUChannelDeduction,
            // systemType MUST be passed per-item or the backend INSERT defaults
            // to 'box' (orders.service.ts:2038). Match handleSaveDraft's logic
            // so Submit doesn't wipe open_roll → box and silently kill the
            // open-roll discount on recalc.
            systemType: formData.sameSystemType ? formData.globalSystemType : unit.systemType,
            skipWeightBarKit: !!unit.skipWeightBarKit,
            boxWidthAdjustment: unit.boxWidthAdjustmentType === 'same' ? null
              : unit.boxWidthAdjustmentType === 'smaller' ? -(parseFloat(unit.boxWidthAdjustmentValue) || 0)
              : (parseFloat(unit.boxWidthAdjustmentValue) || 0),
            applyBoxDeduction: unit.applyBoxDeduction,
            expertMode: isExpertMode,
            biasedCut: unit.biasedCut,
            biasedCutInches: unit.biasedCut ? parseFloat(unit.biasAmount) || undefined : undefined,
            biasedCutSide: unit.biasedCut ? unit.biasSide : undefined,
            installationType: formData.sameInstallType ? formData.globalInstallationType : unit.installationType,
            trackMount: trackMount,
            systemColor: unit.systemColor,
            ralNumber: unit.systemColor === 'custom' ? unit.ralNumber : undefined,
            // Per-component color overrides (only store differences from frame color)
            componentColors: unit.useCustomColors ? (() => {
              const cc: any = {};
              const fc = unit.systemColor;
              if (unit.componentColors.housingColor && unit.componentColors.housingColor !== fc) {
                cc.housingColor = unit.componentColors.housingColor;
                if (unit.componentColors.housingColor === 'custom' && unit.componentColors.housingRalNumber) cc.housingRalNumber = unit.componentColors.housingRalNumber;
              }
              if (unit.componentColors.leftTrackColor && unit.componentColors.leftTrackColor !== fc) {
                cc.leftTrackColor = unit.componentColors.leftTrackColor;
                if (unit.componentColors.leftTrackColor === 'custom' && unit.componentColors.leftTrackRalNumber) cc.leftTrackRalNumber = unit.componentColors.leftTrackRalNumber;
              }
              if (unit.componentColors.rightTrackColor && unit.componentColors.rightTrackColor !== fc) {
                cc.rightTrackColor = unit.componentColors.rightTrackColor;
                if (unit.componentColors.rightTrackColor === 'custom' && unit.componentColors.rightTrackRalNumber) cc.rightTrackRalNumber = unit.componentColors.rightTrackRalNumber;
              }
              if (unit.componentColors.weightBarColor && unit.componentColors.weightBarColor !== fc) {
                cc.weightBarColor = unit.componentColors.weightBarColor;
                if (unit.componentColors.weightBarColor === 'custom' && unit.componentColors.weightBarRalNumber) cc.weightBarRalNumber = unit.componentColors.weightBarRalNumber;
              }
              if (unit.componentColors.leftUChannelColor && unit.componentColors.leftUChannelColor !== fc) {
                cc.leftUChannelColor = unit.componentColors.leftUChannelColor;
                if (unit.componentColors.leftUChannelColor === 'custom' && unit.componentColors.leftUChannelRalNumber) cc.leftUChannelRalNumber = unit.componentColors.leftUChannelRalNumber;
              }
              if (unit.componentColors.rightUChannelColor && unit.componentColors.rightUChannelColor !== fc) {
                cc.rightUChannelColor = unit.componentColors.rightUChannelColor;
                if (unit.componentColors.rightUChannelColor === 'custom' && unit.componentColors.rightUChannelRalNumber) cc.rightUChannelRalNumber = unit.componentColors.rightUChannelRalNumber;
              }
              return Object.keys(cc).length > 0 ? cc : undefined;
            })() : undefined,
            brushSize: unit.brushSize,
            // Screw covers
            screwCovers: screwCovers,
            screwCoverQty: screwCoverQty,
            // Fabric per unit
            fabricType: unitFabricType,
            fabricColor: unitFabricColor,
            // Screen location
            screenLocation: unit.screenLocation || undefined,
            // Window panel data (if applicable)
            windowPanel: unit.includeWindowPanel,
            windowData: unit.includeWindowPanel ? {
              orientation: unit.windowConfig.orientation,
              windowCount: unit.windowConfig.windowCount,
              dividerType: unit.windowConfig.dividerType,
              dividerSize: unit.windowConfig.dividerSize,
              horizontalDividers: unit.windowConfig.horizontalDividers,
              verticalDividers: unit.windowConfig.verticalDividers,
              windowBottomFromGround: unit.windowConfig.windowBottomFromGround,
              windowTopFromGround: unit.windowConfig.windowTopFromGround,
              borderTop: unit.windowConfig.borderTop,
              borderSide: unit.windowConfig.borderSide,
              borderBottom: unit.windowConfig.borderBottom,
            } : undefined,
            notes: '',
          };
        });

      // Create the order
      const orderData = {
        contactId: (user as any)?.contactId || (user as any)?.id,
        sidemark: formData.sidemark,
        poNumber: formData.poNumber,
        notes: formData.comments,
        shipToAddress: formData.shippingMethod === 'willcall' ? undefined : {
          street: formData.shipStreet,
          city: formData.shipCity,
          state: formData.shipState,
          zip: formData.shipZip,
        },
        // Which ship-to was chosen ('dealer' | 'customer' | 'willcall'). Only the
        // resulting address used to be sent, so the factory could not tell a
        // drop-ship to a homeowner from a delivery to the dealer's warehouse.
        shippingAddressSource: formData.shippingAddressSource || undefined,
        // Shipping method and freight data from carrier selection
        shippingMethod: (freightData?.method === 'will_call' || formData.shippingMethod === 'willcall') ? 'will_call' : 'shipping',
        freightviewShipmentId: freightData?.freightviewShipmentId,
        selectedQuoteId: freightData?.selectedQuoteId,
        selectedCarrierName: freightData?.selectedCarrierName,
        selectedCarrierCode: freightData?.selectedCarrierCode,
        freightQuotes: freightData?.freightQuotes,
        freightTotalAmount: freightData?.freightTotalAmount,
        installationType: formData.globalInstallationType,
        trackMount: formData.globalTrackMount,
        systemColor: formData.systemColor,
        brushSize: formData.units[0]?.brushSize === '2inch' ? '2"' : '1"',
        fabricType: formData.globalFabricType,
        fabricColor: formData.globalFabricColor,
        motorType: formData.motorType.toLowerCase(),
        controllerType: formData.remoteType !== 'none' ? formData.remoteType : undefined,
        controllerQty: formData.remoteQty,
        wallSwitchType: formData.wallSwitchType !== 'none' ? formData.wallSwitchType : undefined,
        wallSwitchQty: formData.wallSwitchQty,
        accessories: formData.accessories || [],
        linkedAccessories: linkedAccessories.length > 0 ? linkedAccessories : undefined,
        // Per-dealer promo code, only send when the dealer typed one AND it
        // passed client-side validation. Server re-validates on submit.
        promoCode: (promoCheck.valid && promoCodeInput.trim()) ? promoCodeInput.trim() : undefined,
        liftgateDelivery: formData.liftgateDelivery,
        residentialDelivery: formData.residentialDelivery,
        insideDelivery: formData.insideDelivery,
        neededByDate: formData.neededByDate || undefined,
        isRush: formData.isRush || false,
        // Customer lead linking (CRM)
        customerLeadId: formData.customerLeadId || undefined,
        customerLeadName: formData.customerName || undefined,
        customerLeadEmail: formData.customerEmail || undefined,
        customerLeadPhone: formData.customerPhone || undefined,
        customerLeadAddress: formData.customerAddress ?
          `${formData.customerAddress}, ${formData.customerCity}, ${formData.customerState} ${formData.customerZip}` : undefined,
        customerSellPrice: formData.customerSellPrice ? parseFloat(formData.customerSellPrice) : undefined,
        items,
        // Dealer opt-in bundling — see comment in the draft-save path above.
        consolidateWithOrderIds:
          bundleWithIds.length > 0 && bundleAcknowledged ? bundleWithIds : undefined,
        // Store full form data for restoration
        formData: { ...formData },
      };

      let order;
      if (isEditMode && editOrderId) {
        // Update existing order
        console.log('[Confirm Order] Updating existing order:', editOrderId, 'original status:', editOriginalStatus);
        const { status: _status, ...updateData } = orderData;
        order = await ordersApi.update(editOrderId, updateData);
        // Only call submit() for draft orders. Already-submitted / pending_payment
        // orders don't need it (the update is the meaningful change), and
        // needs_correction is auto-resubmitted by the API's update() handler.
        // Calling submit() on a non-draft order throws "Order has already been submitted".
        if (editOriginalStatus === 'draft') {
          await ordersApi.submit(editOrderId, invoiceRequested);
          console.log('[Confirm Order] Draft submitted:', editOrderId, 'invoiceRequested:', invoiceRequested);
        } else {
          console.log('[Confirm Order] Order updated (no submit call needed for status:', editOriginalStatus, ')');
        }
      } else {
        console.log('[Confirm Order] Creating new order');
        order = await ordersApi.create(orderData);
        // Submit the new order
        await ordersApi.submit(order.id, invoiceRequested);
        console.log('[Confirm Order] Order created and submitted:', order.id, 'invoiceRequested:', invoiceRequested);
      }

      const msg = invoiceRequested
        ? 'Order submitted for invoicing. You will receive an invoice via email or QuickBooks.'
        : 'Order confirmed! Review your invoice and proceed to payment.';
      navigate(`/orders/${order.id || editOrderId}`, {
        state: { fromNewOrder: true, message: msg }
      });
    } catch (err: any) {
      console.error('[Confirm Order] Exception:', err);
      setError(err.response?.data?.message || 'Failed to create order. Please try again.');
    } finally {
      setSaving(false);
    }
  };


  // Map motorType to package value for compatibility
  const motorPackage = formData.motorType === 'Alpha' ? 'alpha'
    : formData.motorType === 'Somfy' ? 'somfy'
    : formData.motorType === 'Keder Screens' ? 'keder'
    : formData.motorType === 'No Motor' ? 'none'
    : 'alpha'; // default

  const remoteOptions = motorPackage === 'alpha'
    ? ALPHA_REMOTES
    : motorPackage === 'keder'
      ? KEDER_REMOTES
      : motorPackage === 'somfy'
        ? SOMFY_REMOTES
        : [];
  const wallSwitchOptions = motorPackage === 'alpha'
    ? ALPHA_WALL_SWITCHES
    : motorPackage === 'keder'
      ? KEDER_WALL_SWITCHES
      : motorPackage === 'somfy'
        ? SOMFY_WALL_SWITCHES
        : [];

  const isNoMotor = motorPackage === 'none';
  const isKederPackage = motorPackage === 'keder';

  return (
    <div className="max-w-5xl mx-auto">
      <PageHeader
        title={isEditMode ? `Edit Order ${editOrderNumber || ''}` : "V-Track Order Form"}
        subtitle={isEditMode ? "Modify your draft order" : "Complete order form for motorized screen systems"}
      />

      {/* RUSH ORDER CONFIRMATION MODAL — fires when dealer checks the RUSH
          box. Must explicitly confirm here before isRush gets set. Cancel
          leaves the box unchecked. */}
      {rushConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setRushConfirmOpen(false)}>
          <div
            className="bg-keder-dark border-2 border-red-500 rounded-xl max-w-lg w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-500/20 rounded-full p-2">
                <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-white">Confirm RUSH Order</h3>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              Marking this as a RUSH order will:
            </p>
            <ul className="space-y-2 text-sm text-gray-200 mb-5">
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-0.5">•</span>
                <span>Add a <strong className="text-red-300">+25% surcharge</strong> to the dealer-net screen price on your invoice</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-0.5">•</span>
                <span>Move your order to the <strong className="text-keder-yellow">top of every production queue</strong> at the Keder factory</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-0.5">•</span>
                <span>Result in a <strong className="text-red-300">non-refundable</strong> surcharge if you cancel after the order is in production</span>
              </li>
            </ul>
            <p className="text-xs text-gray-400 mb-5">
              Only use this when you genuinely need the order produced ahead of normal lead time. Surcharge applies regardless of whether the order ships on the date you requested.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setRushConfirmOpen(false)}
                className="px-5 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  updateFormField('isRush', true);
                  setRushConfirmOpen(false);
                }}
                className="px-5 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 font-bold"
              >
                Confirm RUSH (+25%)
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Expired-quote notice — shown when an order is started from a quote past its 30-day window */}
        {expiredQuoteNotice && (
          <div className="bg-orange-900/40 border border-orange-500 text-orange-100 px-4 py-3 rounded-lg flex justify-between items-start gap-3">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-sm">{expiredQuoteNotice}</span>
            </div>
            <button onClick={() => setExpiredQuoteNotice(null)} className="text-orange-200 hover:text-white font-bold flex-shrink-0">×</button>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg flex justify-between items-center">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)} className="text-green-200 hover:text-white font-bold">×</button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg flex justify-between items-center">
            <div>
              <span>{error}</span>
              {Object.keys(fieldErrors).length > 0 && (
                <ul className="mt-2 text-sm list-disc list-inside">
                  {Object.values(fieldErrors).filter(Boolean).map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
            <button onClick={() => { setError(null); setFieldErrors({}); }} className="text-red-200 hover:text-white font-bold self-start">×</button>
          </div>
        )}

        {/* ========== CUSTOMER & JOB INFO ========== */}
        <div className="card border-keder-yellow">
          <h2 className="text-lg font-semibold text-keder-yellow mb-4">Customer & Job Information</h2>

          {/* Dealer Info Display (read-only from login) */}
          <div className="mb-6 p-4 bg-keder-gray/30 rounded-lg border border-keder-gray">
            <div className="flex items-center gap-4">
              {/* Show dealer logo if uploaded, otherwise show Keder logo */}
              {(user as any)?.logoUrl ? (
                <img
                  src={(user as any).logoUrl}
                  alt={formData.dealerCompany || 'Dealer'}
                  className="h-12 w-auto max-w-32 object-contain rounded border border-keder-gray bg-white p-1"
                />
              ) : (
                <img
                  src="https://kederscreens.com/wp-content/uploads/2025/07/Keder-logos-1-scaled.png"
                  alt="Keder"
                  className="h-10 w-auto"
                />
              )}
              <div>
                <p className="text-white font-semibold">{formData.dealerCompany || 'Dealer'}</p>
                <p className="text-gray-400 text-sm">{formData.contactName} • {formData.email}</p>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="mb-6">
            <h3 className="text-md font-semibold text-white mb-1">Customer Details</h3>
            <p className="text-xs text-gray-400 mb-3">Customer info is used for warranty registration, RMA processing, and auto-filling warranty forms.</p>
            {customerDuplicateWarning && (
              <div className="mb-4 p-3 bg-yellow-900/30 border border-yellow-500 rounded-lg">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-yellow-400 font-medium">Customer may already exist</p>
                    <p className="text-yellow-300 text-sm">{customerDuplicateWarning}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-1">Customer Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => updateFormField('customerName', e.target.value)}
                  onBlur={async () => {
                    if (formData.customerName && formData.customerAddress) {
                      setCustomerCheckLoading(true);
                      try {
                        const response = await fetch('/api/dealer-leads/check-customer', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                          },
                          body: JSON.stringify({
                            customer_name: formData.customerName,
                            address: formData.customerAddress,
                          }),
                        });
                        const data = await response.json();
                        if (data.exists && data.matches?.length > 0) {
                          const match = data.matches[0];
                          setCustomerDuplicateWarning(
                            `This customer appears to be registered with ${match.dealer_name || 'another dealer'}. Please verify before proceeding.`
                          );
                        } else {
                          setCustomerDuplicateWarning(null);
                        }
                      } catch (err) {
                        console.error('Customer check failed:', err);
                      }
                      setCustomerCheckLoading(false);
                    }
                  }}
                  className={`input-field w-full ${reqClass(formData.customerName, 'customerName')}`}
                  required
                />
                {fieldErrors.customerName && (
                  <p className="text-red-400 text-sm mt-1">{fieldErrors.customerName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Phone <span className="text-red-500">*</span> (Phone or Email required)</label>
                <input
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) => updateFormField('customerPhone', e.target.value)}
                  className={`input-field w-full ${reqClassPhoneEmail('customerPhone')}`}
                />
                {fieldErrors.customerPhone && (
                  <p className="text-red-400 text-sm mt-1">{fieldErrors.customerPhone}</p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Email <span className="text-red-500">*</span> (Phone or Email required)</label>
                <input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => updateFormField('customerEmail', e.target.value)}
                  className={`input-field w-full ${reqClassPhoneEmail('customerEmail')}`}
                />
                {fieldErrors.customerEmail && (
                  <p className="text-red-400 text-sm mt-1">{fieldErrors.customerEmail}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-1">Street Address (optional)</label>
                <input
                  type="text"
                  value={formData.customerAddress}
                  onChange={(e) => updateFormField('customerAddress', e.target.value)}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">City</label>
                <input
                  type="text"
                  value={formData.customerCity}
                  onChange={(e) => updateFormField('customerCity', e.target.value)}
                  className="input-field w-full"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">State <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.customerState}
                    onChange={(e) => updateFormField('customerState', e.target.value)}
                    className={`input-field w-full ${reqClass(formData.customerState, 'customerState')}`}
                    maxLength={2}
                    required
                  />
                  {fieldErrors.customerState && (
                    <p className="text-red-400 text-sm mt-1">{fieldErrors.customerState}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">ZIP Code <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.customerZip}
                    onChange={(e) => updateFormField('customerZip', e.target.value)}
                    className={`input-field w-full ${reqClass(formData.customerZip, 'customerZip')}`}
                    maxLength={10}
                    required
                  />
                  {fieldErrors.customerZip && (
                    <p className="text-red-400 text-sm mt-1">{fieldErrors.customerZip}</p>
                  )}
                </div>
              </div>
            </div>
            {customerCheckLoading && (
              <p className="text-gray-400 text-sm mt-2">Checking customer...</p>
            )}
            <p className="text-xs text-gray-500 mt-2">We never sell or share your data.</p>
          </div>

          {/* Order Details */}
          <div className="border-t border-gray-700 pt-4">
            <h3 className="text-md font-semibold text-white mb-3">Order Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Order Date</label>
              <input
                type="date"
                value={formData.orderDate}
                onChange={(e) => updateFormField('orderDate', e.target.value)}
                className="input-field w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Ordered By (Name) <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={formData.orderedBy}
                onChange={(e) => updateFormField('orderedBy', e.target.value)}
                className={`input-field w-full ${reqClass(formData.orderedBy, 'orderedBy')}`}
                required
              />
              {fieldErrors.orderedBy && (
                <p className="text-red-400 text-sm mt-1">{fieldErrors.orderedBy}</p>
              )}
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm text-gray-400 mb-1">PO / Sidemark / Job Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={formData.sidemark}
              onChange={(e) => updateFormField('sidemark', e.target.value)}
              className={`input-field w-full ${reqClass(formData.sidemark, 'sidemark')}`}
              placeholder="e.g., Smith Residence, PO-12345, Johnson Remodel"
              required
            />
            {fieldErrors.sidemark && (
              <p className="text-red-400 text-sm mt-1">{fieldErrors.sidemark}</p>
            )}
          </div>
          </div>
        </div>

        {/* ========== SCREEN UNITS (PRIMARY) ========== */}
        <div className="card border-2 border-keder-yellow">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-xl font-bold text-keder-yellow">Screen Units ({formData.units.length})</h2>
                <p className="text-sm text-gray-400">Configure each screen unit with all options</p>
              </div>
              {/* Expert Mode Toggle */}
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => setIsExpertMode(!isExpertMode)}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                    isExpertMode ? 'bg-keder-yellow' : 'bg-gray-600'
                  }`}
                  title={isExpertMode ? 'Switch to normal measurement mode' : 'Switch to exact mode (single width & height)'}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    isExpertMode ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
                <span className={`text-xs font-medium ${isExpertMode ? 'text-keder-yellow' : 'text-gray-400'}`}>
                  Exact
                </span>
              </div>
            </div>
            <button onClick={addUnit} className="btn-primary text-sm">
              + Add Unit
            </button>
          </div>

          {/* Global Unit Settings */}
          <div className="mb-4 p-4 bg-keder-gray/30 rounded-lg border border-keder-gray">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sameSystemType}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateFormField('sameSystemType', checked);
                    // If checked, apply Unit 1's value to all other units
                    if (checked && formData.units.length > 0) {
                      const unit1Value = formData.units[0].systemType;
                      setFormData(prev => ({
                        ...prev,
                        units: prev.units.map((u, i) => i === 0 ? u : { ...u, systemType: unit1Value })
                      }));
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                />
                <span className="text-gray-300 text-sm">Same System Type for all units</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sameInstallType}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateFormField('sameInstallType', checked);
                    // If checked, apply Unit 1's value to all other units
                    if (checked && formData.units.length > 0) {
                      const unit1Value = formData.units[0].installationType;
                      setFormData(prev => ({
                        ...prev,
                        units: prev.units.map((u, i) => i === 0 ? u : { ...u, installationType: unit1Value })
                      }));
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                />
                <span className="text-gray-300 text-sm">Same Installation Type for all units</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sameTrackMount}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateFormField('sameTrackMount', checked);
                    // If checked, apply Unit 1's value to all other units
                    if (checked && formData.units.length > 0) {
                      const u1 = formData.units[0];
                      setFormData(prev => ({
                        ...prev,
                        units: prev.units.map((u, i) => i === 0 ? u : {
                          ...u,
                          trackMount: u1.trackMount,
                          boxWidthAdjustmentType: u1.boxWidthAdjustmentType,
                          boxWidthAdjustmentValue: u1.boxWidthAdjustmentValue,
                          applyBoxDeduction: u1.applyBoxDeduction,
                        })
                      }));
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                />
                <span className="text-gray-300 text-sm">Same Track Mount for all units</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sameUchannel}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateFormField('sameUchannel', checked);
                    // If checked, apply Unit 1's value to all other units and sync global
                    if (checked && formData.units.length > 0) {
                      const unit1Value = formData.units[0].uChannel;
                      setFormData(prev => ({
                        ...prev,
                        globalUChannel: unit1Value,
                        units: prev.units.map((u, i) => i === 0 ? u : { ...u, uChannel: unit1Value })
                      }));
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                />
                <span className="text-gray-300 text-sm">Same U-Channel for all units</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sameScrewCovers}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateFormField('sameScrewCovers', checked);
                    // If checked, apply Unit 1's value to all other units
                    if (checked && formData.units.length > 0) {
                      const unit1Value = formData.units[0].screwCovers;
                      setFormData(prev => ({
                        ...prev,
                        units: prev.units.map((u, i) => i === 0 ? u : { ...u, screwCovers: unit1Value })
                      }));
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                />
                <span className="text-gray-300 text-sm">Same Screw Covers for all units</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sameFabricForAll}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateFormField('sameFabricForAll', checked);
                    // If checked, apply Unit 1's values to all other units
                    if (checked && formData.units.length > 0) {
                      const unit1Type = formData.units[0].fabricType;
                      const unit1Color = formData.units[0].fabricColor;
                      setFormData(prev => ({
                        ...prev,
                        units: prev.units.map((u, i) => i === 0 ? u : { ...u, fabricType: unit1Type, fabricColor: unit1Color })
                      }));
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                />
                <span className="text-gray-300 text-sm">Same Material for all units</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.sameBrushSize}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateFormField('sameBrushSize', checked);
                    // If checked, apply Unit 1's value to all other units
                    if (checked && formData.units.length > 0) {
                      const unit1Value = formData.units[0].brushSize;
                      setFormData(prev => ({
                        ...prev,
                        units: prev.units.map((u, i) => i === 0 ? u : { ...u, brushSize: unit1Value })
                      }));
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                />
                <span className="text-gray-300 text-sm">Same Brush Size for all units</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.includeWindowPanels}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    updateFormField('includeWindowPanels', checked);
                    // If checked, enable window panels on all units; if unchecked, allow independent control
                    setFormData(prev => ({
                      ...prev,
                      units: prev.units.map(u => ({ ...u, includeWindowPanel: checked }))
                    }));
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-keder-yellow focus:ring-keder-yellow"
                />
                <span className="text-gray-300 text-sm">Include Window Panels on all units</span>
              </label>
            </div>
          </div>

          {formData.units.length === 0 ? (
            <div className="border border-dashed border-keder-gray rounded-lg p-8 text-center">
              <p className="text-gray-400 mb-4">No units added yet. Add your first screen unit.</p>
              <button onClick={addUnit} className="btn-primary">
                + Add First Unit
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {formData.units.map((unit, index) => {
                const isExpanded = expandedUnits.has(unit.id);
                const hasValidDimensions = (unit.widthTop || unit.widthMiddle || unit.widthBottom) &&
                                           (unit.heightLeft || unit.heightCenter || unit.heightRight);
                const screenWidth = Math.max(
                  parseFloat(unit.widthTop) || 0,
                  parseFloat(unit.widthMiddle) || 0,
                  parseFloat(unit.widthBottom) || 0
                );
                const screenHeight = Math.max(
                  parseFloat(unit.heightLeft) || 0,
                  parseFloat(unit.heightCenter) || 0,
                  parseFloat(unit.heightRight) || 0
                );
                const trackMount = formData.sameTrackMount ? formData.globalTrackMount : unit.trackMount;

                // Dimension validation - check if exceeds max allowed
                const widthExceedsMax = screenWidth > MAX_SCREEN_WIDTH;
                const heightExceedsMax = screenHeight > MAX_SCREEN_HEIGHT;
                const dimensionError = widthExceedsMax || heightExceedsMax;
                const dimensionErrorMsg = dimensionError
                  ? `Dimensions exceed maximum: ${widthExceedsMax ? `Width ${screenWidth}" > ${MAX_SCREEN_WIDTH}"` : ''}${widthExceedsMax && heightExceedsMax ? ', ' : ''}${heightExceedsMax ? `Height ${screenHeight}" > ${MAX_SCREEN_HEIGHT}"` : ''}`
                  : null;

                return (
                  <div key={unit.id} className="border border-keder-gray rounded-lg overflow-hidden">
                    {/* Unit Header */}
                    <div
                      id={`unit-header-${unit.id}`}
                      className={clsx(
                        'flex justify-between items-center px-4 py-3 cursor-pointer',
                        isExpanded ? 'bg-keder-gray' : 'bg-keder-dark hover:bg-keder-gray/50'
                      )}
                      onClick={() => toggleUnitExpanded(unit.id)}
                    >
                      <div className="flex items-center gap-3">
                        <span className={clsx(
                          'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
                          hasValidDimensions ? 'bg-keder-yellow text-black' : 'bg-gray-600 text-gray-300'
                        )}>
                          {index + 1}
                        </span>
                        <span className="text-white">Unit #{index + 1}</span>
                        {hasValidDimensions && (
                          <span className={clsx(
                            "text-sm",
                            dimensionError ? "text-red-400" : "text-gray-400"
                          )}>
                            {screenWidth}" x {screenHeight}"
                            {dimensionError && " ⚠️ TOO LARGE"}
                          </span>
                        )}
                        {/* Show custom colors badge when collapsed */}
                        {!isExpanded && unit.useCustomColors && (() => {
                          const cc = unit.componentColors;
                          const fc = unit.systemColor;
                          const hasOverrides = (cc.housingColor && cc.housingColor !== fc) ||
                            (cc.leftTrackColor && cc.leftTrackColor !== fc) ||
                            (cc.rightTrackColor && cc.rightTrackColor !== fc) ||
                            (cc.weightBarColor && cc.weightBarColor !== fc) ||
                            (cc.leftUChannelColor && cc.leftUChannelColor !== fc) ||
                            (cc.rightUChannelColor && cc.rightUChannelColor !== fc);
                          return hasOverrides ? (
                            <span className="text-xs text-blue-400 bg-blue-900/30 px-2 py-0.5 rounded">
                              Mixed Colors
                            </span>
                          ) : null;
                        })()}
                        {/* Show fabric info when collapsed */}
                        {!isExpanded && unit.fabricType && (
                          <span className="text-sm text-green-400">
                            {unit.fabricType === 'solar94' ? 'Solar' :
                             unit.fabricType === 'solar99' ? 'Premium Solar' :
                             unit.fabricType === 'vinyl' ? 'Vinyl' :
                             unit.fabricType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            {unit.fabricColor && ` - ${unit.fabricColor}`}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); duplicateUnit(index); }}
                          className="px-3 py-1 text-sm bg-green-800 hover:bg-green-700 text-white rounded"
                        >
                          Duplicate
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeUnit(index); }}
                          className="px-3 py-1 text-sm bg-red-600 hover:bg-red-500 text-white rounded"
                        >
                          Remove
                        </button>
                        {!isExpanded ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleUnitExpanded(unit.id); }}
                            className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded flex items-center gap-1"
                          >
                            Show
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        ) : (
                          <svg className="w-5 h-5 text-gray-400 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Unit Content */}
                    {isExpanded && (
                      <div className="p-4 space-y-4">
                        {/* Dimensions */}
                        {/* Fraction/Decimal Toggle - shown once per unit at the top of dimensions */}
                        {index === 0 && (
                          <div className="flex items-center justify-end gap-2 -mb-2">
                            <span className="text-xs text-gray-500">Input mode:</span>
                            <button
                              type="button"
                              onClick={() => setFractionMode(!fractionMode)}
                              className={clsx(
                                'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
                                fractionMode
                                  ? 'bg-keder-yellow text-black'
                                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              )}
                            >
                              {fractionMode ? '⅝ Fractions' : '.125 Decimal'}
                            </button>
                          </div>
                        )}
                        {isExpertMode ? (
                          /* Expert Mode: Single width + height fields */
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm text-keder-yellow mb-2">System Width (inches)</label>
                              <DimensionInput
                                value={unit.widthTop}
                                fractionMode={fractionMode}
                                onChange={(val) => {
                                  updateUnit(index, 'widthTop', val);
                                  updateUnit(index, 'widthMiddle', val);
                                  updateUnit(index, 'widthBottom', val);
                                }}
                                className="input-field w-full text-center text-lg"
                                placeholder={fractionMode ? 'e.g. 96 5/16' : 'Width'}
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-keder-yellow mb-2">System Height (inches)</label>
                              <DimensionInput
                                value={unit.heightLeft}
                                fractionMode={fractionMode}
                                onChange={(val) => {
                                  updateUnit(index, 'heightLeft', val);
                                  updateUnit(index, 'heightCenter', val);
                                  updateUnit(index, 'heightRight', val);
                                }}
                                className="input-field w-full text-center text-lg"
                                placeholder={fractionMode ? 'e.g. 72 1/2' : 'Height'}
                              />
                            </div>
                          </div>
                        ) : (
                          /* Normal Mode: 3x3 measurement grid */
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Opening Width (inches) <InfoTooltip text="Measure opening width at three points: top, middle, and bottom. We use the SMALLEST measurement for inside/recessed mounts." position="bottom" /></label>
                              <div className="grid grid-cols-3 gap-2">
                                {(['widthTop', 'widthMiddle', 'widthBottom'] as const).map((field, fi) => (
                                  <div key={field}>
                                    <label className="block text-xs text-gray-500 mb-1">{['Top', 'Middle', 'Bottom'][fi]}</label>
                                    <DimensionInput
                                      value={unit[field]}
                                      fractionMode={fractionMode}
                                      onChange={(val) => updateUnit(index, field, val)}
                                      className="input-field w-full text-center"
                                      placeholder={fractionMode ? '0' : ''}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-2">Opening Height (inches) <InfoTooltip text="Measure opening height at three points: left side, center, and right side. We use the SMALLEST measurement." position="bottom" /></label>
                              <div className="grid grid-cols-3 gap-2">
                                {(['heightLeft', 'heightCenter', 'heightRight'] as const).map((field, fi) => (
                                  <div key={field}>
                                    <label className="block text-xs text-gray-500 mb-1">{['Left', 'Center', 'Right'][fi]}</label>
                                    <DimensionInput
                                      value={unit[field]}
                                      fractionMode={fractionMode}
                                      onChange={(val) => updateUnit(index, field, val)}
                                      className="input-field w-full text-center"
                                      placeholder={fractionMode ? '0' : ''}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Incomplete Measurements Disclaimer - shows until all 6 are entered (normal mode only) */}
                        {!isExpertMode && (() => {
                          const hasAllWidths = unit.widthTop && unit.widthMiddle && unit.widthBottom;
                          const hasAllHeights = unit.heightLeft && unit.heightCenter && unit.heightRight;
                          const hasAnyMeasurement = unit.widthTop || unit.widthMiddle || unit.widthBottom ||
                                                    unit.heightLeft || unit.heightCenter || unit.heightRight;

                          if (hasAnyMeasurement && !(hasAllWidths && hasAllHeights)) {
                            const missingWidths = [];
                            const missingHeights = [];
                            if (!unit.widthTop) missingWidths.push('Top');
                            if (!unit.widthMiddle) missingWidths.push('Middle');
                            if (!unit.widthBottom) missingWidths.push('Bottom');
                            if (!unit.heightLeft) missingHeights.push('Left');
                            if (!unit.heightCenter) missingHeights.push('Center');
                            if (!unit.heightRight) missingHeights.push('Right');

                            return (
                              <div className="p-4 bg-yellow-900/30 border-2 border-yellow-500 rounded-lg">
                                <div className="flex items-start gap-3">
                                  <svg className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  <div>
                                    <h4 className="font-bold text-yellow-400 mb-1">Measurement Responsibility</h4>
                                    <div className="text-sm text-yellow-200/80 mb-2">
                                      <span className="text-yellow-300">Missing:</span>
                                      {missingWidths.length > 0 && <span className="ml-2">Width ({missingWidths.join(', ')})</span>}
                                      {missingWidths.length > 0 && missingHeights.length > 0 && <span>,</span>}
                                      {missingHeights.length > 0 && <span className="ml-1">Height ({missingHeights.join(', ')})</span>}
                                    </div>
                                    <p className="text-sm text-gray-300">
                                      Orders submitted with incomplete measurements will be manufactured as entered.
                                      <strong className="text-yellow-300"> Keder Screens assumes no responsibility for fit,
                                      installation issues, or remakes resulting from incomplete measurements.</strong>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Dimension Error Message */}
                        {dimensionError && (
                          <div className="p-4 bg-red-900/30 border-2 border-red-500 rounded-lg">
                            <div className="flex items-center gap-2 text-red-400">
                              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <span className="font-bold text-lg">Dimensions Too Large</span>
                            </div>
                            <p className="mt-2 text-red-300">
                              {dimensionErrorMsg}
                            </p>
                            <p className="mt-2 text-gray-400 text-sm">
                              Maximum allowed: {MAX_SCREEN_WIDTH}" wide x {MAX_SCREEN_HEIGHT}" tall.
                              Contact us for custom pricing on larger screens.
                            </p>
                          </div>
                        )}

                        {/* Installation Options - Always visible */}
                        {(() => {
                          // Determine if any installation toggle applies to this unit for highlighting
                          const hasAnyInstallToggle = formData.sameSystemType || formData.sameInstallType || formData.sameTrackMount || formData.sameUchannel || formData.sameScrewCovers;
                          const isUnit1WithToggles = index === 0 && hasAnyInstallToggle;
                          return (
                            <div className={clsx(
                              "p-3 rounded-lg border",
                              isUnit1WithToggles
                                ? "bg-keder-yellow/10 border-keder-yellow"
                                : "bg-blue-900/20 border-blue-500"
                            )}>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className={clsx(
                                  "font-medium",
                                  isUnit1WithToggles ? "text-keder-yellow" : "text-blue-400"
                                )}>
                                  Installation Options
                                </h4>
                                {isUnit1WithToggles && (
                                  <span className="text-xs text-keder-yellow bg-keder-yellow/20 px-2 py-1 rounded">
                                    Changes propagate to all units
                                  </span>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* System Type - FIRST */}
                                <div>
                                  <label className="block text-sm text-gray-400 mb-1">
                                    System Type
                                    <InfoTooltip text="Housing Box: Enclosed aluminum housing that conceals the roller and fabric when retracted — standard for most installations. Open Roll: Exposed roller with no housing, designed for recessed ceiling pockets where the roller is hidden inside the structure." position="right" />
                                    {formData.sameSystemType && index > 0 && (
                                      <span className="text-xs text-keder-yellow ml-1">(locked)</span>
                                    )}
                                  </label>
                                  <select
                                    value={unit.systemType}
                                    onChange={(e) => updateUnit(index, 'systemType', e.target.value as SystemType)}
                                    className={clsx(
                                      "input-field w-full",
                                      formData.sameSystemType && index > 0 && "opacity-60 cursor-not-allowed"
                                    )}
                                    disabled={formData.sameSystemType && index > 0}
                                  >
                                    {SYSTEM_TYPE_OPTIONS.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                </div>
                                {/* Installation Type */}
                                <div>
                                  <label className="block text-sm text-gray-400 mb-1">
                                    Installation Type
                                    <InfoTooltip text="Face of Header = screen mounts on front face. Under Header = screen mounts underneath looking up. Recessed = housing sits inside a pocket in the header." className="ml-1" position="bottom" />
                                    {formData.sameInstallType && index > 0 && (
                                      <span className="text-xs text-keder-yellow ml-1">(locked)</span>
                                    )}
                                  </label>
                                  <select
                                    value={unit.installationType}
                                    onChange={(e) => {
                                      const newInstallType = e.target.value as 'wall' | 'ceiling';
                                      updateUnit(index, 'installationType', newInstallType);
                                    }}
                                    className={clsx(
                                      "input-field w-full",
                                      formData.sameInstallType && index > 0 && "opacity-60 cursor-not-allowed"
                                    )}
                                    disabled={formData.sameInstallType && index > 0}
                                  >
                                    <option value="wall">Wall</option>
                                    <option value="ceiling">Ceiling</option>
                                  </select>
                                </div>
                                {/* Track Mount */}
                                <div>
                                  <label className="block text-sm text-gray-400 mb-1">
                                    Track Mount
                                    <InfoTooltip text={isExpertMode ? "How the tracks attach to the structure. In expert mode, enter exact system dimensions — mount-type additions (e.g., outside-mount +3\") are not applied. The box deduction checkbox still applies if checked." : "How the tracks attach to the structure. Face = front of header, Inside = within opening, Outside adds +3\" to system width for wall-mount brackets."} className="ml-1" position="bottom" />
                                    {formData.sameTrackMount && index > 0 && (
                                      <span className="text-xs text-keder-yellow ml-1">(locked)</span>
                                    )}
                                  </label>
                                  <select
                                    value={unit.trackMount}
                                    onChange={(e) => {
                                      const newTrackMount = e.target.value as TrackMountType;
                                      updateUnit(index, 'trackMount', newTrackMount);

                                      // Get allowed U-channel options for the new track mount
                                      const allowedOptions = getUChannelOptionsForTrackMount(newTrackMount);
                                      const currentUChannelAllowed = allowedOptions.some(opt => opt.value === unit.uChannel && !opt.disabled);

                                      // If current U-channel is not allowed, reset to 'none'
                                      if (!currentUChannelAllowed) {
                                        updateUnit(index, 'uChannel', 'none');
                                        updateUnit(index, 'applyUChannelDeduction', false);
                                      }

                                      // Auto-default U-Channel to "Both" when Inside is selected (if currently None)
                                      if (newTrackMount === 'inside' && unit.uChannel === 'none') {
                                        updateUnit(index, 'uChannel', 'both_sides');
                                        updateUnit(index, 'applyUChannelDeduction', !isExpertMode);
                                      }

                                      // Update screw covers default based on new track mount and u-channel
                                      if (trackMountIncludesFace(newTrackMount)) {
                                        const newUChannel = newTrackMount === 'inside' && unit.uChannel === 'none' ? 'both_sides' : unit.uChannel;
                                        const defaultScrewCovers = getDefaultScrewCovers(newTrackMount, newUChannel);
                                        const screwCoverQty = getScrewCoverQty(newTrackMount, newUChannel);
                                        updateUnit(index, 'screwCovers', defaultScrewCovers);
                                        updateUnit(index, 'screwCoverQty', screwCoverQty);
                                      } else {
                                        updateUnit(index, 'screwCovers', false);
                                        updateUnit(index, 'screwCoverQty', 0);
                                      }

                                      // Reset box width adjustment when switching away from inside mount
                                      if (!trackMountIncludesInside(newTrackMount)) {
                                        updateUnit(index, 'boxWidthAdjustmentType', 'same');
                                        updateUnit(index, 'boxWidthAdjustmentValue', '');
                                      }
                                    }}
                                    className={clsx(
                                      "input-field w-full",
                                      formData.sameTrackMount && index > 0 && "opacity-60 cursor-not-allowed"
                                    )}
                                    disabled={formData.sameTrackMount && index > 0}
                                  >
                                    {TRACK_MOUNT_OPTIONS.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </select>
                                  <p className="text-xs text-gray-500 mt-1 italic">Tracks ship assembled</p>
                                </div>
                              </div>
                              {/* Second row: U-Channel and Screw Covers */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                {/* U-Channel */}
                                <div>
                                  <label className="block text-sm text-gray-400 mb-1">
                                    U-Channel
                                    <InfoTooltip text="Aluminum squaring channel for out-of-square openings. Factory applies size deductions automatically when 'Apply factory deduction' is checked. Cost: $9.16/ft dealer." className="ml-1" position="right" />
                                    {formData.sameUchannel && index > 0 && (
                                      <span className="text-xs text-keder-yellow ml-1">(locked)</span>
                                    )}
                                  </label>
                                  {(() => {
                                    const uChannelOptions = getUChannelOptionsForTrackMount(unit.trackMount as TrackMountType);
                                    const isDisabled = formData.sameUchannel && index > 0;
                                    return (
                                      <>
                                        <select
                                          value={unit.uChannel}
                                          onChange={(e) => {
                                            const newUChannel = e.target.value;
                                            updateUnit(index, 'uChannel', newUChannel);
                                            // Update screw covers default based on new u-channel
                                            if (trackMountIncludesFace(unit.trackMount)) {
                                              const defaultScrewCovers = getDefaultScrewCovers(unit.trackMount, newUChannel);
                                              const screwCoverQty = getScrewCoverQty(unit.trackMount, newUChannel);
                                              updateUnit(index, 'screwCovers', defaultScrewCovers);
                                              updateUnit(index, 'screwCoverQty', screwCoverQty);
                                            }
                                          }}
                                          className={clsx(
                                            "input-field w-full",
                                            isDisabled && "opacity-60 cursor-not-allowed"
                                          )}
                                          disabled={isDisabled}
                                        >
                                          {uChannelOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                        {/* Recessed note — u-channel can ship OR be pre-installed */}
                                        {unit.trackMount === 'recessed' && (
                                          <div className="mt-2 p-2 bg-orange-900/30 border border-orange-500 rounded text-orange-300 text-xs">
                                            <strong>Recessed mount:</strong> pick which sides to ship u-channel on, or leave <strong>None</strong> if it's pre-installed on site.
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                  {/* Factory Deduction Checkbox - only show when U-Channel is not "none" */}
                                  {unit.uChannel !== 'none' && (
                                    <label className={clsx(
                                      "flex items-center gap-2 mt-2 text-sm cursor-pointer",
                                      formData.sameUchannel && index > 0 && "opacity-60 cursor-not-allowed"
                                    )}>
                                      <input
                                        type="checkbox"
                                        checked={unit.applyUChannelDeduction}
                                        onChange={(e) => updateUnit(index, 'applyUChannelDeduction', e.target.checked)}
                                        disabled={formData.sameUchannel && index > 0}
                                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-keder-yellow focus:ring-keder-yellow"
                                      />
                                      <span className="text-gray-300">
                                        Apply factory deduction ({unit.uChannel === 'both_sides' ? '¾"' : '⅜"'})
                                      </span>
                                    </label>
                                  )}
                                </div>
                                {/* Screw Covers - ONLY shows when Track Mount includes Face */}
                                {trackMountIncludesFace(unit.trackMount) && (
                                  <div>
                                    <label className="block text-sm text-gray-400 mb-1">
                                      Screw Covers
                                      <InfoTooltip text="Decorative snap-in inserts that clip into the track after wall-mount installation. They cover exposed screw heads for a clean, finished look." position="right" />
                                      {formData.sameScrewCovers && index > 0 && (
                                        <span className="text-xs text-keder-yellow ml-1">(locked)</span>
                                      )}
                                    </label>
                                    <div className="flex items-center gap-3">
                                      <select
                                        value={unit.screwCovers ? 'yes' : 'no'}
                                        onChange={(e) => updateUnit(index, 'screwCovers', e.target.value === 'yes')}
                                        className={clsx(
                                          "input-field flex-1",
                                          formData.sameScrewCovers && index > 0 && "opacity-60 cursor-not-allowed"
                                        )}
                                        disabled={formData.sameScrewCovers && index > 0}
                                      >
                                        <option value="no">No</option>
                                        <option value="yes">Yes</option>
                                      </select>
                                      {/* Show quantity and calculated price when Yes is selected */}
                                      {unit.screwCovers && (() => {
                                        const height = Math.max(
                                          parseFloat(unit.heightLeft) || 0,
                                          parseFloat(unit.heightCenter) || 0,
                                          parseFloat(unit.heightRight) || 0
                                        );
                                        const qty = getScrewCoverQty(unit.trackMount, unit.uChannel);
                                        if (height > 0) {
                                          const price = calculateScrewCoversPrice(height, qty);
                                          return (
                                            <span className="text-green-400 text-sm font-medium whitespace-nowrap">
                                              ({qty}) + ${price.toFixed(2)}
                                            </span>
                                          );
                                        }
                                        return <span className="text-gray-500 text-sm">({qty})</span>;
                                      })()}
                                    </div>
                                  </div>
                                )}
                              </div>
                              {/* Assembly Note - shows based on Track Mount and U-Channel selection */}
                              {(() => {
                                const assemblyNote = getTrackAssemblyNote(unit.trackMount, unit.uChannel);
                                if (!assemblyNote) return null;
                                return (
                                  <div className="mt-3 text-sm text-red-500 font-medium">
                                    {assemblyNote}
                                  </div>
                                );
                              })()}

                              {/* Box Width — checkbox always shown, adjustment radios only for inside mounts */}
                              {(() => {
                                const isBoxLocked = formData.sameTrackMount && index > 0;
                                const isInside = trackMountIncludesInside(unit.trackMount);
                                const wT = parseFloat(unit.widthTop) || 0;
                                const wM = parseFloat(unit.widthMiddle) || 0;
                                const wB = parseFloat(unit.widthBottom) || 0;
                                const boxCalc = isInside ? calculateRecommendedBoxAdjustment(wT, wM, wB) : null;
                                return (
                                  <div className="mt-4 p-4 bg-gray-800/50 border border-gray-600 rounded-lg">
                                    <h4 className="text-sm font-medium text-gray-300 mb-3">Box Width</h4>

                                    {/* Apply ¼" box deduction checkbox — independent of radio, they stack */}
                                    <label className={clsx(
                                      "flex items-center gap-2 text-sm cursor-pointer",
                                      isInside ? "mb-3" : "mb-0",
                                      isBoxLocked && "opacity-60 cursor-not-allowed"
                                    )}>
                                      <input
                                        type="checkbox"
                                        checked={unit.applyBoxDeduction}
                                        onChange={(e) => updateUnit(index, 'applyBoxDeduction', e.target.checked)}
                                        disabled={isBoxLocked}
                                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-keder-yellow focus:ring-keder-yellow"
                                      />
                                      <span className="text-gray-300">
                                        Apply ¼" box deduction <span className="text-gray-500">(recommended for fit)</span>
                                      </span>
                                    </label>

                                    {/* Calculated Box Width display */}
                                    {wT > 0 && (() => {
                                      const systemW = isInside ? Math.min(...[wT, wM, wB].filter(w => w > 0)) : wT;
                                      const adjVal = parseFloat(unit.boxWidthAdjustmentValue) || 0;
                                      const dealerAdj = unit.boxWidthAdjustmentType === 'smaller' ? -adjVal : unit.boxWidthAdjustmentType === 'larger' ? adjVal : 0;
                                      const boxDed = unit.applyBoxDeduction ? 0.25 : 0;
                                      const calcBoxW = systemW + dealerAdj - boxDed;
                                      return (
                                        <div className="text-sm text-gray-400 mt-1">
                                          Calculated Box Width: <span className="text-keder-yellow font-semibold">{calcBoxW.toFixed(2)}"</span>
                                          <span className="text-gray-600 text-xs ml-2">
                                            ({systemW}"{dealerAdj > 0 ? ` + ${adjVal}"` : dealerAdj < 0 ? ` − ${adjVal}"` : ''}{boxDed > 0 ? ` − ${boxDed}"` : ''})
                                          </span>
                                        </div>
                                      );
                                    })()}

                                    {/* Box Width Adjustment (same/smaller/larger) — only for inside mounts */}
                                    {isInside && boxCalc && (<>

                                    {/* RED warning when top exceeds min by more than 0.75" */}
                                    {boxCalc.exceedsMaxVariance && (
                                      (() => {
                                        const boxDed = unit.applyBoxDeduction ? 0.25 : 0;
                                        const cappedBoxW = boxCalc.minWidth + 0.75 - boxDed;
                                        const uncappedBoxW = boxCalc.minWidth + boxCalc.variance - boxDed;
                                        return (
                                          <div className="p-3 mb-3 bg-red-900/30 border border-red-500 rounded-lg">
                                            <div className="flex items-center gap-2">
                                              <span className="text-red-400 font-bold text-sm">Box Width Limit Exceeded</span>
                                            </div>
                                            <div className="text-red-200 text-sm mt-1">
                                              Top ({wT}") is <strong>{boxCalc.variance.toFixed(2)}"</strong> wider than the smallest measurement ({boxCalc.minWidth}").
                                              Max allowed is <strong>0.75"</strong> wider.
                                            </div>
                                            <div className="text-red-200 text-sm mt-1">
                                              Box width capped: {boxCalc.minWidth}" + 0.75"{boxDed > 0 ? ` − ${boxDed}"` : ''} = <strong>{cappedBoxW.toFixed(3)}"</strong>
                                              <span className="text-red-400 ml-2">(uncapped would be {uncappedBoxW.toFixed(3)}")</span>
                                            </div>
                                          </div>
                                        );
                                      })()
                                    )}

                                    {/* Blue info banner — only when variance is within limits (no red banner) */}
                                    {boxCalc.needsAdjustment && !boxCalc.exceedsMaxVariance && (() => {
                                      const adjVal = parseFloat(unit.boxWidthAdjustmentValue) || 0;
                                      const dealerAdj = unit.boxWidthAdjustmentType === 'smaller' ? -adjVal : unit.boxWidthAdjustmentType === 'larger' ? adjVal : 0;
                                      const boxDed = unit.applyBoxDeduction ? 0.25 : 0;
                                      const boxW = boxCalc.minWidth + dealerAdj - boxDed;
                                      return (
                                        <div className="p-3 mb-3 bg-blue-900/20 border border-blue-500 rounded-lg">
                                          <div className="text-blue-200 text-sm">
                                            {boxCalc.adjustmentDescription}
                                          </div>
                                          <div className="text-blue-200 text-sm mt-1">
                                            Box width: {boxCalc.minWidth}"{dealerAdj > 0 ? ` + ${adjVal}"` : dealerAdj < 0 ? ` − ${adjVal}"` : ''}{boxDed > 0 ? ` − ${boxDed}"` : ''} = <strong>{boxW.toFixed(3)}"</strong>
                                          </div>
                                        </div>
                                      );
                                    })()}

                                    {/* Radio group: Same / Smaller / Larger */}
                                    <div className="flex flex-wrap items-center gap-4">
                                      <label className={clsx("flex items-center gap-1.5 text-sm cursor-pointer", isBoxLocked && "opacity-60 cursor-not-allowed")}>
                                        <input
                                          type="radio"
                                          name={`boxWidthAdj-${unit.id}`}
                                          checked={unit.boxWidthAdjustmentType === 'same'}
                                          onChange={() => {
                                            updateUnit(index, 'boxWidthAdjustmentType', 'same');
                                            updateUnit(index, 'boxWidthAdjustmentValue', '');
                                          }}
                                          disabled={isBoxLocked}
                                          className="w-4 h-4 border-gray-600 bg-gray-700 text-keder-yellow focus:ring-keder-yellow"
                                        />
                                        <span className="text-gray-300">Same as System Width</span>
                                      </label>

                                      <label className={clsx("flex items-center gap-1.5 text-sm cursor-pointer", isBoxLocked && "opacity-60 cursor-not-allowed")}>
                                        <input
                                          type="radio"
                                          name={`boxWidthAdj-${unit.id}`}
                                          checked={unit.boxWidthAdjustmentType === 'smaller'}
                                          onChange={() => updateUnit(index, 'boxWidthAdjustmentType', 'smaller')}
                                          disabled={isBoxLocked}
                                          className="w-4 h-4 border-gray-600 bg-gray-700 text-keder-yellow focus:ring-keder-yellow"
                                        />
                                        <span className="text-gray-300">Smaller</span>
                                        {unit.boxWidthAdjustmentType === 'smaller' && (
                                          <div className="flex items-center gap-1">
                                            <input
                                              type="number"
                                              step="0.0625"
                                              min="0"
                                              max="0.25"
                                              value={unit.boxWidthAdjustmentValue}
                                              onChange={(e) => {
                                                const val = Math.min(parseFloat(e.target.value) || 0, 0.25);
                                                updateUnit(index, 'boxWidthAdjustmentValue', val.toString());
                                              }}
                                              onWheel={(e) => e.currentTarget.blur()}
                                              disabled={isBoxLocked}
                                              className="input-field w-24 text-center"
                                              placeholder="0.25"
                                            />
                                            <span className="text-gray-500 text-xs">"</span>
                                            <span className="text-gray-500 text-xs ml-1">(max 0.25")</span>
                                          </div>
                                        )}
                                      </label>

                                      <label className={clsx("flex items-center gap-1.5 text-sm cursor-pointer", isBoxLocked && "opacity-60 cursor-not-allowed")}>
                                        <input
                                          type="radio"
                                          name={`boxWidthAdj-${unit.id}`}
                                          checked={unit.boxWidthAdjustmentType === 'larger'}
                                          onChange={() => updateUnit(index, 'boxWidthAdjustmentType', 'larger')}
                                          disabled={isBoxLocked}
                                          className="w-4 h-4 border-gray-600 bg-gray-700 text-keder-yellow focus:ring-keder-yellow"
                                        />
                                        <span className="text-gray-300">Larger</span>
                                        {unit.boxWidthAdjustmentType === 'larger' && (
                                          <div className="flex items-center gap-1">
                                            <input
                                              type="number"
                                              step="0.0625"
                                              min="0"
                                              max="0.75"
                                              value={unit.boxWidthAdjustmentValue}
                                              onChange={(e) => {
                                                const val = Math.min(parseFloat(e.target.value) || 0, 0.75);
                                                updateUnit(index, 'boxWidthAdjustmentValue', val.toString());
                                              }}
                                              onWheel={(e) => e.currentTarget.blur()}
                                              disabled={isBoxLocked}
                                              className="input-field w-24 text-center"
                                              placeholder="0.5"
                                            />
                                            <span className="text-gray-500 text-xs">"</span>
                                            <span className="text-gray-500 text-xs ml-1">(max 0.75")</span>
                                          </div>
                                        )}
                                      </label>
                                    </div>

                                    <p className="text-xs text-gray-500 mt-2 italic">
                                      Adjusts housing box and roller tube width. Tracks, fabric, and weight bar use system width.
                                    </p>
                                    </>)}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })()}

                        {/* Track mount disclosure - hidden in expert mode since dimensions are exact */}
                        {hasValidDimensions && !isExpertMode && (() => {
                          const trackMount = unit.trackMount as TrackMountType;
                          switch (trackMount) {
                            case 'face':
                              return (
                                <div className="p-3 bg-keder-yellow/10 border border-keder-yellow rounded-lg text-yellow-300 text-sm">
                                  <strong>Face Track Mount:</strong> 3 inches will be added to each side of width (+6" total)
                                </div>
                              );
                            case 'inside':
                              return (
                                <div className="p-3 bg-blue-900/20 border border-blue-500 rounded-lg text-blue-300 text-sm">
                                  <strong>Inside Track Mount:</strong> Unit will be made based on the smallest measurement to ensure it fits.
                                </div>
                              );
                            case 'inside_face':
                              return (
                                <div className="p-3 bg-blue-900/20 border border-blue-500 rounded-lg text-blue-300 text-sm">
                                  <strong>Inside/Face:</strong> One side face-mounted, other side inside-mounted. Unit width made to fit inside opening.
                                </div>
                              );
                            case 'inside_outside':
                              return (
                                <div className="p-3 bg-keder-yellow/10 border border-keder-yellow rounded-lg text-yellow-300 text-sm">
                                  <strong>Inside/Outside:</strong> One side outside-mounted (+3"), other side inside-mounted.
                                </div>
                              );
                            case 'recessed':
                              return (
                                <div className="p-3 bg-blue-900/20 border border-blue-500 rounded-lg text-blue-300 text-sm">
                                  <strong>Recessed Track Mount:</strong> Unit will be made based on the smallest measurement to ensure it fits inside pocket.
                                </div>
                              );
                            default:
                              return null;
                          }
                        })()}

                        {/* Height Difference Popup */}
                        {(() => {
                          const hLeft = parseFloat(unit.heightLeft) || 0;
                          const hCenter = parseFloat(unit.heightCenter) || 0;
                          const hRight = parseFloat(unit.heightRight) || 0;

                          // Only show if at least two height values are entered
                          const heightValues = [hLeft, hCenter, hRight].filter(h => h > 0);
                          if (heightValues.length < 2) return null;

                          const maxH = Math.max(...heightValues);
                          const minH = Math.min(...heightValues);
                          const heightDiff = maxH - minH;

                          if (heightDiff === 0) return null;

                          if (heightDiff < 1) {
                            return (
                              <div className="p-3 bg-blue-900/20 border border-blue-500 rounded-lg text-blue-300 text-sm">
                                <span className="font-medium">ℹ️ Height variation under 1 inch</span> - 2 inch brush recommended
                              </div>
                            );
                          } else {
                            return (
                              <div className="p-3 bg-blue-900/20 border border-blue-500 rounded-lg text-blue-300 text-sm">
                                <span className="font-medium">ℹ️ Height variation over 1 inch</span> - consider 2 inch brush or bias cut
                              </div>
                            );
                          }
                        })()}

                        {/* Bias Indicator - Shows after height variation message */}
                        {(() => {
                          const biasCalc = calculateRecommendedBias(
                            parseFloat(unit.heightLeft) || 0,
                            parseFloat(unit.heightCenter) || 0,
                            parseFloat(unit.heightRight) || 0
                          );
                          if (biasCalc.needsBias) {
                            if (unit.biasedCut) {
                              // Bias is enabled and recommended
                              return (
                                <div className="p-3 bg-orange-900/20 border border-orange-500 rounded-lg flex items-center gap-2">
                                  <span className="text-orange-400 font-medium text-sm">⚡ Bias Applied:</span>
                                  <span className="text-orange-300 text-sm">{biasCalc.biasDescription}</span>
                                  <span className="text-orange-200 text-xs ml-auto">Amount: {unit.biasAmount || biasCalc.biasAmount}" (Max 4")</span>
                                </div>
                              );
                            } else {
                              // Bias is recommended but not enabled - show suggestion
                              return (
                                <div className="p-3 bg-blue-900/20 border border-blue-500 rounded-lg flex items-center gap-2">
                                  <span className="text-blue-400 font-medium text-sm">💡 Bias Suggested:</span>
                                  <span className="text-blue-300 text-sm">{biasCalc.biasDescription}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      updateUnit(index, 'biasedCut', true);
                                      updateUnit(index, 'biasAmount', biasCalc.biasAmount.toString());
                                    }}
                                    className="ml-auto px-2 py-1 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded"
                                  >
                                    Enable Bias ({biasCalc.biasAmount}")
                                  </button>
                                </div>
                              );
                            }
                          }
                          return null;
                        })()}

                        {/* Width Popup - Weight Bar Kit Required */}
                        {(() => {
                          const wTop = parseFloat(unit.widthTop) || 0;
                          const wMid = parseFloat(unit.widthMiddle) || 0;
                          const wBot = parseFloat(unit.widthBottom) || 0;
                          const maxWidth = Math.max(wTop, wMid, wBot);

                          // Auto-add Weight Bar Kit when screen <72". Dealer can
                          // opt out per unit via the checkbox; we still store as
                          // boolean `skipWeightBarKit` on the unit.
                          if (maxWidth > 0 && maxWidth < 72) {
                            const skipped = !!(unit as any).skipWeightBarKit;
                            return (
                              <div className={`p-3 rounded-lg text-sm border ${skipped ? 'bg-gray-800 border-gray-600 text-gray-400' : 'bg-emerald-900/20 border-emerald-500 text-emerald-300'}`}>
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1">
                                    <div className="font-medium">
                                      {skipped ? 'Weight Bar Kit skipped' : '✓ Weight Bar Kit added — $99'}
                                    </div>
                                    <div className="text-xs mt-1 opacity-80">
                                      {skipped
                                        ? `Re-enable below — recommended for screens under 6ft (this one is ${maxWidth}").`
                                        : `Required for screens under 6ft (this one is ${maxWidth}"). 2 powder-coated steel bars + bolts + labor.`}
                                    </div>
                                  </div>
                                  <label className="flex items-center gap-1 text-xs cursor-pointer whitespace-nowrap">
                                    <input
                                      type="checkbox"
                                      checked={skipped}
                                      onChange={(e) => updateUnit(index, 'skipWeightBarKit' as any, e.target.checked as any)}
                                      className="rounded"
                                    />
                                    Skip kit
                                  </label>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}

                        {/* Unit Options */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Motor Side <InfoTooltip text="Which side the motor and power cord will be located when facing the screen from outside." position="right" /></label>
                            <select
                              value={unit.motorSide}
                              onChange={(e) => updateUnit(index, 'motorSide', e.target.value)}
                              className="input-field w-full"
                            >
                              <option value="right">Right Side</option>
                              <option value="left">Left Side</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">
                              Brush Size
                              <InfoTooltip text="Weather seal brush at bottom of screen. 1&quot; standard, 2&quot; for larger gaps or uneven surfaces." className="ml-1" />
                              {formData.sameBrushSize && index > 0 && (
                                <span className="text-xs text-keder-yellow ml-1">(locked)</span>
                              )}
                            </label>
                            <select
                              value={unit.brushSize}
                              onChange={(e) => updateUnit(index, 'brushSize', e.target.value)}
                              className={clsx(
                                "input-field w-full",
                                formData.sameBrushSize && index > 0 && "opacity-60 cursor-not-allowed"
                              )}
                              disabled={formData.sameBrushSize && index > 0}
                            >
                              <option value="1inch">1 inch</option>
                              <option value="2inch">2 inch</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Biased Cut <InfoTooltip text="Angled cut on fabric bottom edge to accommodate uneven floors or slopes. Specify which side is longer." /></label>
                            <select
                              value={unit.biasedCut ? 'yes' : 'no'}
                              onChange={(e) => updateUnit(index, 'biasedCut', e.target.value === 'yes')}
                              className="input-field w-full"
                            >
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          </div>
                          {unit.biasedCut && (
                            <>
                              <div>
                                <label className="block text-sm text-gray-400 mb-1">Longer Side</label>
                                <select
                                  value={unit.biasSide}
                                  onChange={(e) => updateUnit(index, 'biasSide', e.target.value)}
                                  className="input-field w-full"
                                >
                                  <option value="left">Left</option>
                                  <option value="right">Right</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm text-gray-400 mb-1">Bias Amount (in)</label>
                                <input
                                  type="number"
                                  step="0.25"
                                  max={4}
                                  value={unit.biasAmount}
                                  onChange={(e) => updateUnit(index, 'biasAmount', e.target.value)}
                                  onWheel={(e) => e.currentTarget.blur()}
                                  className="input-field w-full"
                                />
                              </div>
                            </>
                          )}
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Window Panel</label>
                            <label className="flex items-center gap-2 cursor-pointer mt-2">
                              <input
                                type="checkbox"
                                checked={unit.includeWindowPanel || false}
                                onChange={(e) => updateUnit(index, 'includeWindowPanel', e.target.checked)}
                                className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-keder-yellow focus:ring-keder-yellow"
                              />
                              <span className="text-white text-sm">Include Windows</span>
                            </label>
                          </div>
                        </div>

                        {/* Screen Location (per-unit) */}
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Screen Location</label>
                          <input
                            type="text"
                            value={unit.screenLocation || ''}
                            onChange={(e) => updateUnit(index, 'screenLocation', e.target.value)}
                            className="input-field w-full"
                            placeholder="e.g., East Patio, Kitchen Window, Master Bedroom"
                          />
                        </div>

                        {/* System Options - Frame Color */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Frame Color</label>
                            <select
                              value={unit.systemColor}
                              onChange={(e) => {
                                updateUnit(index, 'systemColor', e.target.value);
                                // Clear RAL number if not custom
                                if (e.target.value !== 'custom') {
                                  updateUnit(index, 'ralNumber', '');
                                }
                              }}
                              className="input-field w-full"
                            >
                              {SYSTEM_COLORS.map(color => (
                                <option key={color.value} value={color.value}>{color.label}</option>
                              ))}
                            </select>
                          </div>
                          {/* RAL Number field - only shown when Custom is selected */}
                          {unit.systemColor === 'custom' && (
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">RAL Color Number <span className="text-red-500">*</span></label>
                              <input
                                type="text"
                                value={unit.ralNumber || ''}
                                onChange={(e) => updateUnit(index, 'ralNumber', e.target.value)}
                                className="input-field w-full"
                                placeholder="e.g. RAL 7016"
                                required
                              />
                            </div>
                          )}
                        </div>
                        {/* Custom Frame Color Upcharge Notice */}
                        {unit.systemColor === 'custom' && (
                          <div className="mt-2 p-2 bg-yellow-900/20 border border-yellow-500 rounded text-sm">
                            <span className="text-yellow-400">
                              Custom frame color upcharge: {(() => {
                                const maxWidth = Math.max(
                                  parseFloat(unit.widthTop) || 0,
                                  parseFloat(unit.widthMiddle) || 0,
                                  parseFloat(unit.widthBottom) || 0
                                );
                                return maxWidth > 144 ? '$895.00' : '$495.00';
                              })()}
                            </span>
                          </div>
                        )}

                        {/* Per-Component Color Override Toggle */}
                        <div className="mt-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={unit.useCustomColors}
                              onChange={(e) => {
                                updateUnit(index, 'useCustomColors', e.target.checked);
                                if (!e.target.checked) {
                                  // Reset all component colors when toggling off
                                  updateUnit(index, 'componentColors', {
                                    housingColor: '',
                                    housingRalNumber: '',
                                    leftTrackColor: '',
                                    leftTrackRalNumber: '',
                                    rightTrackColor: '',
                                    rightTrackRalNumber: '',
                                    weightBarColor: '',
                                    weightBarRalNumber: '',
                                    leftUChannelColor: '',
                                    leftUChannelRalNumber: '',
                                    rightUChannelColor: '',
                                    rightUChannelRalNumber: '',
                                  });
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-600 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-300">Custom Component Colors</span>
                            <span className="text-xs text-gray-500">(different colors per component)</span>
                          </label>
                        </div>
                        {unit.useCustomColors && (
                          <div className="mt-2 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg space-y-2">
                            <p className="text-xs text-blue-300 mb-2">Override frame color for individual components. Leave as frame color to use the default.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* Housing (Box) */}
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Housing (Box)</label>
                                <select
                                  value={unit.componentColors.housingColor || unit.systemColor}
                                  onChange={(e) => {
                                    const val = e.target.value === unit.systemColor ? '' : e.target.value;
                                    updateUnit(index, 'componentColors', { ...unit.componentColors, housingColor: val, ...(val !== 'custom' ? { housingRalNumber: '' } : {}) });
                                  }}
                                  className="input-field w-full text-sm"
                                >
                                  {SYSTEM_COLORS.map(color => (
                                    <option key={color.value} value={color.value}>{color.label}</option>
                                  ))}
                                </select>
                                {(unit.componentColors.housingColor || unit.systemColor) === 'custom' && (
                                  <input type="text" value={unit.componentColors.housingRalNumber || ''} onChange={(e) => updateUnit(index, 'componentColors', { ...unit.componentColors, housingRalNumber: e.target.value })} className="input-field w-full text-sm mt-1" placeholder="RAL number" />
                                )}
                              </div>
                              {/* Weight Bar */}
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Weight Bar</label>
                                <select
                                  value={unit.componentColors.weightBarColor || unit.systemColor}
                                  onChange={(e) => {
                                    const val = e.target.value === unit.systemColor ? '' : e.target.value;
                                    updateUnit(index, 'componentColors', { ...unit.componentColors, weightBarColor: val, ...(val !== 'custom' ? { weightBarRalNumber: '' } : {}) });
                                  }}
                                  className="input-field w-full text-sm"
                                >
                                  {SYSTEM_COLORS.map(color => (
                                    <option key={color.value} value={color.value}>{color.label}</option>
                                  ))}
                                </select>
                                {(unit.componentColors.weightBarColor || unit.systemColor) === 'custom' && (
                                  <input type="text" value={unit.componentColors.weightBarRalNumber || ''} onChange={(e) => updateUnit(index, 'componentColors', { ...unit.componentColors, weightBarRalNumber: e.target.value })} className="input-field w-full text-sm mt-1" placeholder="RAL number" />
                                )}
                              </div>
                              {/* Left Track */}
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Left Track</label>
                                <select
                                  value={unit.componentColors.leftTrackColor || unit.systemColor}
                                  onChange={(e) => {
                                    const val = e.target.value === unit.systemColor ? '' : e.target.value;
                                    updateUnit(index, 'componentColors', { ...unit.componentColors, leftTrackColor: val, ...(val !== 'custom' ? { leftTrackRalNumber: '' } : {}) });
                                  }}
                                  className="input-field w-full text-sm"
                                >
                                  {SYSTEM_COLORS.map(color => (
                                    <option key={color.value} value={color.value}>{color.label}</option>
                                  ))}
                                </select>
                                {(unit.componentColors.leftTrackColor || unit.systemColor) === 'custom' && (
                                  <input type="text" value={unit.componentColors.leftTrackRalNumber || ''} onChange={(e) => updateUnit(index, 'componentColors', { ...unit.componentColors, leftTrackRalNumber: e.target.value })} className="input-field w-full text-sm mt-1" placeholder="RAL number" />
                                )}
                              </div>
                              {/* Right Track */}
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Right Track</label>
                                <select
                                  value={unit.componentColors.rightTrackColor || unit.systemColor}
                                  onChange={(e) => {
                                    const val = e.target.value === unit.systemColor ? '' : e.target.value;
                                    updateUnit(index, 'componentColors', { ...unit.componentColors, rightTrackColor: val, ...(val !== 'custom' ? { rightTrackRalNumber: '' } : {}) });
                                  }}
                                  className="input-field w-full text-sm"
                                >
                                  {SYSTEM_COLORS.map(color => (
                                    <option key={color.value} value={color.value}>{color.label}</option>
                                  ))}
                                </select>
                                {(unit.componentColors.rightTrackColor || unit.systemColor) === 'custom' && (
                                  <input type="text" value={unit.componentColors.rightTrackRalNumber || ''} onChange={(e) => updateUnit(index, 'componentColors', { ...unit.componentColors, rightTrackRalNumber: e.target.value })} className="input-field w-full text-sm mt-1" placeholder="RAL number" />
                                )}
                              </div>
                              {/* Left U-Channel - only if u-channel includes left side */}
                              {(unit.uChannel === 'one_side' || unit.uChannel === 'two_sides' || unit.uChannel === 'both_sides' || unit.uChannel === 'left_side') && (
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Left U-Channel</label>
                                  <select
                                    value={unit.componentColors.leftUChannelColor || unit.systemColor}
                                    onChange={(e) => {
                                      const val = e.target.value === unit.systemColor ? '' : e.target.value;
                                      updateUnit(index, 'componentColors', { ...unit.componentColors, leftUChannelColor: val, ...(val !== 'custom' ? { leftUChannelRalNumber: '' } : {}) });
                                    }}
                                    className="input-field w-full text-sm"
                                  >
                                    {SYSTEM_COLORS.map(color => (
                                      <option key={color.value} value={color.value}>{color.label}</option>
                                    ))}
                                  </select>
                                  {(unit.componentColors.leftUChannelColor || unit.systemColor) === 'custom' && (
                                    <input type="text" value={unit.componentColors.leftUChannelRalNumber || ''} onChange={(e) => updateUnit(index, 'componentColors', { ...unit.componentColors, leftUChannelRalNumber: e.target.value })} className="input-field w-full text-sm mt-1" placeholder="RAL number" />
                                  )}
                                </div>
                              )}
                              {/* Right U-Channel - only if u-channel includes right side */}
                              {(unit.uChannel === 'two_sides' || unit.uChannel === 'both_sides' || unit.uChannel === 'right_side') && (
                                <div>
                                  <label className="block text-xs text-gray-400 mb-1">Right U-Channel</label>
                                  <select
                                    value={unit.componentColors.rightUChannelColor || unit.systemColor}
                                    onChange={(e) => {
                                      const val = e.target.value === unit.systemColor ? '' : e.target.value;
                                      updateUnit(index, 'componentColors', { ...unit.componentColors, rightUChannelColor: val, ...(val !== 'custom' ? { rightUChannelRalNumber: '' } : {}) });
                                    }}
                                    className="input-field w-full text-sm"
                                  >
                                    {SYSTEM_COLORS.map(color => (
                                      <option key={color.value} value={color.value}>{color.label}</option>
                                    ))}
                                  </select>
                                  {(unit.componentColors.rightUChannelColor || unit.systemColor) === 'custom' && (
                                    <input type="text" value={unit.componentColors.rightUChannelRalNumber || ''} onChange={(e) => updateUnit(index, 'componentColors', { ...unit.componentColors, rightUChannelRalNumber: e.target.value })} className="input-field w-full text-sm mt-1" placeholder="RAL number" />
                                  )}
                                </div>
                              )}
                            </div>
                            {/* Summary of overrides */}
                            {(() => {
                              const cc = unit.componentColors;
                              const fc = unit.systemColor;
                              const overrides: string[] = [];
                              const fmt = (color: string, ral: string) => color === 'custom' && ral ? `Custom (${ral})` : color;
                              if (cc.housingColor && cc.housingColor !== fc) overrides.push(`Box=${fmt(cc.housingColor, cc.housingRalNumber)}`);
                              if (cc.leftTrackColor && cc.leftTrackColor !== fc) overrides.push(`L.Track=${fmt(cc.leftTrackColor, cc.leftTrackRalNumber)}`);
                              if (cc.rightTrackColor && cc.rightTrackColor !== fc) overrides.push(`R.Track=${fmt(cc.rightTrackColor, cc.rightTrackRalNumber)}`);
                              if (cc.weightBarColor && cc.weightBarColor !== fc) overrides.push(`WBar=${fmt(cc.weightBarColor, cc.weightBarRalNumber)}`);
                              if (cc.leftUChannelColor && cc.leftUChannelColor !== fc) overrides.push(`L.UCh=${fmt(cc.leftUChannelColor, cc.leftUChannelRalNumber)}`);
                              if (cc.rightUChannelColor && cc.rightUChannelColor !== fc) overrides.push(`R.UCh=${fmt(cc.rightUChannelColor, cc.rightUChannelRalNumber)}`);
                              if (overrides.length === 0) return null;
                              const customCount = [cc.housingColor, cc.weightBarColor, cc.leftTrackColor, cc.rightTrackColor, cc.leftUChannelColor, cc.rightUChannelColor]
                                .filter((c, idx) => {
                                  const isCustom = c && c !== fc && c === 'custom';
                                  return isCustom;
                                }).length;
                              return (
                                <>
                                  <p className="text-xs text-blue-300 mt-2">
                                    Overrides: {overrides.join(', ')}
                                  </p>
                                  {customCount > 0 && (
                                    <p className="text-xs text-yellow-400 mt-1">
                                      {customCount} custom color component{customCount > 1 ? 's' : ''} × $245 = ${(customCount * 245).toLocaleString()} additional
                                    </p>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}

                        {/* Material (Fabric) Options - Always visible */}
                        <div className={clsx(
                          "p-3 rounded-lg border",
                          formData.sameFabricForAll && index === 0
                            ? "bg-keder-yellow/10 border-keder-yellow"
                            : "bg-green-900/20 border-green-500"
                        )}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={clsx(
                              "font-medium",
                              formData.sameFabricForAll && index === 0 ? "text-keder-yellow" : "text-green-400"
                            )}>
                              Material
                            </h4>
                            {formData.sameFabricForAll && index === 0 && (
                              <span className="text-xs text-keder-yellow bg-keder-yellow/20 px-2 py-1 rounded">
                                Changes propagate to all units
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">
                                Fabric Type
                                <InfoTooltip text="Solar blocks sun/heat (3-10% openness). Premium Solar (Nano) has tighter weave. Vinyl is clear/tinted. Insect is bug screen." className="ml-1" position="right" />
                                {formData.sameFabricForAll && index > 0 && (
                                  <span className="text-xs text-keder-yellow ml-1">(locked)</span>
                                )}
                              </label>
                              <select
                                value={unit.fabricType}
                                onChange={(e) => {
                                  const newType = e.target.value;
                                  updateUnit(index, 'fabricType', newType);
                                }}
                                className={clsx(
                                  "input-field w-full",
                                  formData.sameFabricForAll && index > 0 && "opacity-60 cursor-not-allowed"
                                )}
                                disabled={formData.sameFabricForAll && index > 0}
                              >
                                {FABRIC_TYPES.map(group => (
                                  <optgroup key={group.group} label={group.group}>
                                    {group.options.map(opt => (
                                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">
                                Fabric Color
                                <InfoTooltip text="Available colors depend on fabric type selected. Some fabrics only available in black." className="ml-1" />
                                {formData.sameFabricForAll && index > 0 && (
                                  <span className="text-xs text-keder-yellow ml-1">(locked)</span>
                                )}
                              </label>
                              <select
                                value={unit.fabricColor}
                                onChange={(e) => updateUnit(index, 'fabricColor', e.target.value)}
                                className={clsx(
                                  "input-field w-full",
                                  formData.sameFabricForAll && index > 0 && "opacity-60 cursor-not-allowed"
                                )}
                                disabled={formData.sameFabricForAll && index > 0}
                              >
                                {getAvailableColors(unit.fabricType).map(color => (
                                  <option key={color} value={color}>{color}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* Screen Unit Visual Renderer - hidden when window panels selected (has its own preview) */}
                        {hasValidDimensions && !dimensionError && !unit.includeWindowPanel && (
                          <ScreenUnitVisualRenderer
                            widthTop={parseFloat(unit.widthTop) || 0}
                            widthMiddle={parseFloat(unit.widthMiddle) || 0}
                            widthBottom={parseFloat(unit.widthBottom) || 0}
                            heightLeft={parseFloat(unit.heightLeft) || 0}
                            heightCenter={parseFloat(unit.heightCenter) || 0}
                            heightRight={parseFloat(unit.heightRight) || 0}
                            motorSide={unit.motorSide}
                            trackMount={unit.trackMount}
                            installationType={unit.installationType}
                            uChannel={unit.uChannel}
                            biasedCut={unit.biasedCut}
                            biasAmount={parseFloat(unit.biasAmount) || 0}
                            biasSide={unit.biasSide}
                            boxWidthAdjustment={
                              unit.boxWidthAdjustmentType === 'same' ? 0
                              : unit.boxWidthAdjustmentType === 'smaller' ? -(parseFloat(unit.boxWidthAdjustmentValue) || 0)
                              : (parseFloat(unit.boxWidthAdjustmentValue) || 0)
                            }
                            isExpertMode={isExpertMode}
                            isCollapsed={collapsedPreviews.has(unit.id)}
                            onToggleCollapse={() => {
                              setCollapsedPreviews(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(unit.id)) {
                                  newSet.delete(unit.id);
                                } else {
                                  newSet.add(unit.id);
                                }
                                return newSet;
                              });
                            }}
                          />
                        )}

                        {/* Window Panel Designer */}
                        {unit.includeWindowPanel && hasValidDimensions && (
                          <WindowDesigner
                            unitNum={index + 1}
                            screenWidth={screenWidth}
                            screenHeight={screenHeight}
                            trackMount={trackMount}
                            motorSide={unit.motorSide}
                            uChannel={unit.uChannel}
                            biasedCut={unit.biasedCut}
                            biasAmount={parseFloat(unit.biasAmount) || 0}
                            biasSide={unit.biasSide || 'left'}
                            heightLeft={parseFloat(unit.heightLeft) || 0}
                            heightRight={parseFloat(unit.heightRight) || 0}
                            config={unit.windowConfig}
                            onChange={(config) => updateUnitWindowConfig(index, config)}
                            isPreviewCollapsed={collapsedPreviews.has(`window-${unit.id}`)}
                            onTogglePreview={() => {
                              setCollapsedPreviews(prev => {
                                const newSet = new Set(prev);
                                const key = `window-${unit.id}`;
                                if (newSet.has(key)) {
                                  newSet.delete(key);
                                } else {
                                  newSet.add(key);
                                }
                                return newSet;
                              });
                            }}
                          />
                        )}

                        {/* Hide Unit Button */}
                        <div className="mt-4 pt-4 border-t border-gray-700 flex justify-center">
                          <button
                            type="button"
                            onClick={() => toggleUnitExpanded(unit.id)}
                            className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg flex items-center gap-2 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            Hide Unit #{index + 1}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ========== CONTROLS & ACCESSORIES ========== */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Motor Package & Controls</h2>

          {/* Motor Package Selection */}
          <div className="mb-6">
            <h3 className="text-md font-semibold text-gray-300 mb-3">Select Motor Package <InfoTooltip text="Alpha = economy option with good features. Somfy = premium brand with advanced programming options." /></h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {MOTOR_PACKAGES.map(pkg => {
                const isSelected = (pkg.value === 'none' && formData.motorType === 'No Motor') ||
                  (pkg.value === 'keder' && formData.motorType === 'Keder Screens') ||
                  (pkg.value === 'alpha' && formData.motorType === 'Alpha') ||
                  (pkg.value === 'somfy' && formData.motorType === 'Somfy');
                const motorTypeValue = pkg.value === 'none' ? 'No Motor'
                  : pkg.value === 'keder' ? 'Keder Screens'
                  : pkg.value === 'alpha' ? 'Alpha'
                  : 'Somfy';
                // Under 36" wide, only the Alpha motor fits (No Motor stays
                // available — we're not supplying one).
                const lockedOut = isAlphaOnlyOrder && (pkg.value === 'keder' || pkg.value === 'somfy');

                return (
                  <button
                    key={pkg.value}
                    type="button"
                    disabled={lockedOut}
                    title={lockedOut ? `Not available — screen #${alphaOnlyUnits[0]} is under ${ALPHA_ONLY_MAX_WIDTH}" wide` : undefined}
                    onClick={() => {
                      if (lockedOut) return;
                      updateFormField('motorType', motorTypeValue);
                      updateFormField('remoteType', 'none');
                      updateFormField('wallSwitchType', 'none');
                      if (pkg.value === 'none') {
                        updateFormField('remoteQty', 0);
                        updateFormField('wallSwitchQty', 0);
                      }
                    }}
                    className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                      lockedOut
                        ? 'border-gray-700 bg-keder-darker opacity-40 cursor-not-allowed'
                        : isSelected
                          ? 'border-keder-yellow bg-keder-yellow/10'
                          : 'border-gray-600 hover:border-gray-500 bg-keder-darker'
                    }`}
                  >
                    {pkg.badge && !lockedOut && (
                      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                        {pkg.badge}
                      </span>
                    )}
                    {lockedOut && (
                      <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-gray-600 text-white text-xs rounded-full">
                        {ALPHA_ONLY_MAX_WIDTH}"+ only
                      </span>
                    )}
                    <div className="font-medium text-white mb-1">{pkg.label}</div>
                    <div className="text-xs text-gray-400">{pkg.description}</div>
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <svg className="w-5 h-5 text-keder-yellow" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Under 36" = Alpha only */}
            {isAlphaOnlyOrder && (
              <div className="mt-3 p-3 bg-amber-900/30 border border-amber-500 rounded-lg text-sm text-amber-200">
                <strong>Alpha motor required:</strong> {alphaOnlyUnits.length === 1 ? 'Screen' : 'Screens'} #{alphaOnlyUnits.join(', #')} {alphaOnlyUnits.length === 1 ? 'is' : 'are'} under {ALPHA_ONLY_MAX_WIDTH}" wide. Only the Alpha motor fits a tube that short — Keder and Somfy need {ALPHA_ONLY_MAX_WIDTH}" or wider. The Alpha package applies to the whole order (+$125/unit).
              </div>
            )}

            {/* No Motor info */}
            {isNoMotor && (
              <div className="mt-3 p-3 bg-blue-900/30 border border-blue-500 rounded-lg text-sm text-blue-200">
                <strong>Screen Only:</strong> No motor or remote included. Use this for competitor screen replacements where the customer keeps their existing motor system.
              </div>
            )}

          </div>

          {/* Controllers - Only show if motor package selected */}
          {!isNoMotor && (
            <div className="border-t border-gray-700 pt-4">
              <h3 className="text-md font-semibold text-gray-300 mb-3">
                {isKederPackage ? 'Keder Screens' : formData.motorType} Remote Controls & Wall Switches
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Remote Type <InfoTooltip text="Number of channels = number of screens one remote can control independently." /></label>
                  <select
                    value={formData.remoteType}
                    onChange={(e) => updateFormField('remoteType', e.target.value)}
                    className="input-field w-full"
                  >
                    {remoteOptions.map(opt => (
                      <option key={opt.value} value={opt.value} disabled={(opt as any).disabled}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Remote QTY</label>
                  <input
                    type="number"
                    value={formData.remoteQty}
                    onChange={(e) => updateFormField('remoteQty', parseInt(e.target.value) || 0)}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="input-field w-full"
                  min={0}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Wall Switch</label>
                <select
                  value={formData.wallSwitchType}
                  onChange={(e) => updateFormField('wallSwitchType', e.target.value)}
                  className="input-field w-full"
                >
                  {wallSwitchOptions.map(opt => (
                    <option key={opt.value} value={opt.value} disabled={(opt as any).disabled}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Wall Switch QTY</label>
                <input
                  type="number"
                  value={formData.wallSwitchQty}
                  onChange={(e) => updateFormField('wallSwitchQty', parseInt(e.target.value) || 0)}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="input-field w-full"
                  min={0}
                />
              </div>
            </div>
          </div>
          )}

          {/* Additional Accessories Link */}
          <div className="border-t border-gray-700 pt-4 mt-4">
            <h3 className="text-md font-semibold text-gray-300 mb-3">Additional Accessories</h3>

            {/* Show linked accessories if any */}
            {linkedAccessories.length > 0 && (
              <div className="mb-4 p-3 bg-purple-900/30 border border-purple-500 rounded-lg">
                <div className="text-sm text-purple-200 mb-2 font-medium">Added to this order:</div>
                <div className="space-y-2">
                  {linkedAccessories.map((item, index) => (
                    <div key={item.id || index} className="flex items-center justify-between bg-purple-900/20 rounded px-3 py-2">
                      <div className="flex items-center gap-3">
                        <span className="text-white">{item.name}</span>
                        <span className="text-purple-300 text-sm">x{item.quantity}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-green-400">${(item.unitPrice * item.quantity).toFixed(2)}</span>
                        <button
                          type="button"
                          onClick={() => setLinkedAccessories(prev => prev.filter((_, i) => i !== index))}
                          className="text-red-400 hover:text-red-300"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-purple-500/30 flex justify-between text-sm">
                  <span className="text-purple-200">Accessories Total:</span>
                  <span className="text-green-400 font-medium">
                    ${linkedAccessories.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-400 mb-3">
              Need additional accessories like wind sensors, sun sensors, extra remotes, or replacement parts?
            </p>
            <button
              type="button"
              onClick={() => {
                // Save current form state to localStorage for linking
                const orderLinkData = {
                  formData,
                  orderId: editOrderId || `draft_${Date.now()}`,
                  returnUrl: window.location.pathname + window.location.search,
                  timestamp: Date.now(),
                };
                localStorage.setItem('pendingOrderAccessories', JSON.stringify(orderLinkData));
                window.location.href = '/parts?fromOrder=true';
              }}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {linkedAccessories.length > 0 ? 'Add More Accessories' : 'Browse & Add Accessories'}
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
            <p className="text-xs text-gray-500 mt-2">
              Items added will be linked to this order and included in the invoice.
            </p>
          </div>
        </div>

        {/* ========== SHIPPING METHOD ========== */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Shipping Method <InfoTooltip text="Will Call is free pickup from our facility in Lavon, TX (75166). Freight Shipping is for LTL carrier delivery to your location — carrier rates are added to your order total." position="right" /></h2>

          <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-1">Ship To Address</label>
            <select
              value={formData.shippingAddressSource}
              onChange={(e) => handleShippingSourceChange(e.target.value as any)}
              className="input-field w-full md:w-2/3"
            >
              <option value="dealer">Ship to My Address</option>
              <option value="customer">Use Customer Address</option>
              {willCallAllowed && (
                <option value="willcall">Will Call / Pickup</option>
              )}
              <option value="custom">Use a Different Address</option>
            </select>

            {!willCallAllowed && (
              <div className="mt-3 p-3 rounded-lg bg-amber-900/20 border border-amber-500 text-amber-200 text-xs">
                <strong>
                  {autoConsolidateDealer
                    ? 'Auto-consolidation enabled on your account.'
                    : `Will-call pickup isn't available outside TX/OK/LA/AR${dealerState ? ` (your account is in ${dealerState})` : ''}.`}
                </strong>{' '}
                Your orders ship via freight. You don't have to pick a carrier at order time — shipping cost is calculated when your order is packaged and added to the invoice.
              </div>
            )}

            {/* Bundle with existing orders — dealer opt-in consolidation */}
            {eligibleToBundle.length > 0 && (
              <div className="mt-4 p-3 rounded-lg border" style={{ borderColor: 'var(--border-color)' }}>
                <button
                  type="button"
                  onClick={() => setShowBundleSection(s => !s)}
                  className="w-full flex items-center justify-between text-sm font-medium text-white hover:opacity-80"
                >
                  <span>
                    📦 Bundle with existing orders
                    <span className="ml-2 text-xs text-gray-400">({eligibleToBundle.length} available)</span>
                  </span>
                  <span className="text-gray-400">{showBundleSection ? '▲' : '▼'}</span>
                </button>

                {showBundleSection && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-gray-400">
                      Ship existing orders together with this new one. Pick which to bundle:
                    </p>
                    <div className="space-y-1 max-h-64 overflow-y-auto rounded border p-2" style={{ borderColor: 'var(--border-color)' }}>
                      {eligibleToBundle.map(o => {
                        const checked = bundleWithIds.includes(o.id);
                        return (
                          <label
                            key={o.id}
                            className="flex items-center gap-2 p-2 cursor-pointer rounded hover:bg-gray-800/40"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                setBundleAcknowledged(false);
                                setBundleWithIds(prev =>
                                  checked ? prev.filter(id => id !== o.id) : [...prev, o.id]
                                );
                              }}
                              className="rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-white truncate">
                                {o.order_number}
                                {o.sidemark && <span className="text-gray-400"> — {o.sidemark}</span>}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                {o.item_count} item{o.item_count === 1 ? '' : 's'} · {String(o.status).replace(/_/g, ' ')}
                                {o.paid_in_full ? ' · paid' : o.deposit_paid ? ' · deposit' : ''}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {bundleWithIds.length > 0 && (
                      <div className="p-3 rounded bg-amber-900/30 border border-amber-500 text-xs text-amber-200">
                        <div className="font-medium mb-2">
                          ⚠ Bundling delays shipping
                        </div>
                        <p>
                          Shipping for the <strong>{bundleWithIds.length}</strong> selected order
                          {bundleWithIds.length === 1 ? '' : 's'} will wait until this new order is complete.
                          You can keep adding more orders to this group later to delay shipping further.
                          You can also undo a bundle from the Shipments page if a specific order needs to ship sooner.
                        </p>
                        <label className="flex items-center gap-2 mt-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={bundleAcknowledged}
                            onChange={(e) => setBundleAcknowledged(e.target.checked)}
                            className="rounded"
                          />
                          <span>I understand — bundle these orders.</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Show helper text based on selection */}
            {formData.shippingAddressSource === 'dealer' && (
              <p className="text-sm text-green-400 mt-2">✓ Using your business address on file</p>
            )}
            {formData.shippingAddressSource === 'customer' && (
              <p className="text-sm text-blue-400 mt-2">✓ Using customer address from above</p>
            )}
          </div>

          {formData.shippingAddressSource === 'willcall' ? (
            <div className="p-4 bg-green-900/20 border border-green-500 rounded-lg">
              <strong className="text-green-400">Pickup Location:</strong>
              <p className="text-gray-300 mt-2">
                12446 State Hwy 205<br />
                Lavon, TX 75166
              </p>
              <p className="text-gray-400 text-sm mt-3">
                <em>We'll notify you when order is ready for pickup</em>
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">Street Address <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.shipStreet}
                    onChange={(e) => updateFormField('shipStreet', e.target.value)}
                    className={`input-field w-full ${reqClass(formData.shipStreet, 'shipStreet')}`}
                  />
                  {fieldErrors.shipStreet && (
                    <p className="text-red-400 text-sm mt-1">{fieldErrors.shipStreet}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">City <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.shipCity}
                    onChange={(e) => updateFormField('shipCity', e.target.value)}
                    className={`input-field w-full ${reqClass(formData.shipCity, 'shipCity')}`}
                  />
                  {fieldErrors.shipCity && (
                    <p className="text-red-400 text-sm mt-1">{fieldErrors.shipCity}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">State <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.shipState}
                      onChange={(e) => updateFormField('shipState', e.target.value)}
                      className={`input-field w-full ${reqClass(formData.shipState, 'shipState')}`}
                      maxLength={2}
                    />
                    {fieldErrors.shipState && (
                      <p className="text-red-400 text-sm mt-1">{fieldErrors.shipState}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">ZIP <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.shipZip}
                      onChange={(e) => {
                        updateFormField('shipZip', e.target.value);
                        setCalculatedPricing(null);
                      }}
                      className={`input-field w-full ${reqClass(formData.shipZip, 'shipZip')}`}
                      maxLength={10}
                    />
                    {fieldErrors.shipZip && (
                      <p className="text-red-400 text-sm mt-1">{fieldErrors.shipZip}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Freight Options */}
              <div className="mt-4 p-3 bg-keder-yellow/10 border border-keder-yellow rounded-lg">
                <strong className="text-keder-yellow">Freight Options</strong>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.liftgateDelivery}
                      onChange={(e) => {
                        updateFormField('liftgateDelivery', e.target.checked);
                        setCalculatedPricing(null);
                      }}
                      className="w-4 h-4 rounded border-gray-600 text-keder-yellow"
                    />
                    <span className="text-gray-300 text-sm">Liftgate Required</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.residentialDelivery}
                      onChange={(e) => {
                        updateFormField('residentialDelivery', e.target.checked);
                        setCalculatedPricing(null);
                      }}
                      className="w-4 h-4 rounded border-gray-600 text-keder-yellow"
                    />
                    <span className="text-gray-300 text-sm">Residential Delivery</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.insideDelivery}
                      onChange={(e) => {
                        updateFormField('insideDelivery', e.target.checked);
                        setCalculatedPricing(null);
                      }}
                      className="w-4 h-4 rounded border-gray-600 text-keder-yellow"
                    />
                    <span className="text-gray-300 text-sm">Inside Delivery</span>
                  </label>
                </div>
              </div>

            </>
          )}
        </div>

        {/* ========== ADDITIONAL INFORMATION ========== */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Additional Information</h2>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Comments</label>
            <textarea
              value={formData.comments}
              onChange={(e) => updateFormField('comments', e.target.value)}
              className="input-field w-full"
              rows={3}
              placeholder="Special instructions or comments"
            />
          </div>
        </div>

        {/* ========== AUTHORIZATION ========== */}
        <div id="authorization-section" className="card border-keder-yellow">
          <h2 className="text-lg font-semibold text-keder-yellow mb-4">Authorization</h2>

          {/* Date Needed By */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Date Needed By <span className="text-red-500">*</span></label>
            <input
              type="date"
              value={formData.neededByDate}
              onChange={(e) => updateFormField('neededByDate', e.target.value)}
              min={earliestDateString}
              className={`input-field w-full md:w-64 ${reqClass(formData.neededByDate || '', 'neededByDate')}`}
            />
            <div className="mt-2 text-sm text-gray-400">
              <p>Current Lead Time: {currentLeadTimeDays} business days</p>
              <p>Earliest Available Date: <span className="text-keder-yellow">{earliestDateDisplay}</span></p>
            </div>
            {fieldErrors.neededByDate && (
              <p className="text-red-400 text-sm mt-1">{fieldErrors.neededByDate}</p>
            )}
          </div>

          {/* Rush Order — double-confirm popup before flag is set */}
          <div className="mb-6 p-4 bg-red-950/40 border-2 border-red-500/40 rounded-lg">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isRush}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Don't actually set until the modal confirmation comes back.
                    setRushConfirmOpen(true);
                  } else {
                    updateFormField('isRush', false);
                  }
                }}
                className="mt-1 w-5 h-5 accent-red-500 cursor-pointer"
              />
              <div className="flex-1">
                <div className="font-semibold text-red-300 uppercase tracking-wide text-sm">
                  Mark as RUSH order (+25% surcharge)
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Jumps this order to the top of production. <span className="text-red-300">+25%</span> surcharge applied to the dealer-net screen price. Use only when you genuinely need it ahead of normal lead time.
                </p>
                {formData.isRush && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-red-500/20 border border-red-400/50 rounded text-red-200 text-xs font-bold">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/></svg>
                    RUSH ACTIVE
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Authorized By */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Authorized By <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={formData.authorizedBy}
              onChange={e => updateFormField('authorizedBy', e.target.value)}
              placeholder="Type your full name"
              className={`input-field w-full ${reqClass(formData.authorizedBy, 'authorization')}`}
            />
            <p className="text-xs text-gray-500 mt-2">
              This serves as your electronic signature authorizing this order.
            </p>
            {fieldErrors.authorization && (
              <p className="text-red-400 text-sm mt-1">{fieldErrors.authorization}</p>
            )}
          </div>

          {/* Terms and Conditions */}
          <div className={`p-5 rounded-lg border-2 transition-all duration-200 ${
            orderTermsAccepted
              ? 'bg-green-900/10 border-green-500/40'
              : 'bg-keder-dark/50 border-red-500/40'
          }`}>
            <p className="text-sm text-gray-300 mb-4">
              By submitting this order, I confirm that all measurements are accurate and acknowledge that <strong className="text-white">custom-manufactured products cannot be returned</strong> due to measurement errors.
            </p>
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={orderTermsAccepted}
                onChange={(e) => setOrderTermsAccepted(e.target.checked)}
                className={`w-5 h-5 rounded transition-colors duration-200 focus:ring-2 ${
                  orderTermsAccepted
                    ? 'bg-green-600 border-green-500 text-white focus:ring-green-500'
                    : 'bg-keder-dark border-red-500 text-red-500 focus:ring-red-500'
                }`}
              />
              <span className={`font-semibold transition-colors duration-200 ${
                orderTermsAccepted ? 'text-green-400' : 'text-red-400'
              }`}>
                I agree to the order terms and conditions <span className="text-red-400">*</span>
              </span>
            </label>
            {fieldErrors.termsAccepted && (
              <p className="text-red-400 text-sm mt-2">{fieldErrors.termsAccepted}</p>
            )}
          </div>
        </div>

        {/* ========== PRICING & PAYMENT ========== */}
        <div className="card border-2 border-keder-yellow">
          <h2 className="text-lg font-semibold text-keder-yellow mb-4">Pricing & Payment</h2>

          <div className="text-center mb-4">
            {hasAnyDimensionErrors && (
              <p className="text-red-400 text-sm mb-2">
                One or more units exceed maximum dimensions ({MAX_SCREEN_WIDTH}" x {MAX_SCREEN_HEIGHT}"). Please correct before calculating price.
              </p>
            )}
            <button
              onClick={handleCalculatePricing}
              disabled={calculatingPrice || hasAnyDimensionErrors}
              className="btn-primary px-8 py-3"
            >
              {calculatingPrice ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Getting freight quote...
                </span>
              ) : (
                'Calculate Price'
              )}
            </button>
          </div>

          {calculatedPricing && (
            <>
              {/* Invoice Table */}
              <div className="border border-keder-yellow rounded-lg overflow-visible mb-6">
                <table className="w-full text-sm">
                  <thead className="bg-keder-gray">
                    <tr>
                      <th className="text-left p-3 text-gray-300">Item</th>
                      <th className="text-left p-3 text-gray-300">Details</th>
                      <th className="text-right p-3 text-gray-300">Your Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculatedPricing.screens.map((screen: any) => (
                      <tr key={screen.unit} className="border-t border-keder-gray">
                        <td className="p-3 text-white">Screen #{screen.unit}</td>
                        <td className="p-3 text-gray-400">{screen.width}" x {screen.height}"</td>
                        <td className="p-3 text-right text-white font-mono">${screen.basePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                    {calculatedPricing.extraWindowsTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Extra Windows</td>
                        <td className="p-3 text-gray-400">
                          {calculatedPricing.screens.filter((s: any) => s.extraWindowsCost > 0).map((s: any, i: number, arr: any[]) => (
                            <span key={s.unit}>
                              Unit {s.unit}: {s.windowCount - 1} additional @ $175 ea
                              {i < arr.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.extraWindowsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.remoteTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Controller</td>
                        <td className="p-3 text-gray-400">
                          {[...ALPHA_REMOTES, ...SOMFY_REMOTES, ...KEDER_REMOTES].find(r => r.value === formData.remoteType)?.label || formData.remoteType} x{formData.remoteQty}
                        </td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.remoteTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.uChannelTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">U-Channel</td>
                        <td className="p-3 text-gray-400">{calculatedPricing.uChannelFeet.toFixed(1)} linear feet</td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.uChannelTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.screwCoversTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Screw Covers</td>
                        <td className="p-3 text-gray-400">
                          {calculatedPricing.screens.filter((s: any) => s.hasScrewCovers).map((s: any, i: number, arr: any[]) => (
                            <span key={s.unit}>
                              Unit {s.unit}: ${s.screwCoversCost.toFixed(2)}
                              {i < arr.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.screwCoversTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.weightBarKitTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Weight Bar Kit</td>
                        <td className="p-3 text-gray-400">{calculatedPricing.weightBarKitCount} kit{calculatedPricing.weightBarKitCount === 1 ? '' : 's'} × $99 (screens under 6ft)</td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.weightBarKitTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.customFrameTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Custom Frame Color</td>
                        <td className="p-3 text-gray-400">
                          {calculatedPricing.screens.filter((s: any) => s.systemColor === 'custom').map((s: any, i: number, arr: any[]) => (
                            <span key={s.unit}>
                              Unit {s.unit}: RAL {s.ralNumber || 'TBD'}
                              {i < arr.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.customFrameTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.customComponentTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Custom Component Colors</td>
                        <td className="p-3 text-gray-400">
                          {calculatedPricing.screens.filter((s: any) => s.customComponentCount > 0).map((s: any, i: number, arr: any[]) => (
                            <span key={s.unit}>
                              Unit {s.unit}: {s.customComponentCount} part{s.customComponentCount > 1 ? 's' : ''} × $245
                              {i < arr.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.customComponentTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.somfyUpgradeTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Somfy Motor Upgrade</td>
                        <td className="p-3 text-gray-400">{formData.units.length} unit(s) × $275</td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.somfyUpgradeTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.alphaUpgradeTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Alpha Motor Upgrade</td>
                        <td className="p-3 text-gray-400">{formData.units.length} unit(s) × $125</td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.alphaUpgradeTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.accessoriesTotal > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Accessories</td>
                        <td className="p-3 text-gray-400">{linkedAccessories.length} item(s)</td>
                        <td className="p-3 text-right text-white font-mono">${calculatedPricing.accessoriesTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {calculatedPricing.openRollDiscount > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-green-400">Open Roll Discount</td>
                        <td className="p-3 text-gray-400">{openRollDiscountPercent}% off dealer cost</td>
                        <td className="p-3 text-right text-green-400 font-mono">-${calculatedPricing.openRollDiscount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    {/* Promo Code row — always shown so the dealer sees the input */}
                    <tr className="border-t border-keder-gray bg-keder-dark/30">
                      <td className="p-3 text-white">
                        Promo Code
                        <InfoTooltip text="Personal single-use code issued to your dealer account. Ask us if you have one." className="ml-1" />
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={promoCodeInput}
                            onChange={e => {
                              setPromoCodeInput(e.target.value.toUpperCase());
                              // Clear stale validation result when user types
                              if (promoCheck.valid !== null) {
                                setPromoCheck({ checking: false, valid: null, discount: 0, error: null });
                              }
                            }}
                            onBlur={validatePromoCode}
                            placeholder="Enter code"
                            className="px-2 py-1 bg-keder-dark border border-keder-gray text-white text-sm rounded font-mono w-32 focus:border-keder-yellow focus:outline-none"
                          />
                          {promoCheck.checking && <span className="text-xs text-gray-400">Checking…</span>}
                          {promoCheck.valid === true && (
                            <span className="text-xs text-green-400 font-medium">✓ Applied</span>
                          )}
                          {promoCheck.valid === false && promoCheck.error && (
                            <span className="text-xs text-red-400 font-medium">{promoCheck.error}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right text-green-400 font-mono">
                        {promoCheck.valid && promoCheck.discount > 0
                          ? `-$${promoCheck.discount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : <span className="text-gray-500">$0.00</span>}
                      </td>
                    </tr>
                    {calculatedPricing.rushSurcharge > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-red-400 font-semibold">RUSH Surcharge</td>
                        <td className="p-3 text-gray-400">25% of dealer-net goods (priority production)</td>
                        <td className="p-3 text-right text-red-400 font-mono font-semibold">+${calculatedPricing.rushSurcharge.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                    <tr className="border-t border-keder-gray">
                      <td className="p-3 text-white">Packaging <InfoTooltip text="Crating/packaging cost per unit. Under $15k order: $82.50/unit. $15k-$25k: $44/unit. Over $25k: FREE." /></td>
                      <td className="p-3 text-gray-400">{formData.units.length} unit(s)</td>
                      <td className="p-3 text-right text-white font-mono">${calculatedPricing.packaging.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr className="border-t border-keder-gray">
                      <td className="p-3 text-white">
                        {formData.shippingMethod === 'willcall' ? 'Shipping' : 'Freight'}
                        <InfoTooltip text="Shipping cost via LTL freight carrier. Will Call = pick up at our facility (75166) for $0." className="ml-1" />
                      </td>
                      <td className="p-3 text-gray-400">
                        {calculatedPricing.freightInfo?.willCall ? (
                          'Will Call / Pickup at Lavon, TX'
                        ) : (
                          <>
                            Ship to {formData.shipZip}
                            {calculatedPricing.freightInfo && !calculatedPricing.freightInfo.fallback && (
                              <span className="block text-xs text-gray-500">
                                {calculatedPricing.freightInfo.carrier} - {calculatedPricing.freightInfo.transitTime}
                              </span>
                            )}
                            {calculatedPricing.freightInfo?.fallback && (
                              <span className="block text-xs text-yellow-500">Estimated - final confirmed before production</span>
                            )}
                          </>
                        )}
                      </td>
                      <td className="p-3 text-right text-white font-mono">
                        {calculatedPricing.freightInfo?.willCall ? (
                          <span className="text-green-400">$0.00</span>
                        ) : (
                          `$${calculatedPricing.freight.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        )}
                      </td>
                    </tr>
                    {taxPreview > 0 && (
                      <tr className="border-t border-keder-gray">
                        <td className="p-3 text-white">Sales Tax</td>
                        <td className="p-3 text-gray-400">
                          {calculatedPricing.freightInfo?.willCall ? 'Will-call (TX)' : (formData.shipState || 'Ship-to state')} · {taxRatePct.toFixed(2)}%
                        </td>
                        <td className="p-3 text-right text-white font-mono">
                          ${taxPreview.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    )}
                    <tr className="border-t-2 border-keder-yellow bg-keder-gray">
                      <td colSpan={2} className="p-3 text-lg font-bold text-keder-yellow">TOTAL</td>
                      <td className="p-3 text-right text-2xl font-bold text-keder-yellow font-mono">
                        ${Math.max(0,
                          (calculatedPricing.total + taxPreview) - (promoCheck.valid ? promoCheck.discount : 0)
                        ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Carrier Selection - shown after Calculate Price if multiple carriers available */}
              {calculatedPricing?.freightInfo?.allQuotes?.length > 1 && !calculatedPricing?.freightInfo?.willCall && (
                <div className="mb-6 p-4 bg-keder-dark/50 border border-keder-gray rounded-lg">
                  <CarrierSelection
                    onSelect={(data) => {
                      setFreightData(data);
                      // Update pricing with new carrier selection
                      if (data.freightTotalAmount) {
                        const selectedQuote = data.freightQuotes?.find(q => q.quoteId === data.selectedQuoteId);
                        setCalculatedPricing({
                          ...calculatedPricing,
                          freight: data.freightTotalAmount,
                          freightInfo: {
                            ...calculatedPricing.freightInfo,
                            carrier: data.selectedCarrierName,
                            rate: data.freightTotalAmount,
                            transitTime: selectedQuote?.transitTime || calculatedPricing.freightInfo?.transitTime,
                          },
                          total: calculatedPricing.subtotal + calculatedPricing.packaging + data.freightTotalAmount,
                        });
                      }
                    }}
                    disabled={saving}
                    providedQuotes={calculatedPricing?.freightInfo?.allQuotes}
                    providedShipmentId={calculatedPricing?.freightInfo?.freightviewShipmentId}
                    selectedQuoteId={freightData?.selectedQuoteId}
                  />
                </div>
              )}

              {/* Confirm Order */}
              <div className="relative">
                {!isFormReadyForSubmit && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/30 rounded-lg">
                    <div className="bg-gray-800 px-4 py-2 rounded-lg text-sm text-gray-300 border border-gray-600">
                      {firstMissingRequiredField
                      ? `Missing: ${firstMissingRequiredField}`
                      : !formData.neededByDate || formData.neededByDate < earliestDateString
                        ? `Needed By date must be ${earliestDateString} or later — please update it`
                        : !orderTermsAccepted
                          ? 'Check the Terms & Conditions box below to submit'
                          : 'Enter your name in the Authorized By field to submit'}
                    </div>
                  </div>
                )}
                <button
                  onClick={handleConfirmOrder}
                  disabled={saving || !isFormReadyForSubmit}
                  className={clsx(
                    "w-full py-4 px-6 text-lg font-bold rounded-lg transition-colors flex items-center justify-center gap-3",
                    isFormReadyForSubmit
                      ? "bg-green-600 text-white hover:bg-green-700 border-2 border-green-400"
                      : "bg-gray-700 text-gray-400 opacity-50 cursor-not-allowed"
                  )}
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      Submitting Order...
                    </span>
                  ) : (
                    <>
                      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Confirm Order &mdash; ${calculatedPricing.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </>
                  )}
                </button>
                <p className="text-center text-sm text-gray-500 mt-3">
                  Your invoice will be generated &mdash; review and pay on the next page.
                </p>

                <div className="text-center mt-3">
                  <button
                    onClick={() => {
                      if (!isFormReadyForSubmit) return;
                      setShowInvoiceConfirm(true);
                    }}
                    disabled={saving || !isFormReadyForSubmit}
                    className="text-sm text-gray-400 hover:text-gray-200 underline disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Submit for Invoicing (wire transfer / QB)
                  </button>
                </div>

                {/* Invoice Confirmation Modal */}
                {showInvoiceConfirm && (
                  <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowInvoiceConfirm(false)}>
                    <div className="bg-gray-900 border-2 border-yellow-500/50 rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-white">Are you sure?</h3>
                      </div>
                      <p className="text-gray-300 text-sm mb-3">
                        The <strong className="text-white">Submit for Invoicing</strong> button is only for dealers who pay by <strong className="text-white">wire transfer</strong> or receive invoices through <strong className="text-white">QuickBooks</strong>.
                      </p>
                      <p className="text-gray-300 text-sm mb-3">
                        Orders submitted for invoicing are <strong className="text-yellow-400">not processed until payment is received</strong>, which may delay your production timeline.
                      </p>
                      <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-3 mb-6">
                        <p className="text-green-400 text-sm font-medium">
                          For faster processing, go back and use the <strong>Submit Payment</strong> button to pay instantly with credit card or ACH bank transfer.
                        </p>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setShowInvoiceConfirm(false)}
                          className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                        >
                          Go Back & Pay Now
                        </button>
                        <button
                          onClick={() => { setShowInvoiceConfirm(false); executeSubmit(true); }}
                          className="flex-1 px-4 py-2.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 font-medium text-sm"
                        >
                          Proceed with Invoicing
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center gap-4 py-6 border-t border-keder-gray">
          <button
            onClick={() => navigate('/orders')}
            className="px-6 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </button>

          <div className="flex gap-4">
            {/* Save Draft */}
            <button
              onClick={handleSaveDraft}
              disabled={savingDraft || saving}
              className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {savingDraft ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                  </svg>
                  Save Draft
                </>
              )}
            </button>

          </div>
        </div>
        <div className="pb-8"></div>
      </div>

      {/* Incomplete Measurements Disclaimer Modal */}
      {incompleteUnitsToAcknowledge.length > 0 && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border-2 border-yellow-500 rounded-xl max-w-lg w-full shadow-2xl">
            {/* Header */}
            <div className="bg-yellow-500 px-6 py-4 rounded-t-lg">
              <h3 className="text-xl font-bold text-black flex items-center gap-2">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Measurement Responsibility
              </h3>
              <p className="text-black/80 text-sm mt-1">
                Unit #{incompleteUnitsToAcknowledge[currentAcknowledgeIndex] + 1} of {formData.units.length}
                {incompleteUnitsToAcknowledge.length > 1 && (
                  <span className="ml-2">({currentAcknowledgeIndex + 1} of {incompleteUnitsToAcknowledge.length} incomplete)</span>
                )}
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {/* Show which measurements are missing */}
              {(() => {
                const unitIndex = incompleteUnitsToAcknowledge[currentAcknowledgeIndex];
                const unit = formData.units[unitIndex];
                const missingWidths = [];
                const missingHeights = [];
                if (!unit.widthTop) missingWidths.push('Top');
                if (!unit.widthMiddle) missingWidths.push('Middle');
                if (!unit.widthBottom) missingWidths.push('Bottom');
                if (!unit.heightLeft) missingHeights.push('Left');
                if (!unit.heightCenter) missingHeights.push('Center');
                if (!unit.heightRight) missingHeights.push('Right');

                return (
                  <div className="mb-4 p-3 bg-red-900/30 border border-red-500 rounded-lg">
                    <p className="text-red-300 text-sm font-medium mb-2">Missing measurements for Unit #{unitIndex + 1}:</p>
                    <div className="flex gap-4 text-sm">
                      {missingWidths.length > 0 && (
                        <div>
                          <span className="text-gray-400">Width:</span>
                          <span className="text-red-400 ml-1">{missingWidths.join(', ')}</span>
                        </div>
                      )}
                      {missingHeights.length > 0 && (
                        <div>
                          <span className="text-gray-400">Height:</span>
                          <span className="text-red-400 ml-1">{missingHeights.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <p className="text-gray-200 leading-relaxed">
                Orders submitted with incomplete measurements will be manufactured as entered.
                <strong className="text-white"> Keder Screens assumes no responsibility for fit,
                installation issues, or remakes resulting from incomplete measurements.</strong>
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-800 rounded-b-xl flex justify-between items-center gap-4">
              <button
                onClick={handleCancelAcknowledge}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel & Edit
              </button>
              <button
                onClick={handleAcknowledgeIncomplete}
                className="px-6 py-3 bg-yellow-500 text-black font-bold rounded-lg hover:bg-yellow-400 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                I Understand & Proceed
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default NewOrderPage;
